<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="sale-body">

        <!-- ── Header ── -->
        <header class="sale-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <button class="back-btn" @click="$router.push({ name: 'AgentUnderwriting' })">
              <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
            </button>
            <div>
              <p class="sale-breadcrumb">Underwriting / Motor Quote</p>
              <h2 class="sale-title">Convert to Sale</h2>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="sale-premium-chip" v-if="totalPremium > 0">
              <v-icon x-small color="#00AEEF" class="tw-mr-1">mdi-shield-check</v-icon>
              <span>{{ formattedPremium }}</span>
              <span v-if="liveRateLabel" class="sale-rate-note">{{ liveRateLabel }}</span>
            </div>
            <!-- New Sale button — always visible -->
            <button class="btn-new-sale" @click="showNewSaleConfirm = true">
              <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-refresh</v-icon>
              New Sale
            </button>
          </div>
        </header>

        <!-- ── New Sale Confirmation Dialog ── -->
        <v-dialog v-model="showNewSaleConfirm" max-width="380" overlay-opacity="0.5">
          <div class="new-sale-dialog">
            <div class="nsd-header">
              <div class="nsd-icon">
                <v-icon size="24" color="white">mdi-alert-circle-outline</v-icon>
              </div>
              <div>
                <p class="nsd-title">Start a New Sale?</p>
                <p class="nsd-sub">This will clear all current progress and start fresh.</p>
              </div>
            </div>
            <div class="nsd-body">
              <p class="nsd-desc">Any unsaved data for this sale will be lost. The quote in the system will not be affected.</p>
            </div>
            <div class="nsd-footer">
              <button class="nsd-btn-cancel" @click="showNewSaleConfirm = false">Keep Working</button>
              <button class="nsd-btn-confirm" @click="startNewSale">
                <v-icon x-small color="white" class="tw-mr-1">mdi-refresh</v-icon>
                Yes, Start New
              </button>
            </div>
          </div>
        </v-dialog>

        <!-- ── Step Progress ── -->
        <div class="step-progress">
          <div
            v-for="(step, i) in steps" :key="i"
            class="step-item"
            :class="{ 'step-active': activeTray === i+1, 'step-done': completedTrays.includes(i+1) }"
          >
            <div class="step-circle">
              <v-icon v-if="completedTrays.includes(i+1)" x-small color="white">mdi-check</v-icon>
              <span v-else>{{ i+1 }}</span>
            </div>
            <span class="step-label">{{ step }}</span>
            <div v-if="i < steps.length-1" class="step-connector" :class="{ done: completedTrays.includes(i+1) }"></div>
          </div>
        </div>

        <div class="trays-wrap">

          <!-- ════════════════════════════════════════
               TRAY 1 · VEHICLE VERIFICATION & DETAILS
          ════════════════════════════════════════ -->
          <div :class="['tray-card', activeTray===1?'tray-open':'', completedTrays.includes(1)?'tray-done':'']">
            <div class="tray-head" @click="goToTray(1)">
              <div class="tray-head-left">
                <div class="tray-num" :class="{ done: completedTrays.includes(1) }">
                  <v-icon v-if="completedTrays.includes(1)" x-small color="white">mdi-check</v-icon>
                  <span v-else>1</span>
                </div>
                <div>
                  <p class="tray-title">Vehicle Verification &amp; Details</p>
                  <p class="tray-sub" v-if="completedTrays.includes(1)">{{ form.make }} {{ form.model }} · {{ form.regNo }}</p>
                  <p class="tray-sub" v-else>NTSA check + vehicle details</p>
                </div>
              </div>
              <v-icon size="18" :color="completedTrays.includes(1)?'#10b981':'#94a3b8'">
                {{ completedTrays.includes(1) ? 'mdi-check-circle' : activeTray===1 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>

            <div class="tray-body" v-show="activeTray===1">
              <p class="tray-desc" v-if="!isTpo">Enter the cover start date and registration number to verify from NTSA, then complete all vehicle fields.</p>
              <p class="tray-desc" v-else>Complete the vehicle details below to proceed with the TPO cover.</p>

              <!-- Cover Date — Comprehensive journey only (TPO block has its own) -->
              <div class="form-row-2 tw-mb-4" v-if="!isTpo">
                <div class="input-wrap">
                  <label class="input-lbl">Cover Start Date <span class="req">*</span></label>
                  <v-text-field
                    v-model="form.startDate"
                    :min="minDate"
                    :max="maxStartDate"
                    type="date"
                    outlined dense hide-details
                    class="sale-input"
                  ></v-text-field>
                  <transition name="fade-up">
                    <div v-if="isNextYearStart" class="underwriting-disclaimer-mini tw-mt-2">
                      <v-icon x-small color="#92400E" class="tw-mr-1">mdi-alert-circle</v-icon>
                      Next-year policy — manual review required by Underwriting Team
                    </div>
                  </transition>
                </div>
              </div>

              <!-- ── TPO CUSTOM JOURNEY (non-direct only) ── -->
              <div v-if="isTpo && !isDirectJourney">

                <!-- Cover Start Date -->
                <div class="form-row-2 tw-mb-4">
                  <div class="input-wrap">
                    <label class="input-lbl">Cover Start Date <span class="req">*</span></label>
                    <v-text-field
                      v-model="form.startDate"
                      :min="minDate"
                      :max="maxStartDate"
                      type="date"
                      outlined dense hide-details
                      class="sale-input"
                    ></v-text-field>
                    <transition name="fade-up">
                      <div v-if="isNextYearStart" class="underwriting-disclaimer-mini tw-mt-2">
                        <v-icon x-small color="#92400E" class="tw-mr-1">mdi-alert-circle</v-icon>
                        Next-year policy — manual review required by Underwriting Team
                      </div>
                    </transition>
                  </div>
                </div>

                <!-- Reg + Verify (same as comprehensive) -->
                <div class="form-row-2 tw-mb-4">
                  <div class="input-wrap">
                    <label class="input-lbl">Registration Number <span class="req">*</span></label>
                    <div class="input-with-btn">
                      <v-text-field v-model="form.regNo" placeholder="e.g. KCD 123A" outlined dense hide-details class="sale-input tw-flex-1" maxlength="10" @keyup.enter="fetchVehicleDetails" @blur="form.regNo = sanitizeReg(form.regNo)"></v-text-field>
                      <button class="verify-btn" @click="fetchVehicleDetails" :disabled="fetching.vehicle || !form.regNo">
                        <v-progress-circular v-if="fetching.vehicle" indeterminate size="14" width="2" color="white"></v-progress-circular>
                        <template v-else><v-icon small color="white">mdi-magnify</v-icon> Verify</template>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- NTSA status banners (TPO) -->
                <transition name="fade-up">
                  <div v-if="fetched.vehicle">
                    <div v-if="ntsaFound" class="ntsa-success-banner tw-mb-4">
                      <div class="nsb-icon"><v-icon size="18" color="#059669">mdi-check-decagram</v-icon></div>
                      <div class="nsb-body">
                        <p class="nsb-title">NTSA Record Found — proceed</p>
                        <p class="nsb-sub">Insurance check passed and vehicle record confirmed.</p>
                      </div>
                    </div>
                    <div v-else-if="ntsaUnavailable" class="ntsa-manual-banner tw-mb-4">
                      <div class="nsb-icon"><v-icon size="18" color="#D97706">mdi-cloud-off-outline</v-icon></div>
                      <div class="nsb-body">
                        <p class="nsb-title">NTSA Search Unavailable</p>
                        <p class="nsb-sub">{{ ntsaStatusMessage || 'The NTSA service did not respond. Fill in the vehicle details manually below to continue.' }}</p>
                      </div>
                    </div>
                    <div v-else class="ntsa-manual-banner tw-mb-4">
                      <div class="nsb-icon"><v-icon size="18" color="#D97706">mdi-car-search-outline</v-icon></div>
                      <div class="nsb-body">
                        <p class="nsb-title">NTSA Record Not Found</p>
                        <p class="nsb-sub">Insurance check passed. Please fill in the vehicle details manually below.</p>
                      </div>
                    </div>

                  </div>
                </transition>

                <!-- Vehicle detail fields (shown after verify) -->
                <div v-if="fetched.vehicle">

                  <!-- ── NTSA FOUND: Show only verified confirmation ── -->
                  <div v-if="ntsaFound" class="ntsa-verified-summary tw-mb-4">
                    <div class="ntsa-locked-notice tw-mb-3">
                      <v-icon x-small color="#059669" class="tw-mr-1">mdi-check-decagram</v-icon>
                      Vehicle details verified.
                    </div>
                    <!-- Logbook optional even when locked -->
                    <div class="form-row-2 tw-mt-3">
                      <div class="input-wrap">
                        <label class="input-lbl">Logbook Number <span class="req">*</span></label>
                        <v-text-field v-model="form.logbookNo" placeholder="e.g. K1234567A" autocomplete="off"
                          outlined dense hide-details class="sale-input" maxlength="10"></v-text-field>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Logbook Transferred? <span class="req">*</span></label>
                        <div class="logbook-transfer-toggle">
                          <label :class="['lbt-option', form.logbookTransferred === 'yes' ? 'lbt-yes' : '']">
                            <input type="radio" value="yes" v-model="form.logbookTransferred" class="tw-hidden" />
                            <v-icon x-small :color="form.logbookTransferred === 'yes' ? '#059669' : '#94a3b8'" class="tw-mr-1">mdi-check-circle</v-icon>
                            Yes, transferred
                          </label>
                          <label :class="['lbt-option', form.logbookTransferred === 'no' ? 'lbt-no' : '']">
                            <input type="radio" value="no" v-model="form.logbookTransferred" class="tw-hidden" />
                            <v-icon x-small :color="form.logbookTransferred === 'no' ? '#F59E0B' : '#94a3b8'" class="tw-mr-1">mdi-alert-circle-outline</v-icon>
                            No, not yet
                          </label>
                        </div>
                      </div>
                    </div>
                    <transition name="fade-up">
                      <div v-if="form.logbookTransferred === 'no'" class="lbt-warning tw-mt-2">
                        <v-icon x-small color="#92400e" class="tw-mr-1">mdi-information-outline</v-icon>
                        Note: The logbook must be transferred to the insured.
                      </div>
                    </transition>
                  </div>

                  <!-- ── NTSA NOT FOUND: Editable fields ── -->
                  <div v-else>
                    <!-- Make / Model -->
                    <div class="form-row-2 tw-mb-3">
                      <div class="input-wrap">
                        <label class="input-lbl">Vehicle Make <span class="req">*</span></label>
                        <v-text-field v-model="form.make" placeholder="e.g. TOYOTA" outlined dense hide-details class="sale-input"
                          @blur="form.make = (form.make || '').toUpperCase().trim()"></v-text-field>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Vehicle Model <span class="req">*</span></label>
                        <v-text-field v-model="form.model" placeholder="e.g. MARK II" outlined dense hide-details class="sale-input"
                          @blur="form.model = (form.model || '').toUpperCase().trim()"></v-text-field>
                      </div>
                    </div>

                    <div class="form-row-2 tw-mb-3">
                      <div class="input-wrap">
                        <label class="input-lbl">Year Of Manufacture <span class="req">*</span></label>
                        <v-text-field v-model="form.yom" placeholder="e.g. 2023" type="number" outlined dense hide-details class="sale-input"
                          @blur="form.yom = clampInt(form.yom, 1970, new Date().getFullYear())"></v-text-field>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Engine No. <span class="req">*</span></label>
                        <v-text-field v-model="form.engine" placeholder="e.g. 2TR1234567" autocomplete="off"
                          outlined dense hide-details class="sale-input"
                          @blur="form.engine = sanitizeEngine(form.engine)" maxlength="17"></v-text-field>
                        <span v-if="form.engine && !validateEngineNo(form.engine)"
                          class="tw-text-red-500 tw-text-[10px] tw-mt-1">6–17 characters required</span>
                      </div>
                    </div>

                    <div class="form-row-3 tw-mb-3">
                      <div class="input-wrap">
                        <label class="input-lbl">Engine Size (CC) <span class="req">*</span></label>
                        <v-text-field v-model="form.engineSize" placeholder="e.g. 1800" type="number"
                          autocomplete="off" outlined dense hide-details class="sale-input"
                          @blur="form.engineSize = clampInt(form.engineSize, 50, 9999)" maxlength="4"></v-text-field>
                        <span v-if="form.engineSize && !validateEngineSize(form.engineSize)"
                          class="tw-text-red-500 tw-text-[10px] tw-mt-1">Must be 50–9999 CC</span>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Colour <span class="req">*</span></label>
                        <v-text-field v-model="form.colour" placeholder="e.g. Silver"
                          autocomplete="off" outlined dense hide-details class="sale-input"
                          @blur="form.colour = sanitizeAlpha(form.colour, 20)" maxlength="20"></v-text-field>
                        <span v-if="form.colour && !validateColor(form.colour)"
                          class="tw-text-red-500 tw-text-[10px] tw-mt-1">2–20 characters required</span>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Seating Capacity <span class="req">*</span></label>
                        <v-text-field v-model="form.seatingCapacity" placeholder="e.g. 5" type="number"
                          autocomplete="off" outlined dense hide-details class="sale-input"
                          @blur="form.seatingCapacity = clampInt(form.seatingCapacity, 1, 100)" maxlength="3"></v-text-field>
                        <span v-if="form.seatingCapacity && !validateSeats(form.seatingCapacity)"
                          class="tw-text-red-500 tw-text-[10px] tw-mt-1">Must be 1–100</span>
                      </div>
                    </div>

                    <div class="form-row-2 tw-mb-4">
                      <div class="input-wrap">
                        <label class="input-lbl">Chassis No. <span class="req">*</span></label>
                        <v-text-field v-model="form.chassis" placeholder="e.g. JTEBU5JR0E5123456"
                          autocomplete="off" outlined dense hide-details class="sale-input"
                          @blur="form.chassis = sanitizeChassis(form.chassis)" maxlength="20"></v-text-field>
                        <span v-if="form.chassis && !validateChassis(form.chassis)"
                          class="tw-text-red-500 tw-text-[10px] tw-mt-1">10–20 characters required</span>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Logbook Number <span class="req">*</span></label>
                        <v-text-field v-model="form.logbookNo" placeholder="e.g. K1234567A"
                          autocomplete="off" outlined dense hide-details class="sale-input" maxlength="10"></v-text-field>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Logbook Transferred? <span class="req">*</span></label>
                        <div class="logbook-transfer-toggle">
                          <label :class="['lbt-option', form.logbookTransferred === 'yes' ? 'lbt-yes' : '']">
                            <input type="radio" value="yes" v-model="form.logbookTransferred" class="tw-hidden" />
                            <v-icon x-small :color="form.logbookTransferred === 'yes' ? '#059669' : '#94a3b8'" class="tw-mr-1">mdi-check-circle</v-icon>
                            Yes, transferred
                          </label>
                          <label :class="['lbt-option', form.logbookTransferred === 'no' ? 'lbt-no' : '']">
                            <input type="radio" value="no" v-model="form.logbookTransferred" class="tw-hidden" />
                            <v-icon x-small :color="form.logbookTransferred === 'no' ? '#F59E0B' : '#94a3b8'" class="tw-mr-1">mdi-alert-circle-outline</v-icon>
                            No, not yet
                          </label>
                        </div>
                      </div>
                    </div>
                    <!-- Warning when not transferred (manual entry Tray 1) -->
                    <transition name="fade-up">
                      <div v-if="form.logbookTransferred === 'no'" class="lbt-warning tw-mt-2 tw-mb-4">
                        <v-icon x-small color="#92400e" class="tw-mr-1">mdi-information-outline</v-icon>
                        Note: The logbook must be transferred to the insured.
                      </div>
                    </transition>
                  </div>

                  <!-- Tray 1 CTA (TPO — no valuation required) -->
                  <div class="tw-flex tw-gap-3 tw-mt-5">
                    <button class="btn-primary tw-w-full" @click="confirmVehicle" :disabled="!vehicleFormValid || tray1Busy">
                      <v-progress-circular v-if="tray1Busy" indeterminate size="14" width="2" color="white" class="tw-mr-2"></v-progress-circular>
                      <v-icon v-else small color="white" class="tw-mr-1">mdi-check</v-icon>
                      {{ tray1BusyLabel }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- ── COMPREHENSIVE JOURNEY ── -->
              <div v-else>
                <!-- ── DIRECT JOURNEY: Cover + Make/Model + Value (before verify) ── -->
                <div v-if="isDirectJourney" class="direct-fields-box tw-mb-5">
                  <!-- Cover Type + Usage -->
                  <div class="form-row-2 tw-mb-3">
                    <div class="input-wrap">
                      <label class="input-lbl">Cover Type <span class="req">*</span></label>
                      <v-select
                        v-model="form.cover_type"
                        :items="filteredCoverOptions"
                        item-text="name"
                        item-value="id"
                        outlined dense hide-details
                        class="sale-input"
                        @change="onCoverTypeChange"
                        :disabled="yomDowngradeDetected"
                      ></v-select>
                      <transition name="fade-up">
                        <div v-if="yomDowngradeDetected" class="tpo-flat-info tw-mt-2">
                          <v-icon x-small color="#00AEEF" class="tw-mr-1">mdi-lock-outline</v-icon>
                          TPO only — vehicle is {{ vehicleAge }} years old and does not qualify for Comprehensive.
                        </div>
                      </transition>
                    </div>
                    <div class="input-wrap">
                      <label class="input-lbl">Vehicle Usage <span class="req">*</span></label>
                      <v-select
                        v-model="form.usage_type"
                        :items="usageOptions"
                        item-text="name"
                        item-value="id"
                        outlined dense hide-details
                        class="sale-input"
                      ></v-select>
                    </div>
                  </div>

                  <!-- Make + Model -->
                  <div class="form-row-2 tw-mb-3">
                    <div class="input-wrap">
                      <label class="input-lbl">Vehicle Make <span class="req">*</span></label>
                      <v-autocomplete
                        v-model="form.make"
                        :items="directCarMakes"
                        item-text="makeDetails"
                        item-value="makeDetails"
                        placeholder="e.g. TOYOTA"
                        outlined dense hide-details
                        class="sale-input"
                      ></v-autocomplete>
                    </div>
                    <div class="input-wrap">
                      <label class="input-lbl">Vehicle Model <span class="req">*</span></label>
                      <v-autocomplete
                        v-model="form.model"
                        :items="directCarModels"
                        item-text="modelname"
                        item-value="modelname"
                        placeholder="e.g. MARK II"
                        outlined dense hide-details
                        class="sale-input"
                        :disabled="!form.make || directCarModels.length === 0"
                      ></v-autocomplete>
                    </div>
                  </div>

                  <!-- Year of Manufacture -->
                  <div class="form-row-2 tw-mb-3">
                    <div class="input-wrap">
                      <label class="input-lbl">Year of Manufacture <span class="req">*</span></label>
                      <v-select
                        v-model="form.yom"
                        :items="yomOptions"
                        placeholder="e.g. 2019"
                        outlined dense hide-details
                        class="sale-input"
                      ></v-select>
                      <span v-if="form.yom && vehicleAge > 12 && form.cover_type === 'comprehensive'"
                        class="tw-text-amber-600 tw-text-[10px] tw-mt-1">
                        Vehicle is {{ vehicleAge }} years old — may only qualify for TPO.
                      </span>
                    </div>
                  </div>

                  <!-- Vehicle value — only for Comprehensive, shown always (not after verify) -->
                  <div class="form-row-2 tw-mb-3" v-if="!isTpo">
                    <div class="input-wrap">
                      <label class="input-lbl">Estimated Vehicle Value (Ksh) <span class="req">*</span></label>
                      <v-text-field
                        v-model="formattedValueInput"
                        placeholder="e.g. 1,500,000"
                        outlined dense hide-details
                        class="sale-input"
                      ></v-text-field>
                      <span class="tw-text-[10px] tw-text-gray-400 tw-mt-1">Min: Ksh 500,000</span>
                    </div>
                  </div>
                   <!-- TPO info strip -->
                  <div v-if="isTpo" class="tpo-flat-info tw-mb-3">
                    <v-icon x-small color="#00AEEF" class="tw-mr-1">mdi-information-outline</v-icon>
                    TPO is charged at a flat statutory rate — no vehicle value required.
                  </div>

                  <!-- ── Additional Benefits (Comprehensive only) ── -->
                  <div v-if="!isTpo && form.make && form.model" class="tw-mb-3">
                    <div class="section-divider tw-mt-1 tw-mb-3">Additional Benefits</div>

                    <div v-if="benefitsLoading" class="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-400 tw-mb-3">
                      <v-progress-circular indeterminate size="12" width="2" color="#00AEEF"></v-progress-circular>
                      Loading benefits…
                    </div>

                    <!-- Updated: Using shared BenefitLimitSelector component -->
                    <benefit-limit-selector
                      :benefits="chargeableBenefits"
                      :selected-addons.sync="form.addons_dynamic"
                      :custom-limits.sync="form.benefit_custom_limits"
                      :loading="benefitsLoading"
                      :updating="livePremiumLoading"
                      :premium-lookup="benefitPremiumMap"
                      :car-value="form.value"
                      :basic-premium="liveBasicPremium"
                      @change="calculateLivePremium"
                    />
                  </div>

                  <!-- ── TPO: statutory benefits summary (read-only) ── -->
                  <div v-if="isTpo && form.make && form.model" class="tw-mb-3">
                    <div class="section-divider tw-mt-1 tw-mb-3">What's Covered — TPO Statutory Benefits</div>
                    <div class="tpo-benefit-mini-list">
                      <div class="tpo-benefit-mini-row">
                        <v-icon x-small color="#00AEEF" class="tw-mr-1">mdi-account-group-outline</v-icon>
                        <span>Third Party Person Liability</span>
                        <span class="tw-ml-auto tw-text-[10px] tw-font-bold tw-text-[#00AEEF]">Ksh 3,000,000</span>
                      </div>
                      <div class="tpo-benefit-mini-row">
                        <v-icon x-small color="#10b981" class="tw-mr-1">mdi-seat-passenger</v-icon>
                        <span>Passengers Liability</span>
                        <span class="tw-ml-auto tw-text-[10px] tw-font-bold tw-text-[#10b981]">Ksh 4,000,000</span>
                      </div>
                      <div class="tpo-benefit-mini-row">
                        <v-icon x-small color="#EF4444" class="tw-mr-1">mdi-car-off</v-icon>
                        <span>Third Party Property Damage</span>
                        <span class="tw-ml-auto tw-text-[10px] tw-font-bold tw-text-red-500">Ksh 5,000,000</span>
                      </div>
                    </div>
                  </div>

                  <!-- Live premium estimate — shows for comprehensive OR tpo once make+model+yom present -->
                  <transition name="fade-up">
                    <div v-if="form.make && form.model && form.yom" class="live-estimate-card tw-mt-2" :class="{ 'verified-card': fetched.vehicle }">
                      <!-- Header row -->
                      <div class="tw-flex tw-justify-between tw-items-center tw-mb-3">
                        <span class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider" :style="(fetched.vehicle && !directMakeMismatch) ? 'color:#10b981' : 'color:rgba(255,255,255,0.5)'">
                          <v-icon x-small :color="(fetched.vehicle && !directMakeMismatch) ? '#10b981' : 'rgba(255,255,255,0.5)'">
                            {{ (fetched.vehicle && !directMakeMismatch) ? 'mdi-check-decagram' : 'mdi-calculator' }}
                          </v-icon>
                          {{ (fetched.vehicle && !directMakeMismatch) ? 'Verified Premium Estimate' : (isTpo ? 'TPO Flat Rate Estimate' : 'Live Premium Estimate') }}
                        </span>
                        <div v-if="livePremiumLoading" class="tw-flex tw-items-center tw-gap-2">
                          <v-progress-circular indeterminate size="12" width="1.5" color="white"></v-progress-circular>
                          <span class="tw-text-xs tw-opacity-60">Calculating…</span>
                        </div>
                      </div>

                      <!-- Not enough value entered yet -->
                      <div v-if="!isTpo && form.value < 500000 && !livePremiumLoading" class="tw-text-center tw-py-2">
                        <span class="tw-text-xs tw-opacity-40">Enter vehicle value ≥ Ksh 500,000 to see estimate</span>
                      </div>

                      <!-- Premium breakdown -->
                      <template v-else-if="livePremium && !livePremiumLoading">
                        <div class="lec-row">
                          <span>{{ isTpo ? 'TPO Flat Rate' : 'Basic Premium' }}</span>
                          <span>{{ fmtCurrency(liveBasicPremium || livePremium) }}</span>
                        </div>

                        <!-- Benefit Breakdown -->
                        <template v-if="form.addons_dynamic.length > 0">
                          <div v-for="b in chargeableBenefits.filter(x => form.addons_dynamic.includes(x.sectCode))" :key="b.sectCode" class="lec-row lec-row-dim">
                            <span class="tw-capitalize">{{ (b.sectDesc || 'Benefit').toLowerCase() }}</span>
                            <span>+ {{ fmtCurrency(getBenefitPremium(b)) }}</span>
                          </div>
                        </template>

                        <div class="lec-divider"></div>
                        <div class="lec-row lec-row-total">
                          <span>Estimated Total</span>
                          <span>{{ fmtCurrency(manualTotalPremium) }}</span>
                        </div>
                        <p v-if="liveRateLabel" class="lec-rate-note">{{ liveRateLabel }}</p>
                        <p class="tw-text-[10px] tw-mt-2 tw-mb-0 tw-text-center tw-font-semibold" style="color:#ffd700; opacity:0.85;">
                          <span v-if="fetched.vehicle"></span>
                          <span v-else>{{ isTpo ? 'Flat statutory rate. Final on confirmation.' : '.' }}</span>
                        </p>
                      </template>

                      <!-- TPO loading done but no value shown yet -->
                      <div v-else-if="isTpo && !livePremium && !livePremiumLoading" class="tw-text-center tw-py-2">
                        <span class="tw-text-xs tw-opacity-40">—</span>
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- Reg Number + Verify — shown after premium estimate is available -->
                <transition name="fade-up">
                    <div v-if="!isDirectJourney || livePremium || fetched.vehicle" class="tw-mb-4">
                    <div class="section-divider tw-mt-1 tw-mb-3">Registration &amp; Verification</div>
                    <div class="verify-info-strip tw-mb-4">
                      <v-icon small color="#0284c7" class="tw-mr-2">mdi-shield-check-outline</v-icon>
                      <div>
                        This step checks the vehicle against <strong>NTSA</strong> records and runs <strong>DMVIC</strong> double-insurance screening to ensure the vehicle is not already covered.
                      </div>
                    </div>
                    <div class="form-row-2">
                      <div class="input-wrap">
                        <label class="input-lbl">Registration Number <span class="req">*</span></label>
                        <div class="input-with-btn">
                          <v-text-field v-model="form.regNo" placeholder="e.g. KCD 123A" outlined dense hide-details class="sale-input tw-flex-1" maxlength="10" @keyup.enter="fetchVehicleDetails" @blur="form.regNo = sanitizeReg(form.regNo)"></v-text-field>
                          <button class="verify-btn" @click="fetchVehicleDetails" :disabled="fetching.vehicle || !form.regNo">
                            <v-progress-circular v-if="fetching.vehicle" indeterminate size="14" width="2" color="white"></v-progress-circular>
                            <template v-else><v-icon small color="white">mdi-magnify</v-icon> Verify</template>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>

                <!-- Two-step verification progress -->
                <transition name="fade-up">
                  <div v-if="fetching.vehicle" class="verify-progress-strip tw-mb-4">
                    <div class="verify-step" :class="{ 'vp-active': !fetching.ntsaSearch, 'vp-done': fetching.ntsaSearch }">
                      <div class="vp-dot">
                        <v-progress-circular v-if="!fetching.ntsaSearch" indeterminate size="10" width="1.5" color="#00AEEF"></v-progress-circular>
                        <v-icon v-else x-small color="#10b981">mdi-check</v-icon>
                      </div>
                      <span>Checking insurance status…</span>
                    </div>
                    <div class="vp-connector" :class="{ done: fetching.ntsaSearch }"></div>
                    <div class="verify-step" :class="{ 'vp-active': fetching.ntsaSearch, 'vp-pending': !fetching.ntsaSearch }">
                      <div class="vp-dot">
                        <v-progress-circular v-if="fetching.ntsaSearch" indeterminate size="10" width="1.5" color="#00AEEF"></v-progress-circular>
                        <v-icon v-else x-small :color="fetching.ntsaSearch ? '#00AEEF' : '#cbd5e1'">mdi-car-search-outline</v-icon>
                      </div>
                      <span>Fetching NTSA vehicle data…</span>
                    </div>
                  </div>
                </transition>

                <transition name="fade-up">
                  <div v-if="fetched.vehicle">

                    <!-- ══ DIRECT JOURNEY MAKE MISMATCH ══ -->
                    <div v-if="directMakeMismatch" class="double-insure-block tw-mb-4">
                      <div class="di-header">
                        <div class="di-icon-wrap" style="background:#FEF3C7;">
                          <v-icon size="22" color="#D97706">mdi-car-alert</v-icon>
                        </div>
                        <div>
                          <p class="di-title" style="color:#92400E;">Vehicle Mismatch Detected</p>
                          <p class="di-subtitle" style="color:#B45309;">
                            The registration does not match the vehicle details you entered.
                            Please reinitiate the sale with the correct vehicle details.
                          </p>
                        </div>
                      </div>
                      <button class="btn-danger tw-w-full tw-mt-3"
                        @click="startNewSale">
                        <v-icon small color="white" class="tw-mr-1">mdi-refresh</v-icon>
                        Reinitiate Sale
                      </button>
                    </div>

                    <!-- ══ DOUBLE INSURANCE BLOCK ══ -->
                    <div v-if="verifyError === 'double_insurance'" class="double-insure-block tw-mb-4">
                      <div class="di-header">
                        <div class="di-icon-wrap">
                          <v-icon size="22" color="#DC2626">mdi-shield-alert</v-icon>
                        </div>
                        <div>
                          <p class="di-title">Active Insurance Detected</p>
                          <p class="di-subtitle">
                            Active insurance policy detected for this vehicle. Please ensure your selected start date falls after the current cover's expiry.
                            <template v-if="!doubleInsuranceEarliestStart">
                              A new policy cannot be issued until the existing one expires.
                            </template>
                          </p>
                        </div>
                      </div>
                      <p class="di-footer-note tw-mt-4">
                        <v-icon x-small color="#DC2626">mdi-information-outline</v-icon>
                        If you believe this is an error, please contact the support team or ask the customer to contact their current insurer.
                      </p>
                    </div>

                    <!-- Normal post-verify alerts -->
                    <template v-if="verifyError !== 'double_insurance'">
                      <div v-if="ntsaFound && !mismatchDetected && !directMakeMismatch" class="ntsa-success-banner tw-mb-4">
                        <div class="nsb-icon"><v-icon size="18" color="#059669">mdi-check-decagram</v-icon></div>
                        <div class="nsb-body">
                          <p class="nsb-title">NTSA Record Found — proceed</p>
                          <p class="nsb-sub">Vehicle record confirmed and insurance check passed.</p>
                        </div>
                      </div>

                      <div v-else-if="ntsaUnavailable && !mismatchDetected && !directMakeMismatch" class="ntsa-manual-banner tw-mb-4">
                        <div class="nsb-icon"><v-icon size="18" color="#D97706">mdi-cloud-off-outline</v-icon></div>
                        <div class="nsb-body">
                          <p class="nsb-title">NTSA Search Unavailable</p>
                          <p class="nsb-sub">{{ ntsaStatusMessage || 'The NTSA service did not respond. Fill in the vehicle details manually below to continue.' }}</p>
                        </div>
                      </div>

                      <div v-else-if="!ntsaFound && !mismatchDetected && !directMakeMismatch" class="ntsa-manual-banner tw-mb-4">
                        <div class="nsb-icon"><v-icon size="18" color="#D97706">mdi-car-search-outline</v-icon></div>
                        <div class="nsb-body">
                          <p class="nsb-title">NTSA Record Not Found</p>
                          <p class="nsb-sub">Insurance check passed. Please fill in the vehicle details manually below.</p>
                        </div>
                      </div>

                      <div v-if="mismatchDetected" class="alert-box alert-warning tw-mb-4">
                        <div class="alert-icon-wrap alert-icon-warning"><v-icon size="18" color="#F59E0B">mdi-alert-circle-outline</v-icon></div>
                        <div>
                          <p class="alert-title">Vehicle Mismatch Detected</p>
                          <p class="alert-body">
                            The registration number <strong>{{ form.regNo }}</strong> does not match the vehicle details provided on this quote.
                            Please verify the registration or re-initiate the quote with the correct make and model.
                          </p>
                        </div>
                      </div>

                      <div v-if="yomDowngradeDetected && !mismatchDetected && !directMakeMismatch" class="alert-box alert-info tw-mb-4">
                        <div class="alert-icon-wrap alert-icon-info"><v-icon size="18" color="#00AEEF">mdi-information-outline</v-icon></div>
                        <div><p class="alert-title">TPO Auto-Applied</p><p class="alert-body">Vehicle is older than 12 years — premium recalculated for Third Party Only.</p></div>
                      </div>

                      <div v-if="vehicleAge > 12 && form.cover_type === 'comprehensive' && !mismatchDetected && !directMakeMismatch" class="alert-box alert-warning tw-mb-4">
                        <div class="alert-icon-wrap alert-icon-warning"><v-icon size="18" color="#F59E0B">mdi-alert-circle-outline</v-icon></div>
                        <div>
                          <p class="alert-title">Comprehensive Qualification</p>
                          <p class="alert-body">This vehicle is {{ vehicleAge }} years old. Covers older than 12 years typically only qualify for TPO. Please confirm eligibility with underwriting.</p>
                        </div>
                      </div>
                    </template>

                  </div>
                </transition>

                <!-- ── Vehicle Detail Fields ── -->
                <div v-if="fetched.vehicle && !mismatchDetected && !directMakeMismatch && verifyError !== 'double_insurance'">
                    <div class="section-divider">Vehicle Details</div>

                    <!-- ── NTSA FOUND: Locked summary chip grid ── -->
                    <div v-if="ntsaFound" class="ntsa-verified-summary tw-mb-4">
                      <div class="ntsa-locked-notice tw-mb-3">
                        <v-icon x-small color="#059669" class="tw-mr-1">mdi-check-decagram</v-icon>
                        Vehicle details verified.
                      </div>

                      <div class="form-row-2 tw-mt-3">
                        <div class="input-wrap">
                          <label class="input-lbl">Logbook Number <span class="req">*</span></label>
                          <v-text-field v-model="form.logbookNo" placeholder="e.g. K1234567A" autocomplete="off"
                            outlined dense hide-details class="sale-input" maxlength="10"></v-text-field>
                        </div>
                        <div class="input-wrap">
                          <label class="input-lbl">Logbook Transferred? <span class="req">*</span></label>
                          <div class="logbook-transfer-toggle">
                            <label :class="['lbt-option', form.logbookTransferred === 'yes' ? 'lbt-yes' : '']">
                              <input type="radio" value="yes" v-model="form.logbookTransferred" class="tw-hidden" />
                              <v-icon x-small :color="form.logbookTransferred === 'yes' ? '#059669' : '#94a3b8'" class="tw-mr-1">mdi-check-circle</v-icon>
                              Yes, transferred
                            </label>
                            <label :class="['lbt-option', form.logbookTransferred === 'no' ? 'lbt-no' : '']">
                              <input type="radio" value="no" v-model="form.logbookTransferred" class="tw-hidden" />
                              <v-icon x-small :color="form.logbookTransferred === 'no' ? '#F59E0B' : '#94a3b8'" class="tw-mr-1">mdi-alert-circle-outline</v-icon>
                              No, not yet
                            </label>
                          </div>
                        </div>
                      </div>
                      <transition name="fade-up">
                        <div v-if="form.logbookTransferred === 'no'" class="lbt-warning tw-mt-2">
                          <v-icon x-small color="#92400e" class="tw-mr-1">mdi-information-outline</v-icon>
                          Note: The logbook must be transferred to the insured.
                        </div>
                      </transition>
                    </div>

                    <!-- ── NTSA NOT FOUND: Editable form fields ── -->
                    <div v-else>
                      <div class="form-row-2 tw-mb-3">
                        <div class="input-wrap">
                          <label class="input-lbl">Chassis Number <span class="req">*</span></label>
                          <v-text-field v-model="form.chassis" placeholder="e.g. JTEBU5JR0E5123456"
                            autocomplete="off" outlined dense hide-details class="sale-input"
                            @blur="form.chassis = sanitizeChassis(form.chassis)" maxlength="20"></v-text-field>
                          <span v-if="form.chassis && !validateChassis(form.chassis)"
                            class="tw-text-red-500 tw-text-[10px] tw-mt-1">10–20 characters required</span>
                        </div>
                        <div class="input-wrap">
                          <label class="input-lbl">Engine Number <span class="req">*</span></label>
                          <v-text-field v-model="form.engine" placeholder="e.g. 2TR1234567"
                            autocomplete="off" outlined dense hide-details class="sale-input"
                            @blur="form.engine = sanitizeEngine(form.engine)" maxlength="17"></v-text-field>
                          <span v-if="form.engine && !validateEngineNo(form.engine)"
                            class="tw-text-red-500 tw-text-[10px] tw-mt-1">6–17 characters required</span>
                        </div>
                      </div>

                      <div class="form-row-3 tw-mb-3">
                        <div class="input-wrap">
                          <label class="input-lbl">Engine Size (CC) <span class="req">*</span></label>
                          <v-text-field v-model="form.engineSize" placeholder="e.g. 1800" type="number"
                            autocomplete="off" outlined dense hide-details class="sale-input"
                            @blur="form.engineSize = clampInt(form.engineSize, 50, 9999)" maxlength="4"></v-text-field>
                          <span v-if="form.engineSize && !validateEngineSize(form.engineSize)"
                            class="tw-text-red-500 tw-text-[10px] tw-mt-1">Must be 50–9999 CC</span>
                        </div>
                        <div class="input-wrap">
                          <label class="input-lbl">Seating Capacity <span class="req">*</span></label>
                          <v-text-field v-model="form.seatingCapacity" placeholder="e.g. 5" type="number"
                            autocomplete="off" outlined dense hide-details class="sale-input"
                            @blur="form.seatingCapacity = clampInt(form.seatingCapacity, 1, 100)" maxlength="3"></v-text-field>
                          <span v-if="form.seatingCapacity && !validateSeats(form.seatingCapacity)"
                            class="tw-text-red-500 tw-text-[10px] tw-mt-1">Must be 1–100</span>
                        </div>
                        <div class="input-wrap">
                          <label class="input-lbl">Colour <span class="req">*</span></label>
                          <v-text-field v-model="form.colour" placeholder="e.g. Silver"
                            autocomplete="off" outlined dense hide-details class="sale-input"
                            @blur="form.colour = sanitizeAlpha(form.colour, 20)" maxlength="20"></v-text-field>
                          <span v-if="form.colour && !validateColor(form.colour)"
                            class="tw-text-red-500 tw-text-[10px] tw-mt-1">2–20 characters required</span>
                        </div>
                      </div>

                      <div class="form-row-2 tw-mb-4">
                        <div class="input-wrap">
                          <label class="input-lbl">Logbook Number <span class="req">*</span></label>
                          <v-text-field v-model="form.logbookNo" placeholder="e.g. K1234567A"
                            autocomplete="off" outlined dense hide-details class="sale-input" maxlength="10"></v-text-field>
                        </div>
                        <div class="input-wrap">
                          <label class="input-lbl">Logbook Transferred? <span class="req">*</span></label>
                          <div class="logbook-transfer-toggle">
                            <label :class="['lbt-option', form.logbookTransferred === 'yes' ? 'lbt-yes' : '']">
                              <input type="radio" value="yes" v-model="form.logbookTransferred" class="tw-hidden" />
                              <v-icon x-small :color="form.logbookTransferred === 'yes' ? '#059669' : '#94a3b8'" class="tw-mr-1">mdi-check-circle</v-icon>
                              Yes, transferred
                            </label>
                            <label :class="['lbt-option', form.logbookTransferred === 'no' ? 'lbt-no' : '']">
                              <input type="radio" value="no" v-model="form.logbookTransferred" class="tw-hidden" />
                              <v-icon x-small :color="form.logbookTransferred === 'no' ? '#F59E0B' : '#94a3b8'" class="tw-mr-1">mdi-alert-circle-outline</v-icon>
                              No, not yet
                            </label>
                          </div>
                        </div>
                      </div>
                      <transition name="fade-up">
                        <div v-if="form.logbookTransferred === 'no'" class="lbt-warning tw-mt-2 tw-mb-4">
                          <v-icon x-small color="#92400e" class="tw-mr-1">mdi-information-outline</v-icon>
                          Note: The logbook must be transferred to the insured.
                        </div>
                      </transition>
                      <div class="form-row-2 tw-mb-4">
                        <div class="input-wrap">
                          <label class="input-lbl">Year Of Manufacture <span class="req">*</span></label>
                          <v-text-field v-model="form.yom" placeholder="e.g. 2023" type="number"
                            outlined dense hide-details class="sale-input"
                            @blur="form.yom = clampInt(form.yom, 1970, new Date().getFullYear())"></v-text-field>
                        </div>
                      </div>
                    </div>
                    <div v-if="!isTpo">
                      <div class="section-divider tw-mt-5">Valuation Assignment</div>
                      <p class="tray-desc tw-mb-4">Select a valuer near you and schedule the valuation (within 14 days of cover start).</p>

                      <div class="inspection-box tw-mb-5">

                        <!-- Step 1: Location filter -->
                        <div class="tw-mb-4">
                          <label class="input-lbl">Your Location <span class="req">*</span></label>
                          <p class="tw-text-[11px] tw-text-gray-400 tw-mb-2">Filter valuers available in your area</p>

                          <!-- Town selector -->
                          <v-select
                            v-model="valuerLocationFilter"
                            :items="valuerTownOptions"
                            placeholder="Select your town / area"
                            outlined dense hide-details
                            class="sale-input tw-mb-3"
                            clearable
                            @change="form.valuer = ''; selectedValuerData = null"
                          ></v-select>

                          <!-- Nairobi sub-area selector (shown only when Nairobi selected) -->
                          <transition name="fade-up">
                            <div v-if="valuerLocationFilter === 'Nairobi'" class="tw-mt-2">
                              <label class="input-lbl tw-mb-1">Nairobi Area <span class="optional">(optional, refines results)</span></label>
                              <div class="nairobi-area-grid">
                                <label
                                  v-for="area in nairobiAreaOptions" :key="area"
                                  :class="['nairobi-area-chip', valuerSearchQuery === area ? 'area-active' : '']"
                                  @click="valuerSearchQuery = valuerSearchQuery === area ? '' : area"
                                >
                                  {{ area }}
                                </label>
                              </div>
                            </div>
                          </transition>
                        </div>

                        <!-- Step 2: Valuer cards filtered by location -->
                        <transition name="fade-up">
                          <div v-if="valuerLocationFilter">
                            <label class="input-lbl tw-mb-3">
                              Available Valuers
                              <span class="tw-text-[10px] tw-text-gray-400 tw-ml-2 tw-font-normal">
                                {{ filteredValuers.length }} found in {{ valuerSearchQuery || valuerLocationFilter }}
                              </span>
                            </label>

                            <div v-if="filteredValuers.length === 0" class="valuer-empty-state">
                              <v-icon color="#94a3b8" size="28">mdi-magnify-close</v-icon>
                              <p>No valuers found for this area.</p>
                              <p class="tw-text-[11px]">Try selecting "Nairobi" or a nearby town.</p>
                            </div>

                            <div v-else class="valuer-cards-list tw-mb-3">
                              <div
                                v-for="valuer in filteredValuers" :key="valuer.id"
                                :class="['valuer-card', form.valuer === valuer.name ? 'valuer-card-selected' : '']"
                                @click="selectValuer(valuer)"
                              >
                                <div class="vc-head">
                                  <div class="vc-avatar">{{ valuer.name.split(' ').slice(0,2).map(w=>w[0]).join('') }}</div>
                                  <div class="vc-info">
                                    <p class="vc-name">{{ valuer.name }}</p>
                                    <p class="vc-hq">{{ valuer.hq }}</p>
                                  </div>
                                  <div v-if="form.valuer === valuer.name" class="vc-check">
                                    <v-icon small color="white">mdi-check</v-icon>
                                  </div>
                                </div>
                                <div class="vc-meta">
                                  <span class="vc-meta-chip">
                                    <v-icon x-small color="#64748b" class="tw-mr-1">mdi-account-outline</v-icon>{{ valuer.contact }}
                                  </span>
                                  <span class="vc-meta-chip">
                                    <v-icon x-small color="#64748b" class="tw-mr-1">mdi-phone-outline</v-icon>{{ valuer.phone }}
                                  </span>
                                  <span v-if="valuer.mobile" class="vc-meta-chip vc-mobile-badge">
                                    <v-icon x-small color="#059669" class="tw-mr-1">mdi-car-cog</v-icon>Mobile valuer
                                  </span>
                                </div>
                                <!-- Branch info when relevant -->
                                <div v-if="valuer.branches && relevantBranch(valuer)" class="vc-branch">
                                  <v-icon x-small color="#00AEEF" class="tw-mr-1">mdi-map-marker-outline</v-icon>
                                  <span>{{ relevantBranch(valuer).location }}: {{ relevantBranch(valuer).address }}</span>
                                </div>
                              </div>
                            </div>

                            <!-- Email sent confirmation -->
                            <transition name="fade-up">
                              <div v-if="valuerEmailSent" class="valuer-email-sent tw-mb-3">
                                <v-icon size="16" color="#059669" class="tw-mr-2">mdi-email-check</v-icon>
                                Valuation request sent to <strong>{{ selectedValuerData && selectedValuerData.name }}</strong>.
                                A copy was sent to the client and underwriting team.
                              </div>
                            </transition>
                          </div>
                        </transition>

                        <!-- Valuation date (shown after valuer selected) -->
                        <transition name="fade-up">
                          <div v-if="form.valuer" class="form-row-2 tw-mb-3">
                            <div class="input-wrap">
                              <label class="input-lbl">Valuation Date <span class="req">*</span></label>
                              <v-menu v-model="valMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field :value="formatDate(form.valuationDate)" label="" readonly outlined dense hide-details class="sale-input" v-bind="attrs" v-on="on">
                                    <template v-slot:prepend-inner><v-icon small color="#94a3b8">mdi-calendar</v-icon></template>
                                  </v-text-field>
                                </template>
                                <v-date-picker
                                  v-model="form.valuationDate"
                                  no-title
                                  :min="form.startDate"
                                  :max="maxValuationDate"
                                  color="#00AEEF"
                                  @input="valMenu = false"
                                ></v-date-picker>
                              </v-menu>
                            </div>
                            <div class="input-wrap tw-flex tw-flex-col tw-justify-end">
                              <div class="valuer-auto-notify-note">
                                <v-icon x-small color="#059669" class="tw-mr-1">mdi-email-check-outline</v-icon>
                                The valuer, client and underwriting team will be notified automatically upon successful payment.
                              </div>
                            </div>
                          </div>
                        </transition>

                      </div>
                    </div>

                  <!-- Tray 1 CTA -->
                  <div class="tw-flex tw-gap-3 tw-mt-2">
                    <button v-if="!mismatchDetected" class="btn-primary tw-flex-1" @click="confirmVehicle" :disabled="!vehicleFormValid || tray1Busy">
                      <v-progress-circular v-if="tray1Busy" indeterminate size="14" width="2" color="white" class="tw-mr-2"></v-progress-circular>
                      <v-icon v-else small color="white" class="tw-mr-1">mdi-check</v-icon>
                      {{ tray1BusyLabel }}
                    </button>
                    <button v-else class="btn-danger tw-flex-1" @click="$router.push({ name: 'AgentUnderwriting' })">
                      <v-icon small color="white" class="tw-mr-1">mdi-refresh</v-icon> Initiate New Quote
                    </button>
                  </div>
                </div>

                <!-- Mismatch-only CTA -->
                <div v-else-if="fetched.vehicle && mismatchDetected" class="tw-mt-4">
                  <button class="btn-danger tw-w-full" @click="$router.push({ name: 'AgentSale', query: { journey: 'direct' } })">
                    <v-icon small color="white" class="tw-mr-1">mdi-refresh</v-icon> Initiate New Quote
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ════════════════════════════════════════
               TRAY 2 · CUSTOMER DETAILS
          ════════════════════════════════════════ -->
          <div :class="['tray-card', activeTray===2?'tray-open':'', completedTrays.includes(2)?'tray-done':'', !completedTrays.includes(1)&&activeTray!==2?'tray-locked':'']">
            <div class="tray-head" @click="goToTray(2)">
              <div class="tray-head-left">
                <div class="tray-num" :class="{ done: completedTrays.includes(2), locked: !completedTrays.includes(1)&&activeTray!==2 }">
                  <v-icon v-if="completedTrays.includes(2)" x-small color="white">mdi-check</v-icon>
                  <span v-else>2</span>
                </div>
                <div>
                  <p class="tray-title">Customer Details</p>
                  <p class="tray-sub" v-if="completedTrays.includes(2)">{{ form.fullName }} · {{ form.idNo }}</p>
                  <p class="tray-sub" v-else>Verify + contact &amp; documents</p>
                </div>
              </div>
              <v-icon size="18" :color="completedTrays.includes(2)?'#10b981':'#94a3b8'">
                {{ completedTrays.includes(2) ? 'mdi-check-circle' : activeTray===2 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>

            <div class="tray-body" v-show="activeTray===2">
              <p class="tray-desc">Enter the customer's first name and ID number for verification.</p>

              <!-- KYC Documents Reminder — shown before verification -->
              <!-- KYC Documents Reminder — shown before verification (REMOVED) -->
              <!-- <div v-if="!fetched.customer" class="kyc-reminder tw-mb-5">
                ...
              </div> -->

              <!-- IPRS Lookup -->
              <div class="form-row-2 tw-mb-4">
                <div class="input-wrap">
                  <label class="input-lbl">Expected First Name <span class="req">*</span></label>
                  <v-text-field v-model="form.expectedFirstName" placeholder="e.g. JOHN" outlined dense hide-details class="sale-input tw-uppercase" maxlength="50" @keyup.enter="fetchCustomerDetails" @blur="form.expectedFirstName = sanitizeAlpha(form.expectedFirstName, 50).toUpperCase()"></v-text-field>
                  <span v-if="form.expectedFirstName && /[0-9]/.test(form.expectedFirstName)" class="tw-text-red-500 tw-text-[10px] tw-mt-1">Name must not contain digits</span>
                </div>
                <div class="input-wrap">
                  <label class="input-lbl">National ID Number <span class="req">*</span></label>
                  <div class="input-with-btn">
                    <v-text-field v-model="form.idNo" placeholder="e.g. 12345678" type="number" outlined dense hide-details class="sale-input tw-flex-1" maxlength="9" @keyup.enter="fetchCustomerDetails" @blur="form.idNo = (form.idNo || '').replace(/\D/g, '').slice(0, 9)"></v-text-field>
                    <button class="verify-btn" @click="fetchCustomerDetails" :disabled="fetching.customer || !form.idNo || !form.expectedFirstName">
                      <v-progress-circular v-if="fetching.customer" indeterminate size="14" width="2" color="white"></v-progress-circular>
                      <template v-else><v-icon small color="white">mdi-magnify</v-icon> Verify</template>
                    </button>
                  </div>
                </div>
              </div>

              <transition name="fade-up">
                <div v-if="fetched.customer">
                  <!-- IPRS Unavailable Banner -->
                  <div v-if="iprsUnavailable && !customerHarvestingDetected" class="iprs-unavailable-banner tw-mb-4">
                    <div class="iub-icon">
                      <v-icon size="18" color="#D97706">mdi-database-alert-outline</v-icon>
                    </div>
                    <div class="iub-body">
                      <p class="iub-title">Identity Registry Unavailable</p>
                      <p class="iub-sub">IPRS could not be reached. Please fill in the customer details manually below. All required documents must be uploaded.</p>
                    </div>
                  </div>

                  <!-- Manual Entry Form (shown when IPRS unavailable) -->
                  <div v-if="iprsManualEntry" class="manual-customer-form tw-mb-5">
                    <div class="mcf-header">
                      <v-icon small color="#1E4276" class="tw-mr-2">mdi-account-edit-outline</v-icon>
                      <span>Enter Customer Details Manually</span>
                    </div>
                    <div class="form-row-2 tw-mb-3 tw-mt-4">
                      <div class="input-wrap">
                        <label class="input-lbl">Full Name <span class="req">*</span></label>
                        <v-text-field
                          v-model="manualForm.fullName"
                          placeholder="e.g. JOHN KAMAU MWANGI"
                          outlined dense hide-details
                          class="sale-input tw-uppercase"
                          @blur="manualForm.fullName = (manualForm.fullName || '').toUpperCase().trim()"
                        ></v-text-field>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Gender <span class="req">*</span></label>
                        <div class="gender-toggle tw-mt-1">
                          <label :class="['gender-option', manualForm.gender==='M'?'gender-active':'']">
                            <input type="radio" value="M" v-model="manualForm.gender" class="tw-hidden" />
                            <v-icon small :color="manualForm.gender==='M'?'#00AEEF':'#94a3b8'" class="tw-mr-1">mdi-gender-male</v-icon> Male
                          </label>
                          <label :class="['gender-option', manualForm.gender==='F'?'gender-active':'']">
                            <input type="radio" value="F" v-model="manualForm.gender" class="tw-hidden" />
                            <v-icon small :color="manualForm.gender==='F'?'#E91E8C':'#94a3b8'" class="tw-mr-1">mdi-gender-female</v-icon> Female
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="form-row-2 tw-mb-4">
                      <div class="input-wrap">
                        <label class="input-lbl">Date of Birth <span class="optional">(optional)</span></label>
                        <v-text-field
                          v-model="manualForm.dateOfBirth"
                          type="date"
                          outlined dense hide-details
                          class="sale-input"
                        ></v-text-field>
                      </div>
                    </div>
                    <button
                      class="btn-primary tw-w-full"
                      @click="applyManualCustomer"
                      :disabled="!manualForm.fullName || !manualForm.gender"
                    >
                      <v-icon small color="white" class="tw-mr-1">mdi-check</v-icon>
                      Use These Details
                    </button>
                  </div>

                  <!-- Mismatch alert -->
                  <div v-if="customerHarvestingDetected" class="alert-box alert-danger tw-mb-5">
                    <div class="alert-icon-wrap alert-icon-danger"><v-icon size="18" color="#EF4444">mdi-shield-alert-outline</v-icon></div>
                    <div>
                      <p class="alert-title">Verification Failed</p>
                      <p class="alert-body">The first name <strong>"{{ form.expectedFirstName }}"</strong> does not match verification records for ID {{ form.idNo }}. Please check and retry.</p>
                    </div>
                  </div>

                  <div v-if="!customerHarvestingDetected">
                    <!-- IPRS populated read-only -->
                    <div class="iprs-verified-card tw-mb-5">
                      <div class="iprs-verified-header">
                        <v-icon size="16" color="#059669" class="tw-mr-2">mdi-check-decagram</v-icon>
                        <span>Identity Verified</span>
                      </div>
                      <div class="iprs-name-row">
                        <span class="iprs-name">{{ form.fullName }}</span>
                      </div>
                      <div class="iprs-meta-row">
                        <div class="iprs-meta-chip">
                          <v-icon x-small :color="form.gender==='M'?'#00AEEF':'#E91E8C'" class="tw-mr-1">
                            {{ form.gender==='M' ? 'mdi-gender-male' : 'mdi-gender-female' }}
                          </v-icon>
                          <span>{{ form.gender==='M'?'Male':form.gender==='F'?'Female':'—' }}</span>
                        </div>
                        <div class="iprs-meta-chip" v-if="form.dateOfBirth">
                          <v-icon x-small color="#64748b" class="tw-mr-1">mdi-cake-variant-outline</v-icon>
                          <span>{{ form.dateOfBirth }}</span>
                        </div>
                        <div class="iprs-meta-chip">
                          <v-icon x-small color="#64748b" class="tw-mr-1">mdi-card-account-details-outline</v-icon>
                          <span>ID {{ form.idNo }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Editable contact fields -->
                    <div class="section-divider">Contact Details</div>
                    <div class="form-row-2 tw-mb-3">
                      <div class="input-wrap">
                        <label class="input-lbl">Phone Number <span class="req">*</span></label>
                        <v-text-field v-model="form.phone" placeholder="e.g. 0712 345 678" outlined dense hide-details class="sale-input" maxlength="10"
                          @focus="touched.phone = false"
                          @blur="form.phone = normalizePhone(form.phone); touched.phone = true">
                          <template v-slot:prepend-inner><v-icon small color="#94a3b8">mdi-phone-outline</v-icon></template>
                        </v-text-field>
                        <span v-if="touched.phone && form.phone && !validatePhone(form.phone)" class="tw-text-red-500 tw-text-[10px] tw-mt-1">Invalid format — use 07XXXXXXXX or 01XXXXXXXX</span>
                        <span v-if="touched.phone && form.phone && form.phone.replace(/\s+/g,'') === agentPhone" class="tw-text-red-500 tw-text-[10px] tw-mt-1">Customer phone cannot be the same as the agent's phone number</span>
                      </div>
                      <div class="input-wrap">
                        <label class="input-lbl">Email Address <span class="req">*</span></label>
                        <v-text-field v-model="form.email" placeholder="email@example.com" outlined dense hide-details class="sale-input" maxlength="150"
                          @focus="touched.email = false"
                          @blur="form.email = sanitizeEmail(form.email); touched.email = true">
                          <template v-slot:prepend-inner><v-icon small color="#94a3b8">mdi-email-outline</v-icon></template>
                        </v-text-field>
                        <span v-if="touched.email && form.email && !validateEmail(form.email)" class="tw-text-red-500 tw-text-[10px] tw-mt-1">Invalid email address</span>
                        <span v-if="touched.email && form.email && form.email.toLowerCase().trim() === agentEmail" class="tw-text-red-500 tw-text-[10px] tw-mt-1">Customer email cannot be the same as the agent's email address</span>
                      </div>
                    </div>

                    <div class="form-row-2">
                      <div class="input-wrap">
                        <label class="input-lbl">KRA PIN <span class="req">*</span></label>
                        <v-text-field
                          v-model="form.kraPin"
                          placeholder="e.g. A012345678Z"
                          outlined dense hide-details
                          class="sale-input tw-uppercase"
                          maxlength="11"
                          :readonly="kraVerified"
                          :append-icon="kraVerified ? 'mdi-lock-outline' : ''"
                          @blur="!kraVerified && (form.kraPin = sanitizeKraPin(form.kraPin))"
                        ></v-text-field>
                        <span v-if="form.kraPin && !validateKraPin(form.kraPin)" class="tw-text-red-500 tw-text-[10px] tw-mt-1">Invalid KRA PIN format</span>
                      </div>
                      <div class="input-wrap tw-mb-5">
                        <label class="input-lbl">Gender <span class="req">*</span></label>
                        <div class="gender-toggle" :class="{ 'tw-opacity-60 tw-pointer-events-none': idVerified }">
                          <label :class="['gender-option', form.gender==='M'?'gender-active':'']">
                            <input type="radio" value="M" v-model="form.gender" class="tw-hidden" :disabled="idVerified" />
                            <v-icon small :color="form.gender==='M'?'#00AEEF':'#94a3b8'" class="tw-mr-1">mdi-gender-male</v-icon> Male
                          </label>
                          <label :class="['gender-option', form.gender==='F'?'gender-active':'']">
                            <input type="radio" value="F" v-model="form.gender" class="tw-hidden" :disabled="idVerified" />
                            <v-icon small :color="form.gender==='F'?'#E91E8C':'#94a3b8'" class="tw-mr-1">mdi-gender-female</v-icon> Female
                          </label>
                        </div>
                        <span v-if="idVerified" class="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-flex tw-items-center tw-gap-1">
                          <v-icon x-small color="#94a3b8">mdi-lock-outline</v-icon>
                        </span>
                      </div>
                    </div>

                    <!-- Mandatory Document Uploads -->
                    <div class="tw-mb-5">
                      <div class="section-divider">Required Documents</div>
                      <p class="tray-desc">
                        Please upload a copy of the <strong>Vehicle Logbook</strong>.
                        <span v-if="iprsUnavailable || !idVerified || !kraVerified" class="tw-text-amber-600 tw-block tw-mt-1">
                          ⚠️ Verification services unavailable — additional documents (KRA PIN, National ID) are also required.
                        </span>
                      </p>

                      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                        <!-- Logbook - Always shown and mandatory -->
                        <upload-zone
                          label="Vehicle Logbook"
                          :required="true"
                          :upload-obj="uploads.logbook"
                          @file-selected="(f) => handleFileSelect(f, 'logbook')"
                          @remove="clearUpload('logbook')"
                        />

                        <!-- KRA & National ID - Only shown when verifications fail -->
                        <template v-if="iprsUnavailable || !idVerified || !kraVerified">
                          <upload-zone
                            label="KRA PIN Certificate"
                            :required="true"
                            :upload-obj="uploads.kra"
                            @file-selected="(f) => handleFileSelect(f, 'kra')"
                            @remove="clearUpload('kra')"
                          />
                          <upload-zone
                            label="National ID"
                            :required="true"
                            :upload-obj="uploads.nationalId"
                            @file-selected="(f) => handleFileSelect(f, 'nationalId')"
                            @remove="clearUpload('nationalId')"
                          />
                        </template>
                      </div>
                    </div>

                    <!-- Agent self-use warning -->
                    <transition name="fade-up">
                      <div v-if="agentSelfUseDetected" class="alert-box alert-danger tw-mb-4">
                        <div class="alert-icon-wrap alert-icon-danger">
                          <v-icon size="18" color="#EF4444">mdi-shield-alert-outline</v-icon>
                        </div>
                        <div>
                          <p class="alert-title">Agent Details Detected</p>
                          <p class="alert-body">
                            The phone number or email entered matches your agent account.
                            Customer contact details must belong to the insured, not the agent.
                          </p>
                        </div>
                      </div>
                    </transition>

                    <button class="btn-primary tw-w-full" @click="confirmCustomer" :disabled="agentSelfUseDetected || !customerFormValid || tray2Busy">
                      <v-progress-circular v-if="tray2Busy" indeterminate size="14" width="2" color="white" class="tw-mr-2"></v-progress-circular>
                      <v-icon v-else small color="white" class="tw-mr-1">mdi-check</v-icon>
                      {{ tray2BusyLabel }}
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- ════════════════════════════════════════
               TRAY 3 · COVER SCHEDULE & FINANCIER
          ════════════════════════════════════════ -->
          <div v-if="!isTpo" :class="['tray-card', activeTray===3?'tray-open':'', completedTrays.includes(3)?'tray-done':'', !completedTrays.includes(2)&&activeTray!==3?'tray-locked':'']">
            <div class="tray-head" @click="goToTray(3)">
              <div class="tray-head-left">
                <div class="tray-num" :class="{ done: completedTrays.includes(3), locked: !completedTrays.includes(2)&&activeTray!==3 }">
                  <v-icon v-if="completedTrays.includes(3)" x-small color="white">mdi-check</v-icon>
                  <span v-else>3</span>
                </div>
                <div>
                  <p class="tray-title">Cover Schedule &amp; Financier</p>
                  <p class="tray-sub" v-if="completedTrays.includes(3)">{{ financierSummaryLabel }}</p>
                  <p class="tray-sub" v-else>Interested party</p>
                </div>
              </div>
              <v-icon size="18" :color="completedTrays.includes(3)?'#10b981':'#94a3b8'">
                {{ completedTrays.includes(3) ? 'mdi-check-circle' : activeTray===3 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>

            <div class="tray-body" v-show="activeTray===3">
              <p class="tray-desc">
                Select the bank or microfinance institution that holds a financial interest in this vehicle, if any.
                No additional documentation is required at this stage.
              </p>

              <!-- ── Type toggle ── -->
              <div class="fin-type-toggle tw-mb-5">
                <label
                  v-for="opt in financierTypeOptions"
                  :key="opt.value"
                  :class="['fin-type-opt', form.financierType===opt.value ? 'fin-type-active' : '']"
                  @click="onFinancierTypeChange(opt.value)"
                >
                  <div class="fin-type-icon">
                    <v-icon size="16" :color="form.financierType===opt.value ? '#00AEEF' : '#94a3b8'">{{ opt.icon }}</v-icon>
                  </div>
                  <div>
                    <p>{{ opt.label }}</p>
                    <span>{{ opt.sub }}</span>
                  </div>
                </label>
              </div>

              <!-- ── Financier fields (shown when 'financier' selected) ── -->
              <transition name="fade-up">
                <div v-if="form.financierType === 'financier'" class="tw-mb-5">

                  <!-- Institution search -->
                  <div class="tw-mb-4">
                    <label class="input-lbl">Financial Institution <span class="req">*</span></label>

                    <!-- Selected chip -->
                    <div v-if="form.financierInstitution" class="fin-selected-chip tw-mb-2">
                      <div class="fin-chip-avatar">{{ financierInitials }}</div>
                      <div class="fin-chip-body">
                        <p>{{ form.financierInstitution }}</p>
                        <span>{{ financierSubType || 'Interested Party' }}</span>
                      </div>
                      <button class="fin-chip-clear" @click="clearFinancier">
                        <v-icon x-small color="#94a3b8">mdi-close</v-icon> clear
                      </button>
                    </div>

                    <v-autocomplete
                      v-else
                      v-model="form.financierInstitution"
                      :items="filteredFinancierList"
                      item-text="name"
                      item-value="name"
                      placeholder="Type to search…"
                      outlined dense hide-details
                      class="sale-input"
                      @change="onInstitutionPicked"
                    >
                      <template v-slot:item="{ item }">
                        <div class="fin-dd-item">
                          <span>{{ item.name }}</span>
                          <span class="fin-dd-badge">{{ item.type }}</span>
                        </div>
                      </template>
                    </v-autocomplete>
                  </div>

                  <!-- Interest type grid (Commented out per user request)
                  <div class="tw-mb-4">
                    <label class="input-lbl">Interest Type <span class="req">*</span></label>
                    <div class="interest-type-grid">
                      <label
                        v-for="opt in interestTypeOptions"
                        :key="opt.value"
                        :class="['interest-type-opt', form.interestType===opt.value ? 'interest-type-active' : '']"
                        @click="form.interestType = opt.value"
                      >
                        <p>{{ opt.label }}</p>
                        <span>{{ opt.sub }}</span>
                      </label>
                    </div>
                  </div>
                  -->

                  <!-- Finance reference (optional) (Commented out per user request)
                  <div>
                    <label class="input-lbl">
                      Finance Reference No.
                      <span class="optional">(optional)</span>
                    </label>
                    <v-text-field
                      v-model="form.financierRef"
                      placeholder="e.g. EBK/HP/2024/001234"
                      outlined dense hide-details
                      class="sale-input"
                      maxlength="40"
                      @blur="form.financierRef = (form.financierRef || '').toUpperCase().trim()"
                    ></v-text-field>
                  </div>
                  -->

                  <!-- Summary strip -->
                  <transition name="fade-up">
                    <div v-if="form.financierInstitution" class="fin-summary-strip tw-mt-4">
                      <div class="fin-sum-row">
                        <span class="fin-sum-label">Institution</span>
                        <span class="fin-sum-val">{{ form.financierInstitution }}</span>
                      </div>
                      <div class="fin-sum-row">
                        <span class="fin-sum-label">Type</span>
                        <span class="fin-sum-val">{{ financierSubType || 'Interested Party' }}</span>
                      </div>
                      <!--
                      <div class="fin-sum-row">
                        <span class="fin-sum-label">Interest</span>
                        <span class="fin-sum-val">{{ interestTypeLabel }}</span>
                      </div>
                      <div class="fin-sum-row" v-if="form.financierRef">
                        <span class="fin-sum-label">Reference</span>
                        <span class="fin-sum-val tw-font-mono">{{ form.financierRef }}</span>
                      </div>
                      -->
                    </div>
                  </transition>

                </div>
              </transition>

               <button class="btn-primary tw-w-full" @click="confirmSchedule" :disabled="!scheduleFormValid || updatingQuote">
                <v-progress-circular v-if="updatingQuote" indeterminate size="14" width="2" color="white" class="tw-mr-2"></v-progress-circular>
                <v-icon v-else small color="white" class="tw-mr-1">mdi-check</v-icon>
                {{ updatingQuote ? 'Saving…' : 'Save Cover Schedule' }}
              </button>
            </div>
          </div>

          <!-- ════════════════════════════════════════
               TRAY 4 · DECLARATIONS & CONSENT
          ════════════════════════════════════════ -->
          <div :class="['tray-card', activeTray===4?'tray-open':'', completedTrays.includes(4)?'tray-done':'', !completedTrays.includes(3)&&activeTray!==4?'tray-locked':'']">
            <div class="tray-head" @click="goToTray(4)">
              <div class="tray-head-left">
                <div class="tray-num" :class="{ done: completedTrays.includes(4), locked: !completedTrays.includes(3)&&activeTray!==4 }">
                  <v-icon v-if="completedTrays.includes(4)" x-small color="white">mdi-check</v-icon>
                  <span v-else>4</span>
                </div>
                <div>
                  <p class="tray-title">Declaration &amp; Customer Consent</p>
                  <p class="tray-sub">Agent declarations &amp; consent link</p>
                </div>
              </div>
              <v-icon size="18" :color="completedTrays.includes(4)?'#10b981':'#94a3b8'">
                {{ completedTrays.includes(4) ? 'mdi-check-circle' : activeTray===4 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>

            <div class="tray-body" v-show="activeTray===4">
              <div class="declaration-box">
                <p class="declaration-title">
                  <v-icon small color="#F59E0B" class="tw-mr-2">mdi-shield-account-outline</v-icon>
                  Agent Declarations
                </p>
                <div class="declaration-checks">
                  <label v-for="(text, key) in declarationTexts" :key="key" :class="['dec-row', form[key]?'dec-checked':'']">
                    <div class="dec-checkbox" @click="form[key] = !form[key]">
                      <v-icon v-if="form[key]" x-small color="white">mdi-check</v-icon>
                    </div>
                    <span>{{ text }}</span>
                  </label>
                </div>
              </div>

              <button class="btn-primary tw-w-full tw-mt-4" @click="completeTray(4)" :disabled="!allDeclarationsChecked">
                <v-icon small color="white" class="tw-mr-1">mdi-arrow-right</v-icon>
                Proceed to Payment
              </button>
            </div>
          </div>

          <!-- ════════════════════════════════════════
               TRAY 5 · PAYMENT & FINALIZATION
          ════════════════════════════════════════ -->
          <div :class="['tray-card', activeTray===5?'tray-open':'', completedTrays.includes(5)?'tray-done':'', !completedTrays.includes(4)&&activeTray!==5?'tray-locked':'']">
            <div class="tray-head" @click="goToTray(5)">
              <div class="tray-head-left">
                <div class="tray-num" :class="{ done: completedTrays.includes(5), locked: !completedTrays.includes(4)&&activeTray!==5 }">
                  <v-icon v-if="completedTrays.includes(5)" x-small color="white">mdi-check</v-icon>
                  <span v-else>5</span>
                </div>
                <div>
                  <p class="tray-title">Payment &amp; Finalization</p>
                  <p class="tray-sub">M-Pesa STK push or Paybill</p>
                </div>
              </div>
              <v-icon size="18" :color="completedTrays.includes(5)?'#10b981':'#94a3b8'">
                {{ completedTrays.includes(5) ? 'mdi-check-circle' : activeTray===5 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>

            <div class="tray-body" v-show="activeTray===5">

              <!-- ── COVER SUMMARY CARD ── -->
              <div class="pay-summary-card tw-mb-5">
                <div class="pay-summary-head">
                  <div class="pay-summary-head-icon">
                    <v-icon size="16" color="#00AEEF">mdi-shield-check</v-icon>
                  </div>
                  <span>Policy Summary</span>
                  <a href="#" class="pay-summary-edit" @click.prevent="$router.push({ name: 'AgentUnderwriting' })">
                    <v-icon x-small color="#00AEEF">mdi-pencil-outline</v-icon> Edit Quote
                  </a>
                </div>

                <div class="pay-summary-section">
                  <div class="pay-sum-row">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-blue">
                        <v-icon size="14" color="#00AEEF">mdi-car-shield</v-icon>
                      </div>
                      <span class="pay-sum-label">Cover Type</span>
                    </div>
                    <span class="pay-sum-value tw-font-bold tw-text-[#1E4276]">
                      {{ coverTypeName }}
                    </span>
                  </div>

                  <div class="pay-sum-row">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-blue">
                        <v-icon size="14" color="#00AEEF">mdi-calendar-range</v-icon>
                      </div>
                      <span class="pay-sum-label">Cover Period</span>
                    </div>
                    <span class="pay-sum-value">
                      {{ form.startDate || '—' }} → {{ nextYearDate || '—' }}
                    </span>
                  </div>
                </div>

                <div class="pay-summary-section pay-summary-section-alt">
                  <p class="pay-sec-label">
                    <v-icon x-small color="#94a3b8" class="tw-mr-1">mdi-car-outline</v-icon> Vehicle
                  </p>
                  <div class="pay-sum-row">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-navy">
                        <v-icon size="14" color="#1E4276">mdi-pound</v-icon>
                      </div>
                      <span class="pay-sum-label">Registration</span>
                    </div>
                    <span class="pay-sum-value tw-font-mono tw-font-bold tw-text-[#1E4276] tw-tracking-wider">
                      {{ form.regNo || '—' }}
                    </span>
                  </div>
                  <div class="pay-sum-row" v-if="form.make || form.model">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-navy">
                        <v-icon size="14" color="#1E4276">mdi-car-info</v-icon>
                      </div>
                      <span class="pay-sum-label">Vehicle</span>
                    </div>
                    <span class="pay-sum-value">{{ form.make }} {{ form.model }} {{ form.yom ? '('+form.yom+')' : '' }}</span>
                  </div>
                </div>

                <div class="pay-summary-section">
                  <p class="pay-sec-label">
                    <v-icon x-small color="#94a3b8" class="tw-mr-1">mdi-account-outline</v-icon> Customer
                  </p>
                  <div class="pay-sum-row">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-green">
                        <v-icon size="14" color="#10b981">mdi-account-check</v-icon>
                      </div>
                      <span class="pay-sum-label">Full Name</span>
                    </div>
                    <span class="pay-sum-value tw-font-bold">{{ form.fullName || '—' }}</span>
                  </div>
                  <div class="pay-sum-row">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-green">
                        <v-icon size="14" color="#10b981">mdi-card-account-details-outline</v-icon>
                      </div>
                      <span class="pay-sum-label">ID / KRA</span>
                    </div>
                    <span class="pay-sum-value">{{ form.idNo || '—' }} · {{ form.kraPin || '—' }}</span>
                  </div>
                  <div class="pay-sum-row">
                    <div class="pay-sum-icon-cell">
                      <div class="pay-sum-icon-wrap pay-sum-icon-green">
                        <v-icon size="14" color="#10b981">mdi-email-outline</v-icon>
                      </div>
                      <span class="pay-sum-label">Email</span>
                    </div>
                    <span class="pay-sum-value tw-truncate" style="max-width:220px">{{ form.email || '—' }}</span>
                  </div>
                </div>

                <div class="pay-summary-section pay-summary-section-alt" v-if="coverBenefits.length > 0">
                  <p class="pay-sec-label">
                    <v-icon x-small color="#94a3b8" class="tw-mr-1">mdi-star-circle-outline</v-icon>
                    Cover Benefits
                    <span class="pay-sec-count tw-ml-1">({{ coverBenefits.length }})</span>
                  </p>
                  <div class="pay-benefits-chips">
                    <div
                      v-for="b in coverBenefits" :key="b.id || b.label"
                      :class="['pay-benefit-chip', b.paid ? 'pay-benefit-chip-paid' : '']"
                    >
                      <v-icon x-small :color="b.paid ? '#10b981' : '#00AEEF'" class="tw-mr-1">
                        {{ b.paid ? 'mdi-plus-circle-outline' : 'mdi-check-circle-outline' }}
                      </v-icon>
                      <span>{{ b.label }}</span>
                      <span v-if="b.paid && b.premium" class="pay-benefit-premium">
                        +{{ fmtCurrency(b.premium) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="pay-premium-footer">
                  <div class="pay-prem-row">
                    <span>Annual Premium</span>
                    <span class="tw-font-bold tw-text-[#1E4276]">{{ formattedPremium }}</span>
                  </div>
                  <div class="pay-prem-total">
                    <span>Amount Due Now</span>
                    <span class="pay-prem-total-val">{{ formattedAmountDueNow }}</span>
                  </div>
                </div>
              </div>

              <!-- ── PAYMENT PLAN (Comprehensive only) ── -->
              <div v-if="isComprehensive" class="tw-mb-6">
                <p class="section-sub-label">Select Payment Plan</p>
                <div class="plan-grid-v2">
                  <label
                    v-for="plan in paymentPlans" :key="plan.value"
                    :class="['plan-card-v2', form.paymentPlan===plan.value?'plan-v2-active':'', plan.disabled?'plan-v2-disabled':'']"
                  >
                    <input type="radio" v-model="form.paymentPlan" :value="plan.value" class="tw-hidden" :disabled="plan.disabled" />
                    <div class="plan-v2-icon-wrap" :class="form.paymentPlan===plan.value?'plan-v2-icon-active':''">
                      <v-icon size="20" :color="form.paymentPlan===plan.value?'#00AEEF':'#94a3b8'">{{ plan.icon }}</v-icon>
                    </div>
                    <div class="plan-v2-body">
                      <p class="plan-v2-name">{{ plan.label }}</p>
                      <p class="plan-v2-amount">{{ plan.amountLine }}</p>
                      <!-- Installment breakdown chips -->
                      <div v-if="plan.value === 'installments' && form.paymentPlan === 'installments'" class="installment-breakdown tw-mt-2">
                        <div class="inst-chip inst-chip-now">
                          <span class="inst-chip-pct">40%</span>
                          <span class="inst-chip-label">Now</span>
                          <span class="inst-chip-amount">{{ fmtCurrency(installmentFirst) }}</span>
                        </div>
                        <div class="inst-chip-connector">→</div>
                        <div class="inst-chip inst-chip-later">
                          <span class="inst-chip-pct">30%</span>
                          <span class="inst-chip-label">Month 2</span>
                          <span class="inst-chip-amount">{{ fmtCurrency(installmentLater) }}</span>
                        </div>
                        <div class="inst-chip-connector">→</div>
                        <div class="inst-chip inst-chip-later">
                          <span class="inst-chip-pct">30%</span>
                          <span class="inst-chip-label">Month 3</span>
                          <span class="inst-chip-amount">{{ fmtCurrency(installmentLater) }}</span>
                        </div>
                      </div>
                      <p v-if="plan.value === 'installments'" class="tw-text-xs tw-text-[#94a3b8] tw-mt-2">Balance to be cleared within 2 months.</p>
                    </div>
                    <div v-if="form.paymentPlan===plan.value" class="plan-v2-check">
                      <v-icon x-small color="white">mdi-check</v-icon>
                    </div>
                  </label>
                </div>
              </div>

              <!-- ── PAYMENT METHOD TABS ── -->
              <p class="section-sub-label tw-mb-3">Payment Method</p>
              <div class="pay-method-tabs tw-mb-4">
                <button
                  :class="['pay-method-tab', activePayTab==='mpesa'?'pay-tab-active':'']"
                  @click="activePayTab='mpesa'"
                >
                  <img src="/mpesa.jpeg" alt="M-Pesa" class="pay-tab-mpesa-logo" />
                  <span>M-Pesa STK</span>
                </button>
                <button
                  :class="['pay-method-tab', activePayTab==='paybill'?'pay-tab-active':'']"
                  @click="activePayTab='paybill'"
                >
                  <div class="pay-tab-icon-wrap">
                    <v-icon size="18" :color="activePayTab==='paybill'?'#1E4276':'#94a3b8'">mdi-bank-transfer</v-icon>
                  </div>
                  <span>Paybill</span>
                </button>
              </div>

              <!-- ── M-PESA STK PANEL ── -->
              <transition name="fade-up">
                <div v-if="activePayTab==='mpesa'" class="pay-panel">
                  <div class="mpesa-panel-card tw-mb-4">
                    <div class="mpesa-panel-header">
                      <img src="/mpesa.jpeg" alt="M-Pesa" class="mpesa-panel-logo" />
                      <div>
                        <p class="mpesa-panel-title">Lipa Na M-Pesa</p>
                        <p class="mpesa-panel-sub">STK Push — you'll receive a prompt on your phone</p>
                      </div>
                    </div>
                    <div class="input-wrap tw-mt-4">
                      <label class="input-lbl">M-Pesa Phone Number <span class="req">*</span></label>
                      <v-text-field
                        v-model="form.mpesaPhone"
                        placeholder="e.g. 0712 345 678"
                        outlined dense hide-details
                        class="sale-input"
                        @blur="form.mpesaPhone = normalizePhone(form.mpesaPhone)"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small color="#94a3b8">mdi-cellphone</v-icon>
                        </template>
                      </v-text-field>
                    </div>
                    </div>
                  <button class="btn-pay tw-w-full" @click="processPayment" :disabled="!form.mpesaPhone || fetching.payment">
                    <v-progress-circular v-if="fetching.payment" indeterminate size="16" width="2" color="white" class="tw-mr-2"></v-progress-circular>
                    <template v-else>
                      <v-icon small color="white" class="tw-mr-2">mdi-cellphone-wireless</v-icon>
                    </template>
                    {{ fetching.payment ? 'Waiting for M-Pesa…' : 'Send STK Push · ' + formattedAmountDueNow }}
                  </button>
                  <p class="pay-stk-hint">
                    <v-icon x-small color="#94a3b8" class="tw-mr-1">mdi-information-outline</v-icon>
                    A payment prompt will appear on&nbsp;<strong>{{ form.mpesaPhone || 'your phone' }}</strong>. Enter your M-Pesa PIN to confirm.
                  </p>
                </div>
              </transition>

              <!-- ── PAYBILL PANEL ── -->
              <transition name="fade-up">
                <div v-if="activePayTab==='paybill'" class="pay-panel">
                  <div v-if="paybillFallback" class="paybill-fallback-alert tw-mb-4">
                    <v-icon size="16" color="#D97706" class="tw-mr-2 tw-flex-shrink-0">mdi-alert-circle-outline</v-icon>
                    <span>STK Push failed or timed out. Complete payment via Paybill below.</span>
                  </div>

                  <div class="paybill-panel-card tw-mb-4">
                    <div class="paybill-panel-header">
                      <div class="paybill-icon-circle">
                        <v-icon size="22" color="white">mdi-bank-transfer</v-icon>
                      </div>
                      <div>
                        <p class="paybill-panel-title">M-Pesa Paybill Payment</p>
                        <p class="paybill-panel-sub">Use any M-Pesa menu → Lipa Na M-Pesa → Pay Bill</p>
                      </div>
                    </div>

                    <!-- <div class="paybill-steps-grid tw-mt-4">
                      <div class="paybill-step-item">
                        <div class="pb-step-num">1</div>
                        <div class="pb-step-body">
                          <p class="pb-step-label">Business Number</p>
                          <p class="pb-step-value">504700</p>
                        </div>
                        <button class="pb-copy-btn" @click="copyText('504700', 'Business number')">
                          <v-icon x-small color="#64748b">mdi-content-copy</v-icon>
                        </button>
                      </div>

                      <div class="paybill-step-item paybill-step-highlight">
                        <div class="pb-step-num pb-step-num-amber">2</div>
                        <div class="pb-step-body">
                          <p class="pb-step-label">Account Reference <span class="pb-unique-badge">Unique</span></p>
                          <p class="pb-step-value tw-font-mono tw-text-[#1E4276] tw-text-base tw-tracking-widest">{{ paymentReference }}</p>
                          <p class="pb-step-hint">This reference auto-allocates your payment to the correct policy.</p>
                        </div>
                        <button class="pb-copy-btn" @click="copyText(paymentReference, 'Reference code')">
                          <v-icon x-small color="#64748b">mdi-content-copy</v-icon>
                        </button>
                      </div>

                      <div class="paybill-step-item">
                        <div class="pb-step-num pb-step-num-green">3</div>
                        <div class="pb-step-body">
                          <p class="pb-step-label">Amount</p>
                          <p class="pb-step-value tw-text-[#00AEEF] tw-text-xl tw-font-extrabold">{{ formattedAmountDueNow }}</p>
                        </div>
                        <button class="pb-copy-btn" @click="copyText(String(amountDueNow), 'Amount')">
                          <v-icon x-small color="#64748b">mdi-content-copy</v-icon>
                        </button>
                      </div>
                    </div> -->

                    <div class="paybill-ussd-steps tw-mt-4">
                      <p class="paybill-ussd-title">
                        <v-icon x-small color="#64748b" class="tw-mr-1">mdi-cellphone-text</v-icon>
                        Quick Steps
                      </p>
                      <div class="paybill-ussd-list">
                        <div v-for="(step, i) in ussdSteps" :key="i" class="paybill-ussd-item">
                          <span class="paybill-ussd-num">{{ i+1 }}</span>
                          <span v-html="step"></span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button class="btn-amber tw-w-full" @click="verifyManualPayment" :disabled="fetching.payment">
                    <v-progress-circular v-if="fetching.payment" indeterminate size="14" width="2" color="#1E4276" class="tw-mr-2"></v-progress-circular>
                    <template v-else>
                      <v-icon small color="#1E4276" class="tw-mr-2">mdi-check-circle-outline</v-icon>
                    </template>
                    {{ fetching.payment ? 'Verifying…' : "I've Paid — Verify &amp; Issue Cover" }}
                  </button>
                  <p class="pay-stk-hint">
                    <v-icon x-small color="#94a3b8" class="tw-mr-1">mdi-information-outline</v-icon>
                    Payment is automatically verified using your registration number <strong>{{ paymentReference }}</strong>.
                  </p>
                </div>
              </transition>

            </div>
          </div>

          <!-- ── SUCCESS ── -->
          <transition name="fade-up">
            <div v-if="activeTray===6" class="success-card">
              <div class="success-icon-wrap"><v-icon size="56" color="white">mdi-check</v-icon></div>
              <h2 class="success-title">Sale Completed!</h2>
              <p class="success-subtitle">Motor cover for <strong>{{ form.regNo }}</strong> has been initiated.</p>

              <div class="success-detail-box">
                <div class="tw-flex tw-justify-between tw-items-center tw-border-b tw-border-dashed tw-border-[#E8EDF2] tw-pb-4 tw-mb-4">
                  <span class="tw-font-bold tw-text-[#1E4276]">{{ isTpo ? 'Third Party Only' : COMPREHENSIVE_LABEL }}</span>
                  <span class="tw-text-gray-400 tw-text-sm">{{ form.startDate }} → {{ nextYearDate }}</span>
                </div>

                <!-- Guidance for Tracking -->
                <div class="tw-bg-blue-50 tw-rounded-lg tw-p-4 tw-mb-5 tw-border tw-border-blue-100">
                  <div class="tw-flex tw-items-start tw-gap-3">
                    <v-icon small color="#00AEEF" class="tw-mt-1">mdi-information-outline</v-icon>
                    <div>
                      <p class="tw-text-xs tw-font-bold tw-text-[#1E4276] tw-mb-1">How to track this policy?</p>
                      <p class="tw-text-[11px] tw-text-[#64748B] tw-m-0">
                        The quote is now in the underwriting queue. You can track progress via the
                        <strong>Notification Bell</strong> in your top bar or the <strong>Recent Conversions</strong> tab on your dashboard.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="tw-flex tw-items-start tw-gap-3 tw-mb-5 tw-text-sm tw-text-gray-500">
                  <v-icon small color="#00AEEF" class="tw-mt-0.5">mdi-email-check</v-icon>
                  <span>Confirmation sent to <strong>{{ form.email }}</strong> and underwriting team.</span>
                </div>

                <!-- Certificate download section -->
                <div class="certificate-download-box tw-mb-5">
                  <div class="tw-flex tw-items-center tw-justify-between">
                    <div class="tw-flex tw-items-center tw-gap-2">
                       <v-icon small color="#059669">mdi-file-certificate</v-icon>
                       <span class="tw-text-xs tw-font-bold tw-text-[#1E4276]">Certificate of Insurance</span>
                    </div>
                    <v-btn
                      v-if="!certificateUrl"
                      depressed
                      small
                      color="primary"
                      :loading="certificateLoading"
                      @click="fetchCertificate"
                      class="tw-text-[10px]"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      Check Certificate
                    </v-btn>
                    <div v-else class="tw-flex tw-gap-2">
                       <v-btn
                         depressed
                         small
                         color="success"
                         :href="certificateUrl"
                         download="Certificate.pdf"
                         class="tw-text-[10px]"
                       >
                         <v-icon left x-small>mdi-download</v-icon>
                         Download
                       </v-btn>
                       <v-btn
                         depressed
                         small
                         outlined
                         color="primary"
                         @click="window.open(certificateUrl, '_blank')"
                         class="tw-text-[10px]"
                       >
                         <v-icon left x-small>mdi-eye</v-icon>
                         Preview
                       </v-btn>
                    </div>
                  </div>
                  <p class="tw-text-[10px] tw-text-gray-400 tw-mt-2 tw-mb-0">
                    <v-icon x-small color="#94a3b8">mdi-information-outline</v-icon>
                    The certificate is generated automatically. If not ready, click "Check" again in a moment.
                  </p>
                </div>

                <!-- Valuation Authority section (if applicable) -->
                <div v-if="!isTpo && form.valuer" class="certificate-download-box tw-mb-6" style="background:#F0FDF4; border-color:#DCFCE7;">
                  <div class="tw-flex tw-items-center tw-justify-between">
                    <div class="tw-flex tw-items-center tw-gap-2">
                       <v-icon small color="#059669">mdi-file-edit-outline</v-icon>
                       <span class="tw-text-xs tw-font-bold tw-text-[#1E4276]">Valuation Authority Letter</span>
                    </div>
                    <v-btn
                      depressed
                      small
                      color="success"
                      class="tw-text-[10px]"
                      @click="valuationPreviewVisible = true"
                    >
                      <v-icon left x-small>mdi-eye</v-icon>
                      Preview Letter
                    </v-btn>
                  </div>
                </div>
              </div>

              <div class="success-actions">
                <button class="btn-primary tw-flex-1" @click="$router.push({ name: 'AgentMotorFinalization', params: { quoteId: quoteId || 'pending' } })">
                  <v-icon small color="white" class="tw-mr-2">mdi-chart-timeline-variant</v-icon> Track Progress
                </button>
                <button class="btn-outline tw-flex-1" @click="$router.push({ name: 'AgentDashboard' })">
                  <v-icon small class="tw-mr-2">mdi-view-dashboard</v-icon> Dashboard
                </button>
              </div>
              <div class="tw-mt-5">
                <v-btn text color="primary" x-small @click="$router.push({ name: 'AgentUnderwriting' })">
                  <v-icon x-small class="tw-mr-1">mdi-plus</v-icon> Start Another Sale
                </v-btn>
              </div>
            </div>
          </transition>

        </div><!-- /trays-wrap -->
      </div>
    </div>
    <!-- Valuation Authority Preview Dialog -->
    <v-dialog v-model="valuationPreviewVisible" max-width="750">
      <v-card class="tw-rounded-xl tw-overflow-hidden">
        <v-card-title class="tw-bg-[#1E4276] tw-text-white tw-py-4 tw-px-6 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center tw-gap-2">
            <v-icon color="white">mdi-file-eye-outline</v-icon>
            <span class="tw-text-lg tw-font-bold">Valuation Authority Letter Preview</span>
          </div>
          <v-btn icon color="white" @click="valuationPreviewVisible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="tw-p-6 tw-bg-white" style="max-height: 70vh; overflow-y: auto;">
          <div v-html="valuationHtml" class="valuation-preview-body"></div>
        </v-card-text>
        <v-card-actions class="tw-p-4 tw-bg-gray-50 tw-border-t">
          <v-spacer></v-spacer>
          <v-btn color="#64748B" text @click="valuationPreviewVisible = false">Close</v-btn>
          <v-btn color="primary" depressed @click="valuationPreviewVisible = false">
            I Understand
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </agent-default>
</template>

<script>
import axios from 'axios';
import { parseError } from '@/utils/errorInterpreter';
import AgentDefault from '@/components/layout/AgentDefault';
import UploadZone from '@/components/agent/UploadZone';
import BenefitLimitSelector from '@/components/agent/BenefitLimitSelector';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { formatDate, reverseFormat, changeFormat } from '@/utils/time';
import { validateNationalId } from '@/utils/paValidation';
import {
  sanitizeReg, sanitizeChassis, sanitizeEngine, sanitizeLogbook, sanitizeAlpha, sanitizeEmail, sanitizeKraPin, normalizePhone, clampInt,
  validateKraPin, validateEmail, validateId, validatePhone,
  validateChassis, validateEngineNo, validateLogbook, validateColor, validateEngineSize, validateSeats,
} from '@/utils/sanitize';
import {
  purgeLegacyKeys, loadPendingQuote, clearPendingQuote, savePendingQuote,
  loadDirectJourney, saveDirectJourney, loadSaleProgress, saveSaleProgress,
  loadAgentId, saveAgentId, clearDirectJourney, clearSaleProgress,
  clearAllSaleData,
} from '@/utils/saleCache';
import motorPaybillApi from '@/services/motorPaybillApi';
import instalmentAmount from '@/utils/instalments';
import { invalidateAfterPayment } from '@/utils/portalBootstrap';
import { fetchNtsaVehicle, NTSA_OUTCOME } from '@/utils/motorVehicleVerify';

const { Loader } = require('google-maps');

const QUOTE_CREATE_URL = '/customer/quotation/create';
const EMAIL_API_URL = '/customer/digital-notification/client-api/v1/emails';
const SHOW_ERROR = 'show-error';

// Map upload keys → human-readable document names
const DOC_NAMES = {
  logbook: 'Log Book',
  kra: 'KRA PIN',
  nationalId: 'National ID',
};

const AUTH_USER_GETTER = 'auth/authenticatedUser';

const CONTACT_HQ = 'Contact head office';
const NAIROBI_CBD = 'Nairobi CBD';
const VALUED_CUSTOMER = 'Valued Customer';
const NGONG_ROAD = 'Ngong Road';
const TPO_LABEL = 'Third Party Only (TPO)';
const COMPREHENSIVE_LABEL = 'Motor Comprehensive';

export default {
  name: 'AgentSale',
  components: {
    AgentDefault,
    UploadZone,
    BenefitLimitSelector,
  },
  data() {
    return {
      activeTray: 1,
      completedTrays: [],
      quoteData: null,
      emailDestination: 'client',

      /* UI state */
      pollingActive: false,
      pollTimer: null,
      mismatchDetected: false,
      customerHarvestingDetected: false,
      yomDowngradeDetected: false,
      recomputingTpo: false,
      directCarMakes: [],
      directCarModels: [],
      directMakeMismatch: false,
      paybillFallback: false,
      updatingQuote: false,
      activePayTab: 'mpesa',
      touched: {
        phone: false,
        email: false,
      },

      /* Double-insurance / verify error state */
      verifyError: null,
      doubleInsuranceDetails: null,
      doubleInsuranceEarliestStart: null,

      certificateUrl: null,
      certificateLoading: false,

      /* Valuation */
      showAgentTour: false,

      // ── New Sale / Reset ──
      showNewSaleConfirm: false,

      // ── IPRS manual fallback ──
      iprsUnavailable: false, // true when IPRS returns 500
      iprsManualEntry: false, // true when agent opts for manual entry
      manualForm: {
        fullName: '',
        gender: '',
        dateOfBirth: '',
      },
      valuerData: [
        {
          id: 'capital-alliance',
          name: 'Capital Alliance Valuers & Assessors Ltd',
          contact: 'Nancy Njeri',
          phone: '0700298666 / 0731747311',
          email: 'jane@capitalalliance-kenya.com',
          hq: 'Emmak Court, Parklands Rd, Westlands, Nairobi',
          areas: ['Westlands', 'Parklands', 'Nairobi'],
          towns: ['Nairobi', 'Mombasa'],
        },
        {
          id: 'links-valuers',
          name: 'Links Valuers and Assessors Ltd',
          contact: 'Jane Kanyeki',
          phone: '0707798383',
          email: 'jane.kanyeki@linksvaluers.com',
          hq: 'Pamstech House, 3rd Floor, Nairobi CBD',
          branches: [
            { location: 'Upperhill', address: 'Upperhill Springs Hotel, Opp CIC, Mara Road' },
            { location: 'Kitengela', address: 'Milele Centre, 1st Floor, Behind Equity Bank, Nairobi-Namanga Rd' },
            { location: 'Kiambu', address: 'Bishop Magua House, 3rd Floor' },
            { location: 'Thika', address: 'Emko Plaza, Mezzanine Floor, Off Nairobi-Garissa Rd' },
            { location: 'Mombasa', address: 'Galaxy Plaza, Opposite CMC Motors, Off Moi Avenue' },
            { location: 'Nakuru', address: 'Saifee House, 1st Floor, Kenyatta Avenue' },
            { location: 'Meru', address: 'Meru Teachers House, 3rd Floor, Opp Barclays Bank' },
            { location: 'Kisumu', address: 'Harleys House, 2nd Floor, B10 Oginga Odinga Street' },
            { location: 'Nyeri', address: 'Old Batian House, 1st Floor' },
            { location: 'Kisii', address: 'Ouru Center, 1st Floor, Above Family Bank' },
            { location: 'Kitale', address: 'Yes Plaza, 1st Floor, M5, Opposite Equity Bank, Kenyatta Street' },
            { location: 'Eldoret', address: 'Nandi Arcade, Oloo Street, Opposite Finns Garage' },
            { location: 'Kakamega', address: CONTACT_HQ },
          ],
          areas: ['CBD', 'Upperhill', 'Kitengela', 'Uthiru', 'Kangemi', 'Parklands', 'Eastlands', 'Langata', NGONG_ROAD],
          towns: ['Nairobi', 'Mombasa', 'Nakuru', 'Meru', 'Upperhill', 'Kisumu', 'Kitengela', 'Kiambu', 'Eldoret', 'Thika', 'Nyeri', 'Kisii', 'Kitale', 'Kakamega'],
          mobile: true,
        },
        {
          id: 'xenon',
          name: 'Xenon Auto Assessors and Valuers Ltd',
          contact: 'Kennedy Kyalo',
          phone: '0700799054',
          email: 'xenonautoassessors@gmail.com',
          hq: 'Mombasa (mobile nationwide)',
          areas: [NAIROBI_CBD],
          towns: ['Nairobi', 'Nakuru', 'Kisumu', 'Eldoret', 'Mombasa', 'Meru', 'Kitui'],
          mobile: true,
        },
        {
          id: 'speedsta',
          name: 'Speedsta Motors Ltd',
          contact: 'Esther Mwangi',
          phone: '0720200623',
          email: 'speedstamotors@gmail.com',
          hq: 'Nairobi (mobile valuers)',
          areas: ['Ruiru', 'Mlolongo', 'Uthiru', NAIROBI_CBD],
          towns: ['Nairobi', 'Ruiru', 'Mlolongo', 'Uthiru', 'Nakuru', 'Nyeri', 'Mombasa', 'Kitui'],
          mobile: true,
        },
        {
          id: 'regent',
          name: 'Regent Automobile Valuers and Assessors Ltd',
          contact: 'Walter Mugo',
          phone: '0721 471 803 / 0712 636 365',
          email: 'regent.wariia@gmail.com',
          hq: 'TRV Office Plaza, Muthithi Road, Westlands, Nairobi',
          branches: [
            { location: 'Westlands (HQ)', address: 'TRV Office Plaza, Muthithi Rd, 2nd Floor, Wing 2B' },
            { location: 'Buruburu', address: 'Buruburu Complex, Room B3' },
            { location: NGONG_ROAD, address: 'Jameson Court Block B, Office No. 10, 50m from China Center' },
            { location: 'Pangani', address: 'Muthaiga Square, 3rd Floor' },
            { location: 'Upperhill', address: 'Along Ngong Road, Jameson Court' },
            { location: 'Kitengela', address: 'Betty Plaza, 3rd Floor, Rm 108' },
            { location: 'Ruiru', address: CONTACT_HQ },
            { location: 'Mombasa', address: 'Yunis Building, 3rd Floor, Room No. 4' },
            { location: 'Nakuru', address: 'Spikes Centre, Kenyatta Avenue, 4th Floor' },
            { location: 'Eldoret', address: CONTACT_HQ },
            { location: 'Kisumu', address: CONTACT_HQ },
            { location: 'Thika', address: CONTACT_HQ },
            { location: 'Meru', address: '3rd Floor, Room 18' },
            { location: 'Nyeri', address: CONTACT_HQ },
            { location: 'Embu', address: CONTACT_HQ },
            { location: 'Kisii', address: CONTACT_HQ },
            { location: 'Kitui', address: 'KCB Building, 2nd Floor, Left Wing, Rm No. 4' },
          ],
          areas: ['Westlands', 'Buruburu', NGONG_ROAD, 'Pangani', 'Upperhill', 'Kitengela'],
          towns: ['Nairobi', 'Mombasa', 'Nakuru', 'Eldoret', 'Kisumu', 'Thika', 'Meru', 'Nyeri', 'Embu', 'Kisii', 'Ruiru', 'Kitengela', 'Kitui'],
        },
        {
          id: 'safety',
          name: 'Safety Surveyors Ltd',
          contact: 'Agnes',
          phone: '0705678012 / 0722421695',
          email: 'motor@safetysurveyors.com',
          hq: 'Occidental House, Baricho Road, Industrial Area, Nairobi',
          areas: ['Industrial Area', NAIROBI_CBD],
          towns: ['Nairobi'],
        },
        {
          id: 'solvit',
          name: 'Solvit Ltd',
          contact: 'David Gichuki',
          phone: '0769737442 / 0794517975',
          email: 'dgichuki@solvit.co.ke',
          hq: 'Springette Business Park, Block G, Lower Kabete Road, Nairobi',
          areas: ['Lower Kabete', 'Spring Valley', NAIROBI_CBD],
          towns: ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Meru', 'Nyeri', 'Thika', 'Kisii', 'Kitale', 'Kiambu', 'Kitui', 'Machakos', 'Embu', 'Naivasha', 'Bungoma', 'Garissa', 'Nanyuki', 'Narok', 'Nyahururu', 'Kenol'],
          mobile: true,
        },
        {
          id: 'kevo',
          name: 'Kevo Motor Valuers & Assessors Ltd',
          contact: 'Sophia Miriko',
          phone: '0713909118 / 0754781825',
          email: 'kevomotor@gmail.com',
          hq: 'Banana Raini Road, Kiambaa, Kiambu',
          areas: ['Kiambu', 'Kiambaa'],
          towns: ['Kiambu'],
        },
        {
          id: 'topnotch',
          name: 'Top Notch Valuers and Assessors',
          contact: 'Michael Maina',
          phone: '0704861492',
          email: 'info@topnotchvaluers.co.ke',
          hq: 'CPA Centre, Ruaraka, Thika Road, Nairobi',
          areas: ['Ruaraka', 'Thika Road', 'Kasarani'],
          towns: ['Nairobi'],
        },
        {
          id: 'topcover',
          name: 'Top Cover Automobile Valuers and Assessors Ltd',
          contact: 'Robert Githae',
          phone: '0743514503 / 0722940892',
          email: 'info@topcovervaluers.co.ke',
          hq: 'Pangani, Muranga Road, Nairobi',
          areas: ['Pangani', 'Muranga Road', NAIROBI_CBD],
          towns: ['Nairobi'],
        },
      ],
      valuerLocationFilter: '', // user's chosen location
      valuerSearchQuery: '',
      valuerEmailSent: false,
      selectedValuerData: null,
      inspectionCoordinates: { latitude: 0, longitude: 0 },
      inspection: { date: '', time: '', location: '' },
      valuationPreviewVisible: false,

      steps: ['Vehicle', 'Customer', 'Schedule', 'Declarations', 'Payment'],

      financierTypeOptions: [
        {
          value: 'none', label: 'No financier', sub: 'Vehicle fully owned', icon: 'mdi-check-circle-outline',
        },
        {
          value: 'financier', label: 'Bank / MFI', sub: 'Commercial lender', icon: 'mdi-bank-outline',
        },
      ],
      interestTypeOptions: [
        { value: 'hp', label: 'Hire purchase', sub: 'Bank owns until loan is cleared' },
        { value: 'loan', label: 'Loan / mortgage', sub: 'Vehicle is collateral' },
        { value: 'lease', label: 'Leasing', sub: 'Bank is the legal owner' },
        { value: 'other', label: 'Other', sub: 'Specify in notes' },
      ],
      bankList: [],
      mfiList: [],
      interestedParties: [],

      declarationTexts: {
        dec1: 'I confirm all details provided are correct and complete.',
        dec2: 'I confirm the terms and conditions of the policy have been explained to the customer.',
        dec3: 'I confirm compliance with KYC / AML requirements.',
      },

      /* File uploads */
      uploads: {
        logbook: { file: null, loading: false, done: false },
        kra: { file: null, loading: false, done: false },
        nationalId: { file: null, loading: false, done: false },
      },

      form: {
        /* direct journey additions */
        cover_type: 'comprehensive',
        usage_type: 'private',
        value: 0,
        addons_dynamic: [],
        benefit_custom_limits: {},
        logbookTransferred: 'yes', // 'yes' | 'no'

        /* vehicle */
        regNo: '',
        make: '',
        model: '',
        yom: '',
        chassis: '',
        engine: '',
        engineSize: '',
        seatingCapacity: '',
        colour: '',
        logbookNo: '',
        startDate: new Date().toISOString().substr(0, 10),
        /* customer */
        expectedFirstName: '',
        idNo: '',
        fullName: '',
        gender: '',
        dateOfBirth: '',
        citizenship: '',
        occupation: '',
        kraPin: '',
        phone: '',
        email: '',
        /* schedule */
        valuer: '',
        valuationDate: '',
        // schedule — financier (replace old hasFinancier / financier fields)
        financierType: 'none', // 'none' | 'bank' | 'mfi'
        financierInstitution: '',
        financierSubType: '', // 'Bank' | 'MFI' — resolved on pick
        interestType: 'hp', // 'hp' | 'loan' | 'lease' | 'other'
        financierRef: '',
        financierCode: '',
        /* declarations */
        dec1: false,
        dec2: false,
        dec3: false,
        /* payment */
        mpesaPhone: '',
        paymentPlan: 'full',
      },

      fetching: {
        vehicle: false, ntsaSearch: false, customer: false, payment: false,
      },
      ntsaFound: false,
      ntsaUnavailable: false,
      ntsaStatusMessage: '',
      fetched: { vehicle: false, customer: false },
      kraVerified: false,
      idVerified: false,
      logbookVerified: false,
      /* Direct Journey */
      coverTypeOptions: [
        { id: 'comprehensive', name: 'Comprehensive', coverTypeId: 101 },
        { id: 'tpo', name: TPO_LABEL, coverTypeId: 106 },
      ],
      usageOptions: [
        { id: 'private', name: 'Motor Private', usageId: 1 },
        {
          id: 'commercial', name: 'Motor Commercial', usageId: 2, disabled: true,
        },
      ],
      chargeableBenefits: [],
      benefitsLoading: false,
      livePremium: null,
      liveRateApplied: null,
      liveBasicPremium: 0,
      confirmedTotal: null, // Trusted total from Underwriting
      livePremiumLoading: false,
      benefitDetails: [],
      calcTimer: null,
    };
  },

  /* ── Computed ── */
  computed: {
    ussdSteps() {
      return [
        'Go to <strong>M-Pesa</strong> — open the <strong>M-Pesa App / SIM Toolkit</strong>, or dial <strong>*334#</strong>',
        'Select <strong>Lipa Na M-Pesa</strong> → <strong>Pay Bill</strong>',
        'Enter Business No. <strong>504700</strong>',
        `Enter Account No. <strong>${this.paymentReference || '—'}</strong>`,
        `Enter amount <strong>${this.formattedAmountDueNow}</strong>`,
        'Enter your <strong>M-Pesa PIN</strong> and confirm',
        'You will receive an <strong>M-Pesa confirmation SMS</strong> — click <em>"I\'ve Paid"</em> below once received',
      ];
    },
    isDirectJourney() { return this.$route.query.journey === 'direct'; },
    yomOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 2000; y -= 1) {
        years.push(String(y));
      }
      return years;
    },
    filteredFinancierList() {
      return this.interestedParties;
    },
    financierInitials() {
      return (this.form.financierInstitution || '')
        .split(' ').slice(0, 2).map((w) => w[0]).join('')
        .toUpperCase();
    },
    financierSubType() {
      const match = this.interestedParties.find((i) => i.name === this.form.financierInstitution);
      return match ? match.type : '';
    },
    interestTypeLabel() {
      const opt = this.interestTypeOptions.find((o) => o.value === this.form.interestType);
      return opt ? opt.label : '';
    },
    valuationHtml() {
      const valuerName = this.selectedValuerData?.name || this.form.valuer || 'Assigned Valuer';
      const agentUser = this.$store?.getters?.[AUTH_USER_GETTER];
      const agentName = agentUser?.name || 'Agent';
      const agentEmail = agentUser?.email || '—';
      const stampUrl = `${window.location.origin}/images/stamp-pacis.jpeg`;

      return `
        <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px; border: 1px solid #eee; border-radius: 8px;">
          <img src="${window.location.origin}/pacis_logo.png" width="140" style="display:block;margin-bottom:20px;" />
          
          <p>Dear Sir/Madam,</p>
          
          <p><strong>RE: VALUATION</strong></p>
          
          <p><strong><u>POLICY NO. #${this.quoteId || 'PENDING'} REG NO. ${this.form.regNo}</u></strong></p>
          
          <p>We refer to the above vehicle(s) which is/are insured with us. To ensure that the sum insured of the motor vehicle reflects the current market value and that the compensation given in the event of a claim is adequate, we authorize the valuation of this vehicle by <strong>${valuerName}</strong>.</p>
          
          <p>This valuation authority is valid for <strong>14 days</strong> as from <strong>${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.</p>
          
          <p>Upon valuation, Pacis will adjust the values as per the report and send an endorsement indicating the revision. You are required to pay additional premium where necessary.</p>
          
          <p>All claims will be settled based on this value less any deductions as provided for in the policy conditions.</p>
          
          <p>In the absence of a valuation report the pre-accident value (PAV) will apply. In such cases, there is a risk of under insurance where the principle of average is applicable.</p>
          
          <p>Yours Sincerely,</p>
          
          <p><strong>Pacis Digital</strong></p>
          
          <img src="${stampUrl}" width="180" style="display:block; margin-top: 10px; margin-bottom: 20px;" alt="Pacis Stamp" />
          
          <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;" />
          <p style="font-size: 12px; color: #666;">
            <strong>Client Details:</strong><br/>
            Name: ${this.form.fullName || '—'}<br/>
            Phone: ${this.form.phone || '—'}<br/>
            Proposed Valuation Date: ${this.form.valuationDate}
          </p>
          <p style="font-size: 12px; color: #666;">
            <strong>Intermediary:</strong> ${agentName} (${agentEmail})
          </p>
        </div>
      `;
    },
    financierSummaryLabel() {
      if (this.form.financierType === 'none') return 'No financier';
      if (this.form.financierInstitution) {
        return `${this.form.financierInstitution} · ${this.interestTypeLabel}`;
      }
      return this.form.financierType === 'bank' ? 'Bank' : 'MFI';
    },
    scheduleFormValid() {
      if (this.form.financierType === 'none') return true;
      return !!(this.form.financierInstitution);
    },
    formattedValueInput: {
      get() {
        if (!this.form.value) return '';
        return new Intl.NumberFormat('en-KE').format(this.form.value);
      },
      set(val) {
        let raw = String(val).replace(/\D/g, '');
        if (raw.length > 8) raw = raw.slice(0, 8);
        const num = parseInt(raw, 10);
        this.form.value = num > 50000000 ? 50000000 : (num || 0);
      },
    },
    filteredCoverOptions() {
      if (this.isDirectJourney) {
        return this.coverTypeOptions.filter((o) => ['comprehensive', 'tpo'].includes(o.id));
      }
      return this.coverTypeOptions;
    },
    benefitPremiumMap() {
      return this.benefitDetails.reduce((map, b) => {
        const result = { ...map };
        const code = b.sectCode || b.sectionCode;
        if (code) result[code] = b;
        return result;
      }, {});
    },
    isTpo() {
      const ct = this.quoteData?.covertypes?.[0];
      // API puts covertypeId on the covertype object, not on car
      // 106 = TPO, 101/104 = Comprehensive
      if (Number(ct?.covertypeId) === 106 || Number(ct?.coverTypeId) === 106) return true;
      if (Number(ct?.car?.coverTypeId) === 106 || Number(ct?.car?.covertypeId) === 106) return true;

      if (this.yomDowngradeDetected) return true;
      if (this.isDirectJourney && this.form.cover_type === 'tpo') return true;
      return false;
    },
    vehicleAge() {
      const year = parseInt(this.form.yom, 10);
      return !Number.isNaN(year) ? new Date().getFullYear() - year : 0;
    },
    maxValuationDate() {
      if (!this.form.startDate) return null;
      const d = new Date(this.form.startDate);
      d.setDate(d.getDate() + 30);
      return d.toISOString().substr(0, 10);
    },
    allDeclarationsChecked() { return this.form.dec1 && this.form.dec2 && this.form.dec3; },
    totalPremium() {
      // Prioritize the total confirmed during Underwriting
      if (this.confirmedTotal > 0 && !this.isDirectJourney) {
        return this.confirmedTotal;
      }
      return this.manualTotalPremium;
    },
    manualSubtotal() {
      const basic = this.liveBasicPremium || this.livePremium || 0;
      const benefits = this.chargeableBenefits
        .filter((b) => this.form.addons_dynamic.includes(b.sectCode))
        .reduce((sum, b) => sum + this.getBenefitPremium(b), 0);
      return basic + benefits;
    },
    manualTotalPremium() {
      if (!this.manualSubtotal) return 0;
      const sub = this.manualSubtotal;
      const levy = Math.round(sub * 0.002);
      const phf = Math.ceil(sub * 0.0025);
      return Math.ceil(sub + levy + phf + 40);
    },
    formattedPremium() { return this.fmtCurrency(this.totalPremium); },
    liveRateLabel() {
      if (this.isTpo || this.liveRateApplied == null) return null;
      const r = this.liveRateApplied;
      const pct = Number.isInteger(r) ? r : Number(r.toFixed(1));
      return `${pct}% of sum insured`;
    },
    isComprehensive() {
      // Inverse of Native TPO or Downgraded TPO — only true Comprehensive covers show installments
      if (this.yomDowngradeDetected) return false;
      return !!this.quoteData && !this.isTpo;
    },
    amountDueNow() {
      if (!this.isComprehensive || this.form.paymentPlan === 'full') return this.totalPremium;
      if (this.form.paymentPlan === 'installments') return this.installmentFirst;
      if (this.form.paymentPlan === 'ipf') return this.totalPremium * 0.2;
      return this.totalPremium;
    },
    formattedAmountDueNow() { return this.fmtCurrency(this.amountDueNow); },
    installmentFirst() { return instalmentAmount(this.totalPremium, 40); },
    installmentLater() { return instalmentAmount(this.totalPremium, 30); },

    paymentPlans() {
      const p40 = this.installmentFirst;
      const p30 = this.installmentLater;
      const plans = [
        {
          value: 'full',
          icon: 'mdi-shield-check',
          label: 'Full Payment',
          amountLine: `Pay ${this.fmtCurrency(this.totalPremium)} now — best value`,
          disabled: false,
        },
        {
          value: 'installments',
          icon: 'mdi-calendar-month',
          label: 'Installments  (40 : 30 : 30)',
          amountLine: `Pay ${this.fmtCurrency(p40)} now, then ${this.fmtCurrency(p30)} × 2`,
          disabled: false,
        },
        {
          value: 'ipf',
          icon: 'mdi-clock-time-four-outline',
          label: 'IPF (Coming Soon)',
          amountLine: 'External financing — available soon',
          disabled: true,
        },
      ];

      // Only show installments/IPF for comprehensive
      if (!this.isComprehensive) {
        return plans.filter((p) => p.value === 'full');
      }

      return plans;
    },

    paymentReference() {
      return (this.form.regNo || '').replace(/\s+/g, '').toUpperCase();
    },
    coverBenefits() {
      const standard = [
        { id: 'std-1', label: 'Own Damage', paid: false },
        { id: 'std-2', label: 'Third Party Liability', paid: false },
        { id: 'std-3', label: 'Passenger Liability', paid: false },
        { id: 'std-4', label: 'Windscreen', paid: false },
        { id: 'std-5', label: 'Fire & Theft', paid: false },
      ];
      if (this.isTpo) return [];
      const apiPaid = (this.quoteData?.covertypes?.[0]?.additionalBenefits || [])
        .map((b) => ({
          id: b.id,
          label: b.sectionName || b.benefit || 'Extra Benefit',
          paid: true,
          premium: b.premium || 0,
        }));
      return [...standard, ...apiPaid];
    },

    vehicleFormValid() {
      const f = this.form;
      const vehicleFieldsOk = !!(
        f.regNo
        && (this.ntsaFound || (
          validateChassis(f.chassis)
          && validateEngineNo(f.engine)
          && validateEngineSize(f.engineSize)
          && validateSeats(f.seatingCapacity)
          && validateColor(f.colour)
        ))
        && f.logbookNo && f.logbookNo.length <= 10
      );

      // Direct journey specifically requires cover selections
      if (this.isDirectJourney) {
        if (this.directMakeMismatch) return false; // Block proceed on mismatch
        if (!f.cover_type || !f.usage_type) return false;
        if (f.cover_type !== 'tpo' && !f.value) return false;
      }

      // TPO (Native or Downgraded): bypass inspection/valuation, require verify step done
      if (this.isTpo) {
        return vehicleFieldsOk && this.fetched.vehicle;
      }

      // Comprehensive: requires valuation assignment
      const valOk = !!(this.form.valuer && this.form.valuationDate);
      return vehicleFieldsOk && valOk;
    },
    tray1Busy() {
      return this.recomputingTpo || this.updatingQuote;
    },
    tray1BusyLabel() {
      if (this.recomputingTpo) return 'Recalculating TPO…';
      if (this.updatingQuote) return this.isDirectJourney && !this.quoteData ? 'Creating Quote…' : 'Saving…';
      return 'Confirm Vehicle Details';
    },

    customerFormValid() {
      const f = this.form;
      const agentEmailNorm = this.agentEmail.toLowerCase().trim();
      const agentPhoneNorm = this.agentPhone.replace(/\s+/g, '');
      const customerEmailNorm = (f.email || '').toLowerCase().trim();
      const customerPhoneNorm = (f.phone || '').replace(/\s+/g, '');

      if (agentEmailNorm && customerEmailNorm === agentEmailNorm) return false;
      if (agentPhoneNorm && customerPhoneNorm === agentPhoneNorm) return false;

      const baseValid = !!(
        f.fullName
        && validatePhone(f.phone)
        && validateEmail(f.email)
        && f.gender
        && validateKraPin(f.kraPin)
        && !!f.logbookTransferred
      );

      // Logbook is always mandatory
      const hasLogbook = !!this.uploads.logbook.file;

      // KRA and ID only mandatory if verification failed
      let otherDocsOk = true;
      if (this.iprsUnavailable || !this.idVerified || !this.kraVerified) {
        otherDocsOk = !!(this.uploads.kra.file && this.uploads.nationalId.file);
      }

      return baseValid && hasLogbook && otherDocsOk;
    },
    tray2Busy() {
      return this.uploads.kra.loading || this.uploads.nationalId.loading
        || this.uploads.logbook.loading || this.updatingQuote;
    },
    tray2BusyLabel() {
      if (this.uploads.kra.loading || this.uploads.nationalId.loading || this.uploads.logbook.loading) return 'Uploading documents…';
      if (this.updatingQuote) return 'Saving…';
      return 'Confirm Customer Details';
    },

    agentEmail() {
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const email = user?.email || user?.attributes?.email || '';
      return email.toLowerCase().trim();
    },
    agentPhone() {
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const raw = user?.phone_number || user?.mobileNumber || user?.['custom:phone'] || '';
      // Normalize +2547X, 2547X, 07X → 07XXXXXXXXX for comparison
      return raw
        .replace(/\s+/g, '')
        .replace(/^\+254/, '0')
        .replace(/^254/, '0');
    },
    isAgentPhone(phone) {
      return !!(this.agentPhone && phone.replace(/\s+/g, '') === this.agentPhone);
    },
    isAgentEmail(email) {
      return !!(this.agentEmail && email.toLowerCase().trim() === this.agentEmail);
    },
    agentSelfUseDetected() {
      const emailMatch = this.agentEmail
        && (this.form.email || '').toLowerCase().trim() === this.agentEmail;
      const phoneMatch = this.agentPhone
        && (this.form.phone || '').replace(/\s+/g, '') === this.agentPhone;
      return emailMatch || phoneMatch;
    },

    quoteId() { return this.quoteData?.quoteId || null; },
    selectedRiskId() { return this.quoteData?.covertypes?.[0]?.riskId || null; },
    prodId() { return this.quoteData?.covertypes?.[0]?.productCode || null; },
    inspectionDateFormatted() { return this.formatDate(this.inspection.date); },
    // cacheState and directCacheKey removed
    valuerTownOptions() {
      const towns = new Set();
      this.valuerData.forEach((v) => v.towns.forEach((t) => towns.add(t)));
      return [...towns].sort();
    },
    nairobiAreaOptions() {
      return [
        'CBD', 'Westlands', 'Upperhill', 'Pangani', 'Industrial Area',
        'Parklands', 'Thika Road / Ruaraka', NGONG_ROAD, 'Lower Kabete',
        'Eastlands / Buruburu', 'Kitengela', 'Ruiru', 'Mlolongo', 'Uthiru',
        'Kiambu',
      ];
    },
    filteredValuers() {
      if (!this.valuerLocationFilter) return [];
      return this.valuerData.filter((v) => {
        const townMatch = v.towns.includes(this.valuerLocationFilter);
        if (!townMatch) return false;
        if (this.valuerLocationFilter === 'Nairobi' && this.valuerSearchQuery) {
          return v.areas.some((a) => a.toLowerCase().includes(this.valuerSearchQuery.toLowerCase())) || v.mobile;
        }
        return true;
      });
    },

    // Today's date string yyyy-mm-dd (local)
    todayStr() {
      return this.toLocalISO(new Date());
    },

    // Current year e.g. 2026
    currentYear() {
      return new Date().getFullYear();
    },

    // Within the 3-month grace window (Oct, Nov, Dec)
    inGraceWindow() {
      return new Date().getMonth() >= 9; // 0-indexed, so 9 = October
    },

    // Min allowed cover start date — today always
    minDate() {
      return this.todayStr;
    },

    // Max allowed cover start date
    // Always allow booking up to Jan 31st of the NEXT year to support renewals and overlaps
    maxStartDate() {
      const nextYear = this.currentYear + 1;
      return `${nextYear}-01-31`;
    },

    // Cover end date — always start + 1 year - 1 day
    nextYearDate() {
      if (!this.form.startDate) return '';
      const d = new Date(this.form.startDate);
      d.setFullYear(d.getFullYear() + 1);
      d.setDate(d.getDate() - 1);
      return this.toLocalISO(d);
    },

    // Formatted display for double insurance earliest start date (DD/MM/YYYY)
    earliestStartFormatted() {
      if (!this.doubleInsuranceEarliestStart) return '';
      const [yyyy, mm, dd] = this.doubleInsuranceEarliestStart.split('-');
      return `${dd}/${mm}/${yyyy}`;
    },

    // True if policy starts in a FUTURE calendar year (e.g., Jan 1 next year)
    isNextYearStart() {
      if (!this.form.startDate) return false;
      const startYear = parseInt(this.form.startDate.split('-')[0], 10);
      const currentYear = new Date().getFullYear();
      return startYear > currentYear;
    },

    // Current cover type name for display
    coverTypeName() {
      return this.isTpo ? TPO_LABEL : COMPREHENSIVE_LABEL;
    },
  },

  /* ── Watchers ── */
  watch: {
    'form.value': function valueWatcher() { if (this.isDirectJourney) this.debounceCalc(); },
    'form.make': function onMakeChanged(val) {
      if (!this.isDirectJourney || !val) return;
      const makeObj = this.directCarMakes.find((x) => x.makeDetails === val);
      if (makeObj) {
        this.form.model = '';
        this.directCarModels = [];
        this.loadDirectCarModels(makeObj.vmkcode);
      }
      // Fire calc even before verify (regNo no longer required)
      this.debounceCalc();
    },
    'form.model': function onModelChanged(val) {
      if (!this.isDirectJourney || !val) return;
      this.debounceCalc();
    },
    'form.yom': function onYomChanged(val) {
      if (!this.isDirectJourney || !val) return;
      const age = new Date().getFullYear() - parseInt(val, 10);
      if (age > 12) {
        this.form.cover_type = 'tpo';
        this.yomDowngradeDetected = true;
        this.form.addons_dynamic = [];
        this.form.benefit_custom_limits = {};
        this.$notify.info('Vehicle is older than 12 years — cover locked to TPO.');
      } else if (this.yomDowngradeDetected) {
        // Reset the lock if the agent corrects the year
        this.yomDowngradeDetected = false;
        this.form.cover_type = 'comprehensive';
      }
      this.debounceCalc();
    },
    'form.cover_type': function coverWatcher() {
      if (this.isDirectJourney) {
        this.debounceCalc();
        // Persist the cover type change so cache reflects the new selection
        this.persistProgress();
      }
    },
    'form.usage_type': function usageWatcher() { if (this.isDirectJourney) this.debounceCalc(); },
    'form.addons_dynamic': function addonsWatcher() { if (this.isDirectJourney) this.debounceCalc(); },
    isTpo(val) {
      if (val && this.form.paymentPlan !== 'full') {
        this.form.paymentPlan = 'full';
      }
    },
    // ── Map initialization ──
    'fetched.vehicle': function onVehicleFetched(val) {
      if (val && !this.isTpo) {
        this.$nextTick(() => this.initGoogleMaps());
      }
    },
    activeTray(val) {
      if (val === 1 && this.fetched.vehicle && !this.isTpo) {
        this.$nextTick(() => this.initGoogleMaps());
      }
    },
    // ── Clear on logout ──
    '$store.getters["auth/authenticatedUser"]': function onAuthUserChanged(val, oldVal) {
      if (!val && oldVal) {
        const email = oldVal?.email || '';
        clearDirectJourney(email);
      }
    },
    'form.startDate': function onStartDateChanged(val) {
      if (!val) return;
      if (val < this.minDate) {
        this.form.startDate = this.minDate;
      }
      if (val > this.maxStartDate) {
        this.form.startDate = this.maxStartDate;
      }
    },
  },

  /* ── Lifecycle ── */
  mounted() {
    const user = this.$store?.getters?.[AUTH_USER_GETTER];
    const email = user?.email || '';
    purgeLegacyKeys(email);

    if (this.isDirectJourney) {
      if (this.$route.query.reset === 'true') {
        clearAllSaleData(email);
      }
      this.fetchChargeableBenefits();
      this.loadDirectCarMakes();

      const cached = loadDirectJourney(email);
      if (cached) {
        if (cached.form) Object.assign(this.form, cached.form);
        if (cached.quoteData) this.quoteData = cached.quoteData;
        if (cached.fetched) Object.assign(this.fetched, cached.fetched);
        if (cached.activeTray) this.activeTray = cached.activeTray;
        if (cached.completedTrays) this.completedTrays = cached.completedTrays;
        if (cached.livePremium != null) this.livePremium = cached.livePremium;
        if (cached.liveBasicPremium != null) this.liveBasicPremium = cached.liveBasicPremium;
        if (cached.ntsaFound != null) this.ntsaFound = cached.ntsaFound;
        if (cached.yomDowngradeDetected != null) this.yomDowngradeDetected = cached.yomDowngradeDetected;
        if (cached.directMakeMismatch != null) this.directMakeMismatch = cached.directMakeMismatch;
      }
    }
    this.fetchChargeableBenefits();
    this.fetchInterestedParties();

    // Journey A: read from namespaced pending quote key
    const quoteData = loadPendingQuote(email);
    if (quoteData) {
      this.quoteData = quoteData;

      // ── Hydrate live premium from the incoming quote so the header chip ──
      // and payment summary show the correct premium from the start
      const ct = quoteData?.covertypes?.[0];
      const riskPrem = ct?.riskPremDto;

      // 1. Capture the exact total from the hand-off payload
      if (quoteData?.confirmedTotal > 0) {
        this.confirmedTotal = quoteData.confirmedTotal;
      }

      // 2. Hydrate base premium for logic that depends on it (e.g. installments)
      if (riskPrem?.totalPremium) {
        const total = riskPrem.totalPremium;
        const basic = riskPrem.basicPremium;
        if (basic > 0) {
          this.liveBasicPremium = basic;
        } else {
          const benefitSum = (ct.additionalBenefits || []).reduce((s, b) => s + (b.premium || 0), 0);
          this.liveBasicPremium = Math.round((total - benefitSum - 40) / 1.0045);
        }
        this.livePremium = this.liveBasicPremium;
      }

      const rate = ct?.rateApplied;
      if (rate != null && Number(rate) < 100 && !this.isTpo) {
        this.liveRateApplied = Number(rate);
      }

      clearPendingQuote(email); // consumed — don't let it bleed into future sessions

      const { client, covertypes } = quoteData;
      if (client) {
        this.form.idNo = client.idNumber || '';
        this.form.fullName = client.fullName || '';
        this.form.kraPin = client.kraPinNumber || '';
        this.form.phone = client.mobileNumber || '';
        this.form.email = client.email || '';
        this.form.mpesaPhone = client.mobileNumber || '';
      }
      const car = covertypes?.[0]?.car;
      if (car) {
        this.form.regNo = car.registrationNumber || '';
        this.form.make = car.make || '';
        this.form.model = car.model || '';
        this.form.yom = car.yearOfManufacture || '';
        this.form.chassis = car.chassis || car.chassisNumber || '';
        this.form.engine = car.engine || car.engineNumber || '';
        this.form.engineSize = car.engineSize || '';
        this.form.seatingCapacity = car.seatingCapacity || '';
        this.form.colour = car.colour || '';
        this.form.logbookNo = car.logbookNo || '';
        this.form.value = car.valueOfCar || car.value || '';
        if (this.isTpo) this.fetched.vehicle = true;

        const additionalBenefits = covertypes?.[0]?.additionalBenefits || [];
        if (additionalBenefits.length > 0) {
          this.form.addons_dynamic = additionalBenefits.map((b) => String(b.id || b.sectionCode || b.sectCode));
          const customLimits = {};
          additionalBenefits.forEach((b) => {
            if (b.value) customLimits[String(b.id || b.sectionCode || b.sectCode)] = b.value;
          });
          this.form.benefit_custom_limits = customLimits;
        }
      }

      // Restore in-progress sale state scoped to this specific quoteId
      const { quoteId } = quoteData;
      const progress = loadSaleProgress(email, quoteId);
      if (progress) {
        if (progress.form) Object.assign(this.form, progress.form);
        if (progress.fetched) Object.assign(this.fetched, progress.fetched);
        if (progress.activeTray) this.activeTray = progress.activeTray;
        if (progress.completedTrays) this.completedTrays = progress.completedTrays;
        if (progress.ntsaFound != null) this.ntsaFound = progress.ntsaFound;
        if (progress.yomDowngradeDetected != null) this.yomDowngradeDetected = progress.yomDowngradeDetected;
      }
    }
  },
  beforeDestroy() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },

  /* ── Methods ── */
  methods: {
    onLogbookUpload(file, tray) {
      if (file) {
        this.uploads[tray].file = file;
        this.uploads[tray].preview = URL.createObjectURL(file);
        this.form.logbookTransferred = true;
      }
    },

    startNewSale() {
      trackAgentInteraction(this, 'agent_sale_start_new');
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const email = user?.email || '';
      clearAllSaleData(email);

      if (this.isDirectJourney) {
        // Full page reload clears Vue state cleanly
        window.location.href = `${window.location.pathname}?journey=direct`;
      } else {
        this.$router.push({ name: 'AgentUnderwriting' });
      }
    },

    applyManualCustomer() {
      // Copy manual form into main form
      if (this.manualForm.fullName) this.form.fullName = this.manualForm.fullName.toUpperCase().trim();
      if (this.manualForm.gender) this.form.gender = this.manualForm.gender;
      if (this.manualForm.dateOfBirth) this.form.dateOfBirth = this.manualForm.dateOfBirth;
      this.iprsManualEntry = false;
      this.idVerified = false; // mark as not verified — docs will be required
    },
    sanitizeReg,
    sanitizeChassis,
    sanitizeEngine,
    sanitizeLogbook,
    selectValuer(valuer) {
      this.form.valuer = valuer.name;
      this.selectedValuerData = valuer;
      this.valuerEmailSent = false;
    },

    relevantBranch(valuer) {
      if (!valuer.branches) return null;
      const filter = this.valuerSearchQuery || this.valuerLocationFilter;
      return valuer.branches.find((b) => b.location.toLowerCase().includes(filter.toLowerCase())) || (valuer.branches.length > 0 ? valuer.branches[0] : null);
    },

    /*
    async sendValuerEmail() {
      if (!this.selectedValuerData || !this.form.valuationDate) return;
      const valuer = this.selectedValuerData;
      const agentUser = this.$store?.getters?.[AUTH_USER_GETTER];
      const agentEmail = agentUser?.email || '';
      const agentName = agentUser?.name || 'Agent';

      const vehicleStr = `${this.form.make} ${this.form.model} — Reg: ${this.form.regNo}`;
      const subject = `Vehicle Valuation Request — ${vehicleStr}`;
      const body = `
        <p>Dear ${valuer.contact},</p>
        <p>We would like to request a motor vehicle valuation for the following:</p>
        <table style="border-collapse:collapse;font-size:14px;margin:12px 0;">
          <tr><td style="padding:4px 12px 4px 0;color:#64748b;">Vehicle:</td><td style="padding:4px 0;font-weight:600;">${vehicleStr}</td></tr>
          <tr><td style="padding:4px 12px 4px 0;color:#64748b;">Year of Manufacture:</td><td style="padding:4px 0;">${this.form.yom}</td></tr>
          <tr><td style="padding:4px 12px 4px 0;color:#64748b;">Valuation Date:</td><td style="padding:4px 0;font-weight:600;">${this.form.valuationDate}</td></tr>
          <tr><td style="padding:4px 12px 4px 0;color:#64748b;">Client Name:</td><td style="padding:4px 0;">${this.form.fullName || '—'}</td></tr>
          <tr><td style="padding:4px 12px 4px 0;color:#64748b;">Client Phone:</td><td style="padding:4px 0;">${this.form.phone || '—'}</td></tr>
          <tr><td style="padding:4px 12px 4px 0;color:#64748b;">Quote ID:</td><td style="padding:4px 0;">${this.quoteId || '—'}</td></tr>
        </table>
        <p>Please confirm receipt and proceed with scheduling. The agent handling this is <strong>${agentName}</strong> (${agentEmail}).</p>
        <p>Regards,<br/>Pacis Insurance Company Limited</p>
      `;

      const clientBody = `
        <p>Dear ${this.form.fullName || VALUED_CUSTOMER},</p>
        <p>A vehicle valuation has been requested for your <strong>${vehicleStr}</strong> on <strong>${this.form.valuationDate}</strong>.</p>
        <p>The assigned valuer is <strong>${valuer.name}</strong>, who will be in touch to confirm the appointment. Contact: ${valuer.phone}.</p>
        <p>Regards,<br/>Pacis Insurance Company Limited</p>
      `;

      const uwBody = `
        <p>Valuation assigned: <strong>${valuer.name}</strong> for <strong>${vehicleStr}</strong> on <strong>${this.form.valuationDate}</strong>. Quote ID: ${this.quoteId}. Agent: ${agentName}.</p>
      `;

      try {
        const emails = [
          axios.post(EMAIL_API_URL, { to: valuer.email, subject, body }, { _skipErrorRedirect: true }),
        ];
        if (this.form.email) {
          emails.push(axios.post(EMAIL_API_URL, { to: this.form.email, subject: `Your Valuation Appointment — ${this.form.valuationDate}`, body: clientBody }, { _skipErrorRedirect: true }));
        }
        if (agentEmail) {
          emails.push(axios.post(EMAIL_API_URL, { to: agentEmail, subject: `[FYI] Valuation Request — ${vehicleStr}`, body: uwBody }, { _skipErrorRedirect: true }));
        }
        // CC underwriting (add your UW team email here)
        emails.push(axios.post(EMAIL_API_URL, { to: 'underwriting@paciskenya.com', subject: `[Valuation] ${vehicleStr} — ${this.form.valuationDate}`, body: uwBody }, { _skipErrorRedirect: true }));

        await Promise.allSettled(emails);
        this.valuerEmailSent = true;
        this.$notify.success(`Valuation request sent to ${valuer.name}`);
      } catch (err) {
        this.$notify.fromError(err, 'Email dispatch failed');
      }
    },
    */
    persistProgress() {
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const email = user?.email || '';
      const snapshot = {
        form: this.form,
        fetched: this.fetched,
        activeTray: this.activeTray,
        completedTrays: this.completedTrays,
        ntsaFound: this.ntsaFound,
        yomDowngradeDetected: this.yomDowngradeDetected,
      };
      if (this.isDirectJourney) {
        saveDirectJourney(email, {
          ...snapshot,
          quoteData: this.quoteData,
          livePremium: this.livePremium,
          liveBasicPremium: this.liveBasicPremium,
          directMakeMismatch: this.directMakeMismatch,
        });
      } else {
        saveSaleProgress(email, this.quoteData?.quoteId, snapshot);
      }
    },
    onSaleComplete() {
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const email = user?.email || '';
      if (this.isDirectJourney) {
        clearDirectJourney(email);
      } else {
        clearSaleProgress(email, this.quoteData?.quoteId);
      }
    },
    sanitizeAlpha,
    sanitizeEmail,
    sanitizeKraPin,
    normalizePhone,
    clampInt,
    validateKraPin,
    validateEmail,
    validateId,
    validatePhone,
    validateChassis,
    validateEngineNo,
    validateLogbook,
    validateColor,
    validateEngineSize,
    validateSeats,
    viewDocs(conv) {
      if (conv.batchNo) {
        // Mocking EDMS behavior - in prod this would open the EDMS viewer
        this.$notify.info(`Opening documents for ${conv.policyNo}...`);
      }
    },

    async fetchChargeableBenefits() {
      // For Journey A, quoteData might already have cover_type info
      const coverType = this.form.cover_type || (this.isTpo ? 'tpo' : 'comprehensive');
      if (coverType !== 'comprehensive') {
        this.chargeableBenefits = [];
        this.form.addons_dynamic = [];
        return;
      }

      this.benefitsLoading = true;
      try {
        const coverObj = this.coverTypeOptions.find((o) => o.id === this.form.cover_type);
        const coverId = coverObj ? coverObj.coverTypeId : 101;
        const res = await axios.get(`/customer/gis/products/70/covertypes/${coverId}/chargeablebenefits`, { _skipErrorRedirect: true });
        this.chargeableBenefits = res.data.object || [];
      } catch (err) {
        this.$notify.fromError(err, 'Failed to fetch benefits');
      } finally {
        this.benefitsLoading = false;
      }
    },
    async fetchInterestedParties() {
      try {
        const res = await axios.get('/customer/gis/policies/interestedParties', { _skipErrorRedirect: true });
        if (res.data && Array.isArray(res.data.object)) {
          this.interestedParties = res.data.object;
        }
      } catch (err) {
        this.$notify.fromError(err, 'Failed to fetch interested parties');
      }
    },

    async verifyManualPayment() {
      trackAgentInteraction(this, 'agent_sale_verify_manual_payment', {
        reference: this.paymentReference || 'unknown',
        quoteId: this.quoteData?.quoteId,
      });
      this.fetching.payment = true;
      try {
        const quoteId = this.quoteData?.quoteId;
        if (!quoteId) {
          this.$notify.error('Quote not found. Cannot confirm paybill payment.');
          return;
        }
        const res = await motorPaybillApi.confirmMotorPaybillPayment({
          quoteId,
          accountNumber: this.paymentReference,
        });
        if (!res.success) {
          this.$notify.error(res.message || 'Payment not yet received. Please complete the Paybill payment and try again.');
          return;
        }
        try {
          await this.initLocalPolicyForPayment();
        } catch (policyErr) {
          this.$notify.error(policyErr.message || 'Payment confirmed but policy could not be initialized.');
          return;
        }
        this.$notify.success(res.message || 'Payment confirmed. Issuing cover.');
        this.onSaleComplete();
        this.completeTray(5);
        this.sendPaymentSuccessEmails().catch((e) => console.error('Silent failure dispatching payment success emails:', e));
        this.generateConsentLink().catch((e) => console.error('Consent dispatch failed:', e));
      } catch (err) {
        this.$notify.fromError(err, 'Payment Check Failed');
      } finally {
        this.fetching.payment = false;
      }
    },
    copyRef() {
      this.copyText(this.paymentReference, 'Reference code');
    },
    copyText(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.$notify.success(`${label} copied to clipboard.`, 'Copied');
      }).catch(() => {
        this.$notify.info(`${label}: ${text}`);
      });
    },
    formatDate,
    reverseFormat,
    changeFormat,

    goToTray(n) {
      trackAgentInteraction(this, 'agent_sale_open_tray', { tray: n });
      if (this.completedTrays.includes(n) || this.activeTray === n) this.activeTray = n;
    },
    completeTray(n) {
      if (!this.completedTrays.includes(n)) this.completedTrays.push(n);
      this.activeTray = n + 1;
      this.persistProgress();
    },

    /* ── TRAY 1: Vehicle ── */
    async fetchVehicleDetails() {
      trackAgentInteraction(this, 'agent_sale_fetch_vehicle_details', {
        reg_no: this.form.regNo || 'unknown',
      });
      if (!this.form.regNo) { this.$root.$emit(SHOW_ERROR, 'Enter a Registration Number'); return; }
      this.fetching.vehicle = true;
      this.mismatchDetected = false;
      this.fetched.vehicle = false;
      this.verifyError = null;
      this.doubleInsuranceDetails = null;
      this.ntsaFound = false;
      this.ntsaUnavailable = false;
      this.ntsaStatusMessage = '';

      // ── Save user-entered make/model before NTSA may overwrite ──
      const preVerifyMake = (this.form.make || '').toUpperCase().trim();
      const preVerifyModel = (this.form.model || '').toUpperCase().trim();
      this.directMakeMismatch = false;

      try {
        // ── DMVIC internal dates: always today → today+364, independent of user's start date ──
        const today = new Date();
        const dmvicStart = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
        const dmvicEndRaw = new Date(today);
        dmvicEndRaw.setDate(dmvicEndRaw.getDate() + 364);
        const dmvicEnd = `${String(dmvicEndRaw.getDate()).padStart(2, '0')}/${String(dmvicEndRaw.getMonth() + 1).padStart(2, '0')}/${dmvicEndRaw.getFullYear()}`;

        const diRes = await axios.post('/customer/gis/policies/double-insurance', {
          vehicleRegistrationNumber: this.form.regNo,
          policyStartDate: dmvicStart, // always today
          policyEndDate: dmvicEnd, // always today + 364 days
          chassisNumber: this.form.chassis || '',
        }, { timeout: 15000, _skipErrorRedirect: true });

        const { result, message } = diRes.data;

        if (result === 'Vehicle is already insured') {
          const parseField = (key) => {
            const m = (message || '').match(new RegExp(`${key}=([^,)]+)`));
            return m ? m[1].trim() : null;
          };
          const coverEndDate = parseField('CoverEndDate');
          const certNo = parseField('InsuranceCertificateNo');
          const insurerName = parseField('MemberCompanyName');
          const regNo = parseField('RegistrationNumber');
          const chassisNo = parseField('ChassisNumber');

          if (coverEndDate) {
            const [dd, mm, yyyy] = coverEndDate.split('/');
            const expiry = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
            const now = new Date();
            now.setHours(0, 0, 0, 0);

            if (expiry >= now) {
              const expiryYear = expiry.getFullYear();
              const currentYearVal = now.getFullYear();

              if (expiryYear === currentYearVal) {
                // Active cover expires THIS year — allow booking from expiry date + 1 day onwards
                // but only within our maxStartDate window
                const nextDay = new Date(expiry);
                nextDay.setDate(nextDay.getDate() + 1);
                const expiryPlusOneStr = this.toLocalISO(nextDay);

                // Only allow if the agent's chosen start date is on or after expiry + 1 day
                if (this.form.startDate >= expiryPlusOneStr) {
                  // Fine — they're booking a future cover that starts after expiry
                  this.$notify.info('Active cover detected, but the selected start date falls after the expiry. Proceeding with future booking.');
                  // Do NOT block — fall through to NTSA lookup
                } else {
                  // Chosen start date is before or on expiry — block
                  this.verifyError = 'double_insurance';
                  this.doubleInsuranceDetails = {
                    coverEndDate: null, certNo, insurerName, regNo, chassisNo,
                  };
                  // Suggest the day AFTER expiry (kept in background for logic, but hidden from UI)
                  this.doubleInsuranceEarliestStart = expiryPlusOneStr;
                  this.$notify.error('The selected start date overlaps with an existing policy. Please adjust the start date to a future date.');
                  this.fetched.vehicle = true;
                  return;
                }
              } else {
                // Expiry is in a FUTURE year — hard block, this is genuinely overlapping
                this.verifyError = 'double_insurance';
                this.doubleInsuranceDetails = {
                  coverEndDate: null, certNo, insurerName, regNo, chassisNo,
                };
                this.fetched.vehicle = true;
                return;
              }
            } else {
              this.$notify.info('Previous insurance has expired. Proceeding with new cover.');
            }
          } else {
            // No expiry date parseable — block to be safe
            this.verifyError = 'double_insurance';
            this.doubleInsuranceDetails = {
              coverEndDate: null, certNo, insurerName, regNo, chassisNo,
            };
            this.fetched.vehicle = true;
            return;
          }
        }

        if (result === 'Validation error' && message !== 'No Records Found') {
          this.verifyError = 'validation_error';
          this.fetched.vehicle = true;
          this.$root.$emit(SHOW_ERROR, message || 'Verification failed');
          return;
        }

        this.fetching.ntsaSearch = true;
        const ntsaLookup = await this.fetchNtsaVehicleDetails(this.form.regNo);
        this.fetching.ntsaSearch = false;

        if (ntsaLookup.outcome === NTSA_OUTCOME.UNAVAILABLE) {
          this.ntsaFound = false;
          this.ntsaUnavailable = true;
          this.ntsaStatusMessage = ntsaLookup.message || '';
          this.fetched.vehicle = true;
          this.$notify.warning(ntsaLookup.message);
          return;
        }

        // success: false / empty Vehicle — vehicle not found in registry
        if (ntsaLookup.outcome === NTSA_OUTCOME.NOT_FOUND) {
          this.ntsaFound = false;
          this.ntsaUnavailable = false;
          // Don't block — just show the manual banner and let agent fill in fields
          this.fetched.vehicle = true;
          return;
        }

        const ntsaResult = ntsaLookup.data;
        const ntsaData = ntsaResult.Vehicle;
        this.ntsaFound = true;
        this.ntsaUnavailable = false;

        if (ntsaData) {
          this.form.make = (ntsaData.Make || '').toUpperCase();
          this.form.bodyType = (ntsaData.BodyType || '').toUpperCase() || this.form.bodyType || '';
          this.form.model = (ntsaData.Model || '').toUpperCase();
          this.form.yom = ntsaData.RegistrationYear || '';
          this.form.chassis = ntsaData.ChassisNumber || this.form.chassis;
          this.form.engine = ntsaData.EngineNumber || this.form.engine;
          this.form.engineSize = ntsaData.cubicCapacity ? String(ntsaData.cubicCapacity) : this.form.engineSize;
          this.form.seatingCapacity = ntsaData.passengerCapacity ? String(ntsaData.passengerCapacity) : this.form.seatingCapacity;
          this.form.colour = (ntsaData.color || '').toUpperCase() || this.form.colour;

          if (this.quoteData?.covertypes?.length > 0) {
            const qCar = this.quoteData.covertypes[0].car;
            const makeMismatch = this.form.make && !(this.form.make.includes((qCar.make || '').toUpperCase()));
            if (makeMismatch) {
              this.mismatchDetected = true;
            } else {
              const age = this.vehicleAge;
              if (age > 12 && this.isComprehensive) {
                this.yomDowngradeDetected = true;
                this.form.addons_dynamic = [];
                this.form.benefit_custom_limits = {};
                this.triggerTpoRequote();
              }
              // ── Trigger live calc for any direct-adjacent flow ──
              if (this.isDirectJourney) {
                this.$nextTick(() => this.calculateLivePremium());
              }
            }
          } else if (this.isDirectJourney) {
            // ── Compare NTSA make with what agent entered ──
            const ntsaMakeNorm = (ntsaData.Make || '').toUpperCase().trim();

            if (preVerifyMake && ntsaMakeNorm) {
              const makeMatches = ntsaMakeNorm.includes(preVerifyMake)
                || preVerifyMake.includes(ntsaMakeNorm);

              if (!makeMatches) {
                // Mismatch — block and tell agent to reinitiate
                this.directMakeMismatch = true;
                // Restore what agent entered (don't overwrite with NTSA)
                this.form.make = preVerifyMake;
                this.form.model = preVerifyModel;
                this.fetched.vehicle = true;
                return; // stop all further processing
              }
            }

            // ── Match confirmed — keep user-entered make/model, only take locked fields from NTSA ──
            this.form.yom = ntsaData.RegistrationYear || this.form.yom;
            this.form.bodyType = (ntsaData.BodyType || '').toUpperCase() || this.form.bodyType || '';
            this.form.chassis = ntsaData.ChassisNumber || this.form.chassis;
            this.form.engine = ntsaData.EngineNumber || this.form.engine;
            this.form.engineSize = ntsaData.cubicCapacity ? String(ntsaData.cubicCapacity) : this.form.engineSize;
            this.form.seatingCapacity = ntsaData.passengerCapacity ? String(ntsaData.passengerCapacity) : this.form.seatingCapacity;
            this.form.colour = (ntsaData.color || '').toUpperCase() || this.form.colour;

            const age = this.vehicleAge;
            if (age > 12) {
              this.form.cover_type = 'tpo';
              this.yomDowngradeDetected = true;
              this.form.addons_dynamic = [];
              this.form.benefit_custom_limits = {};
            }
            // Auto-trigger live premium — make/model/value are all now set
            this.$nextTick(() => this.calculateLivePremium());
          }
        }
      } catch (err) {
        console.error('Vehicle verification failed', err);
        this.$root.$emit(SHOW_ERROR, parseError(err));
      } finally {
        this.fetching.vehicle = false;
        this.fetching.ntsaSearch = false;
        this.fetched.vehicle = true;
      }
    },

    async fetchNtsaVehicleDetails(regNo) {
      return fetchNtsaVehicle(axios, regNo);
    },

    async loadDirectCarMakes() {
      try {
        const res = await axios.get('/customer/gis/vehicles/make/retrieveAll', { _skipErrorRedirect: true });
        this.directCarMakes = res.data.object || [];
      } catch (e) {
        this.$notify.fromError(e, 'Failed to load car makes');
      }
    },
    async loadDirectCarModels(makeCode) {
      if (!makeCode) return;
      try {
        const res = await axios.get(`/customer/gis/vehicles/${makeCode}/models`, { _skipErrorRedirect: true });
        this.directCarModels = res.data.object || [];
      } catch (e) {
        this.$notify.fromError(e, 'Failed to load car models');
      }
    },

    async getAgentId() {
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const email = user?.email || '';

      const cached = loadAgentId(email);
      if (cached !== null) return cached;

      const agnCode = user?.['custom:agent_code'];
      if (!agnCode && !email) return 0;

      try {
        const baseUrl = '/customer/gis/agent/dashboard/summary';
        const url = agnCode
          ? `${baseUrl}?agn_code=${agnCode}`
          : `${baseUrl}?email=${encodeURIComponent(email)}`;

        const res = await axios.get(url, { validateStatus: () => true, _skipErrorRedirect: true });

        if (res.status === 200 && res.data) {
          // ── Use agent.code — the numeric agent ID ──
          const code = res.data.agent?.code;
          if (code !== null && code !== undefined) {
            const parsed = parseInt(String(code), 10);
            if (!Number.isNaN(parsed)) {
              saveAgentId(email, parsed);
              return parsed;
            }
          }
        }
      } catch (e) {
        console.error('[getAgentId] API call failed', e);
      }

      return 0;
    },

    async resolveAgentIdOrFail() {
      const agentId = await this.getAgentId();

      // Only block if genuinely unresolvable — 0 is valid for DIRECT accounts
      if (agentId === null || agentId === undefined || Number.isNaN(agentId)) {
        this.$notify.error(
          'Your agent profile could not be resolved. Please refresh or log out and back in.',
          'Agent ID Missing',
        );
        throw new Error('Agent ID unresolvable — action blocked');
      }

      return agentId; // 0 is valid for DIRECT accounts
    },

    async triggerTpoRequote() {
      this.recomputingTpo = true;
      let agentId;
      try {
        agentId = await this.resolveAgentIdOrFail();
      } catch (err) {
        this.recomputingTpo = false;
        return; // blocked — error already shown
      }
      try {
        const res = await axios.post(QUOTE_CREATE_URL, {
          origin: 'P',
          agentId,
          carDTOS: [{
            carId: 0,
            coverTypeId: 106,
            evaluationSelected: -1,
            hasInsurance: false,
            make: this.form.make,
            model: this.form.model,
            riskId: 0,
            seatingCapacity: 0,
            valueOfCar: 0,
            bodyType: this.form.bodyType || '',
            valueOfEntertainmentScreen: 0,
            valueOfWindScreen: 0,
            vehicleImages: [],
            yearOfManufacture: this.form.yom,
            registrationNumber: this.form.regNo,
          }],
          prodId: 3437,
          email: '',
          fullName: '',
          kraPinNumber: '',
        }, { _skipErrorRedirect: true });
        if (res.data.data) {
          this.quoteData = res.data.data;
          const user = this.$store?.getters?.[AUTH_USER_GETTER];
          const email = user?.email || '';
          savePendingQuote(email, this.quoteData);

          // Push the new quoteId to the URL so refreshes don't pull the old Comprehensive quote
          if (this.quoteData.quoteId && String(this.$route.query.quoteId) !== String(this.quoteData.quoteId)) {
            this.$router.replace({ query: { ...this.$route.query, quoteId: this.quoteData.quoteId } }).catch(() => {});
          }

          const tpoMsg = 'Premium recalculated for TPO coverage.';
          this.$notify.info(tpoMsg);
        }
      } catch (err) {
        this.$notify.fromError(err, 'Requote Failed');
      } finally { this.recomputingTpo = false; }
    },

    async confirmVehicle() {
      trackAgentInteraction(this, 'agent_sale_confirm_vehicle', {
        reg_no: this.form.regNo || 'unknown',
      });
      // ── DIRECT JOURNEY BACKGROUND QUOTE GENERATION ──
      if (this.isDirectJourney && !this.quoteData) {
        this.updatingQuote = true;
        try {
          const agentId = await this.getAgentId();
          // Create Quote Mapping
          const coverMap = { comprehensive: 104, tpo: 106, tpf: 105 };
          const payload = {
            origin: 'P',
            agentId,
            carDTOS: [{
              carId: 0,
              coverTypeId: coverMap[this.form.cover_type] || 104,
              evaluationSelected: -1,
              hasInsurance: false,
              make: this.form.make,
              model: this.form.model,
              riskId: 0,
              seatingCapacity: this.form.seatingCapacity || 0,
              valueOfCar: this.form.cover_type === 'tpo' ? 0 : this.form.value,
              bodyType: this.form.bodyType || '',
              valueOfEntertainmentScreen: 0,
              valueOfWindScreen: 0,
              vehicleImages: [],
              yearOfManufacture: this.form.yom,
              registrationNumber: this.form.regNo,
              logbookTransferred: this.form.logbookTransferred === 'yes' ? 'Y' : 'N',
            }],
            prodId: 3437,
            email: '',
            fullName: '',
            kraPinNumber: '',
          };

          const qres = await axios.post(QUOTE_CREATE_URL, payload, { _skipErrorRedirect: true });
          if (!qres.data?.data && !qres.data?.success) {
            throw new Error(qres.data?.msg || qres.data?.message || 'Failed to create quote');
          }
          this.quoteData = qres.data.data || qres.data.object;
          this.quoteId = this.quoteData.quoteId;

          // Sync Benefits if any
          if (this.form.addons_dynamic.length > 0) {
            const riskId = this.quoteData?.covertypes?.[0]?.riskId;
            if (riskId) {
              const mappedBenefits = this.chargeableBenefits
                .filter((b) => {
                  if (!this.form.addons_dynamic.includes(b.sectCode)) return false;
                  // For freeLimit benefits: only include in API call if custom > freeLimit
                  if (b.freeLimit > 0) {
                    const custom = this.form.benefit_custom_limits[b.sectCode];
                    return custom && custom > b.freeLimit;
                  }
                  return true;
                })
                .map((b) => ({
                  noOfBeneficiaries: 0,
                  sectionCode: b.sectCode,
                  sectionName: b.sectDesc,
                  insuredAmount: this.form.benefit_custom_limits[b.sectCode] || b.insuredAmount || 0,
                }));

              if (mappedBenefits.length > 0) {
                await axios.post('/customer/quotation/benefit/change', {
                  riskId,
                  benefits: mappedBenefits,
                }, { _skipErrorRedirect: true });
              }
            }
          }
        } catch (err) {
          this.$notify.fromError(err, 'Direct Quote Generation Failed');
          this.updatingQuote = false;
          return;
        } finally {
          this.updatingQuote = false;
        }
      }

      // 1. Check for Duplicate Risk before proceeding
      try {
        const riskId = this.quoteData?.covertypes?.[0]?.riskId || this.quoteData?.riskId || this.quoteId;
        const res = await axios.post('/customer/gis/policies/risk/check-duplicate', {
          riskId: String(riskId),
          dateFrom: this.form.startDate,
          dateTo: this.nextYearDate,
          sclCode: 3437,
        }, { _skipErrorRedirect: true });

        if (!res.data.success) {
          this.$notify.error(res.data.message || 'Duplicate risk detected.');
          return;
        }
      } catch (err) {
        this.$notify.fromError(err, 'Risk Check Failed');
        return;
      }

      this.updatingQuote = true;
      try {
        await this.updateQuote();
        this.completeTray(1);
      } catch (err) {
        this.$notify.fromError(err, 'Quote Update Failed');
      } finally {
        this.persistProgress();
        this.updatingQuote = false;
      }
    },

    onCoverTypeChange() {
      // If TPO was forced by vehicle age, don't allow switching away
      if (this.yomDowngradeDetected) return;

      this.directMakeMismatch = false;
      this.fetched.vehicle = false;
      this.ntsaFound = false;
      this.ntsaUnavailable = false;
      this.ntsaStatusMessage = '';
      this.verifyError = null;
      this.doubleInsuranceDetails = null;
      this.quoteData = null;
      this.livePremium = null;
      this.liveRateApplied = null;

      // Explicitly wipe the cached journey so the old TPO state can't restore
      const user = this.$store?.getters?.[AUTH_USER_GETTER];
      const email = user?.email || '';
      clearDirectJourney(email);

      this.fetchChargeableBenefits();
      this.calculateLivePremium();
    },
    debounceCalc() {
      if (this.calcTimer) clearTimeout(this.calcTimer);
      this.calcTimer = setTimeout(() => {
        this.calculateLivePremium();
      }, 800);
    },

    async calculateLivePremium({ skipApiSync } = {}) {
      if (skipApiSync) return;
      // Need make + model + yom minimum
      if (!this.form.make || !this.form.model || !this.form.yom) return;

      // For comprehensive: need a value, but lower threshold to 100k for early estimates
      if (!this.isTpo && (!this.form.value || this.form.value < 100000)) {
        this.livePremium = null;
        this.liveRateApplied = null;
        return;
      }

      this.livePremiumLoading = true;
      try {
        const agentId = await this.getAgentId();
        const coverMap = { comprehensive: 104, tpo: 106 };
        const payload = {
          origin: 'P',
          agentId,
          carDTOS: [{
            carId: 0,
            coverTypeId: coverMap[this.form.cover_type] || 104,
            evaluationSelected: -1,
            hasInsurance: false,
            make: this.form.make,
            model: this.form.model,
            riskId: 0,
            seatingCapacity: this.form.seatingCapacity || 0,
            valueOfCar: this.isTpo ? 0 : this.form.value,
            bodyType: this.form.bodyType || '',
            valueOfEntertainmentScreen: 0,
            valueOfWindScreen: 0,
            vehicleImages: [],
            yearOfManufacture: this.form.yom,
            registrationNumber: this.form.regNo || '',
            cc: this.form.engineSize || '',
            color: this.form.colour || '',
          }],
          prodId: 3437,
          email: '',
          fullName: '',
          kraPinNumber: '',
        };

        const res = await axios.post(QUOTE_CREATE_URL, payload, { _skipErrorRedirect: true });
        const data = res.data?.data || res.data?.object;

        // ── Extract Base Premium immediately ──
        if (data && data.covertypes && data.covertypes.length > 0) {
          const ct = data.covertypes[0];
          if (ct && ct.riskPremDto && ct.riskPremDto.totalPremium) {
            const untaxed = Math.round((ct.riskPremDto.totalPremium - 40) / 1.0045);
            this.liveBasicPremium = untaxed;
            this.livePremium = untaxed;
            this.benefitDetails = [];
            const rate = ct.rateApplied;
            this.liveRateApplied = (!this.isTpo && rate != null && Number(rate) < 100) ? Number(rate) : null;
          }
        }

        // Handle benefits update if addons selected
        if (data && data.quoteId && this.form.addons_dynamic.length > 0 && !this.isTpo) {
          const riskId = data.covertypes?.[0]?.riskId || data.covertypes?.[0]?.car?.riskId;
          if (riskId) {
            const mappedBenefits = this.chargeableBenefits
              .filter((b) => {
                if (!this.form.addons_dynamic.includes(b.sectCode)) return false;
                // For freeLimit benefits: only include in API call if custom > freeLimit
                if (b.freeLimit > 0) {
                  const custom = this.form.benefit_custom_limits[b.sectCode];
                  return custom && custom > b.freeLimit;
                }
                return true;
              })
              .map((b) => ({
                noOfBeneficiaries: 0,
                sectionCode: b.sectCode,
                sectionName: b.sectDesc,
                insuredAmount: this.form.benefit_custom_limits[b.sectCode] || b.insuredAmount || 0,
              }));

            try {
              const bres = await axios.post('/customer/quotation/benefit/change', {
                riskId,
                benefits: mappedBenefits,
              }, { _skipErrorRedirect: true });
              const bdata = bres.data?.data || bres.data?.object || bres.data;
              if (bdata && bdata.totalPremium) {
                this.benefitDetails = bdata.benefits || [];
                // API return bdata.totalPremium is usually the FULL premium with benefits
                // Un-tax it so manualTotalPremium doesn't double-tax
                this.livePremium = Math.round((bdata.totalPremium - 40) / 1.0045);
                return; // skip further processing
              }
            } catch (err) {
              console.warn('Benefit premium update failed, using base premium');
            }
          }
        }
      } catch (e) {
        console.error('Live calc error:', e);
      } finally {
        this.livePremiumLoading = false;
      }
    },

    /* ── TRAY 2: Customer ── */
    async fetchCustomerDetails() {
      if (!this.form.idNo || !this.form.expectedFirstName) {
        this.$root.$emit(SHOW_ERROR, 'Enter first name and ID number'); return;
      }
      const idCheck = validateNationalId(this.form.idNo);
      if (!idCheck.valid) {
        this.$root.$emit(SHOW_ERROR, idCheck.message); return;
      }
      this.form.idNo = idCheck.normalized;
      this.fetching.customer = true;
      this.customerHarvestingDetected = false;
      this.fetched.customer = false;
      this.iprsUnavailable = false;
      this.iprsManualEntry = false;

      try {
        // ── Run IPRS and KRA completely independently ──
        const [iprsRes, kraRes] = await Promise.allSettled([
          axios.get(`/customer/gis/policies/iprsdetail?idNumber=${this.form.idNo}`, {
            timeout: 15000,
            _skipErrorRedirect: true,
            validateStatus: () => true, // don't throw on 500
          }),
          axios.get(`/customer/gis/policies/kra-pin-check?idNumber=${this.form.idNo}`, {
            timeout: 15000,
            _skipErrorRedirect: true,
            validateStatus: () => true,
          }),
        ]);

        // ── Process IPRS ──
        if (iprsRes.status === 'fulfilled') {
          const res = iprsRes.value;

          // Handle server error (500, 503, etc.)
          if (res.status >= 500) {
            this.iprsUnavailable = true;
            this.$notify.info('Identity registry is temporarily unavailable. You can enter customer details manually.');
          } else {
            const raw = res.data?.object;
            const data = Array.isArray(raw) ? raw[0] : raw;

            if (data) {
              const fetchedFirst = (data.firstName || '').toUpperCase();
              const expected = (this.form.expectedFirstName || '').toUpperCase();

              if (fetchedFirst.includes(expected) || expected.includes(fetchedFirst)) {
                this.form.fullName = [data.firstName, data.otherName, data.surname]
                  .filter((v) => v && v !== 'null')
                  .join(' ')
                  .trim();
                this.form.gender = data.gender || '';
                this.form.citizenship = (data.citizenShip && data.citizenShip !== 'null') ? data.citizenShip : '';
                this.form.occupation = (data.occupation && data.occupation !== 'null') ? data.occupation : '';
                this.form.dateOfBirth = '';
                if (data.dateOfBirth && data.dateOfBirth !== 'null') {
                  try {
                    // IPRS format: "M/D/YYYY HH:MM:SS AM" — parse manually to avoid timezone shifts
                    const datePart = data.dateOfBirth.split(' ')[0]; // "12/1/2000"
                    const [month, day, year] = datePart.split('/');
                    if (month && day && year) {
                      const mm = String(month).padStart(2, '0');
                      const dd = String(day).padStart(2, '0');
                      this.form.dateOfBirth = `${year}-${mm}-${dd}`; // "2000-12-01"
                    }
                  } catch { /* skip */ }
                }
                this.idVerified = true;
              } else {
                this.customerHarvestingDetected = true;
              }
            } else {
              // IPRS returned success but empty data — treat as unavailable
              this.iprsUnavailable = true;
              this.$notify.info('No IPRS record found for this ID. Please enter details manually.');
            }
          }
        } else {
          // Network error / timeout on IPRS — don't block
          this.iprsUnavailable = true;
          this.$notify.info('Could not reach identity registry. Please enter details manually.');
        }

        // ── Process KRA (fully independent of IPRS result) ──
        if (kraRes.status === 'fulfilled') {
          const kraData = kraRes.value?.data?.object;
          if (kraData && kraData.ResponseCode === '30000') {
            this.form.kraPin = kraData.TaxpayerPIN || this.form.kraPin;
            this.kraVerified = true;
          } else {
            this.kraVerified = false;
          }
        } else {
          this.kraVerified = false;
        }

        // ── If IPRS unavailable, allow manual entry (don't throw) ──
        if (this.iprsUnavailable) {
          this.iprsManualEntry = true;
        }
      } catch (err) {
        console.error('Customer lookup failed', err);
        this.$notify.fromError(err, 'Verification Error');
        this.iprsUnavailable = true;
        this.iprsManualEntry = true;
      } finally {
        this.fetching.customer = false;
        this.fetched.customer = true;
      }
    },

    async confirmCustomer() {
      trackAgentInteraction(this, 'agent_sale_confirm_customer');
      // Only upload files that haven't been auto-uploaded already
      try {
        const pending = [];

        // Logbook is always required
        if (!this.uploads.logbook.done && this.uploads.logbook.file) {
          pending.push(this.uploadFile('logbook', DOC_NAMES.logbook));
        }

        // KRA and ID only required when verifications fail
        if (this.iprsUnavailable || !this.idVerified || !this.kraVerified) {
          if (!this.uploads.kra.done && this.uploads.kra.file) {
            pending.push(this.uploadFile('kra', DOC_NAMES.kra));
          }
          if (!this.uploads.nationalId.done && this.uploads.nationalId.file) {
            pending.push(this.uploadFile('nationalId', DOC_NAMES.nationalId));
          }
        }

        await Promise.all(pending);
      } catch (err) {
        this.$notify.fromError(err, 'Upload Error'); return;
      }

      this.updatingQuote = true;
      try {
        await this.updateQuote();
        if (this.isTpo) {
          // Tray 3 (Schedule) is hidden for TPO — auto-complete it and jump to Declarations
          if (!this.completedTrays.includes(2)) this.completedTrays.push(2);
          if (!this.completedTrays.includes(3)) this.completedTrays.push(3);
          this.activeTray = 4;
          this.persistProgress();
        } else {
          this.completeTray(2);
        }
      } catch (err) {
        this.$notify.fromError(err, 'Quote Update Failed');
      } finally {
        this.persistProgress();
        this.updatingQuote = false;
      }
    },

    /* ── TRAY 3: Schedule ── */
    onFinancierTypeChange(type) {
      this.form.financierType = type;
      this.form.financierInstitution = '';
      this.form.financierSubType = '';
      this.form.interestType = 'hp';
      this.form.financierRef = '';
    },
    onInstitutionPicked(name) {
      const match = this.interestedParties.find((i) => i.name === name);
      if (match) {
        this.form.financierSubType = match.type || 'Interested Party';
        this.form.financierCode = match.code;
        this.$notify.info('Financial interest noted. Please ensure the policy is updated by the underwriting team.');
      }
    },
    clearFinancier() {
      this.form.financierInstitution = '';
      this.form.financierSubType = '';
      this.form.financierRef = '';
    },
    async confirmSchedule() {
      trackAgentInteraction(this, 'agent_sale_confirm_schedule');
      this.updatingQuote = true;
      try {
        await this.updateQuote();
        this.completeTray(3);
      } catch (err) {
        this.$notify.fromError(err, 'Save Failed');
      } finally {
        this.persistProgress();
        this.updatingQuote = false;
      }
    },

    /* ── Upload helper ──
     * handleFileSelect now immediately kicks off the upload so the file
     * is already uploading in the background before the user moves on.
     */
    handleFileSelect(file, key) {
      if (!file) return;
      if (file.size > 6 * 1024 * 1024) { this.$notify.error('File exceeds 6 MB limit.'); return; }
      if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
        this.$notify.error('Only PDF, JPEG, PNG files allowed.'); return;
      }
      this.uploads[key].file = file;
      this.uploads[key].done = false;
      // ── Immediate background upload ─────────────────────────────────────
      // Fire and forget — the user can continue filling the form while the
      // file uploads. confirmCustomer() skips files already marked as done.
      this.uploadFile(key, DOC_NAMES[key] || key).catch((err) => {
        console.error(`Auto-upload failed for ${key}:`, err);
        // Silently reset so the user can retry via the confirm button
        this.uploads[key].done = false;
      });
    },

    clearUpload(key) {
      this.uploads[key].file = null;
      this.uploads[key].done = false;
    },

    async uploadFile(key, docName) {
      if (!this.uploads[key].file || this.uploads[key].done) return;

      const { file } = this.uploads[key];

      if (file.size > 10 * 1024 * 1024) {
        this.$notify.error(`${docName} exceeds 10 MB limit.`);
        return;
      }

      this.uploads[key].loading = true;

      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(/,(.+)/.exec(reader.result)[1]);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(file);
        });

        const payload = {
          fileName: file.name,
          kraPinNumber: this.form.kraPin || this.form.idNo || '',
          idNumber: this.form.idNo || '',
          img64: base64,
          bucket: 'gi-policies',
          phoneNumber: this.form.phone || '',
          email: this.form.email || '',
          fullName: this.form.fullName || '',
          documentType: docName,
          travelType: null,
        };

        await axios.post('/customer/quotation/upload', payload, { timeout: 60000, _skipErrorRedirect: true });

        this.uploads[key].done = true;
      } catch (err) {
        this.$notify.fromError(err, 'File Upload Failed');
        throw err;
      } finally {
        this.uploads[key].loading = false;
      }
    },

    async updateQuote() {
      if (!this.quoteId) return;
      const agentId = await this.resolveAgentIdOrFail();

      const genderMap = {
        M: 'MALE', F: 'FEMALE', MALE: 'MALE', FEMALE: 'FEMALE',
      };
      const gender = genderMap[this.form.gender] || 'MALE';

      const additionalBenefits = (this.chargeableBenefits || [])
        .filter((b) => (this.form.addons_dynamic || []).includes(b.sectCode))
        .map((b) => ({
          id: b.sectCode,
          benefit: b.sectDesc,
          value: this.form.benefit_custom_limits[b.sectCode] || null,
          noOfBeneficiaries: b.noOfBeneficiaries || 0,
        }));

      const payload = {
        agent_option: 1,
        agentId,
        quoteId: this.quoteData?.quoteId || 0,
        selectedRiskId: this.selectedRiskId || 0,
        prodId: this.prodId || 0,
        stepCode: 'PROVIDE_DETAILS',
        fullName: this.form.fullName || '',
        email: this.form.email || '',
        mobileNumber: this.form.phone || '',
        idNumber: this.form.idNo || '',
        kraPinNumber: this.form.kraPin || '',
        pinNumber: this.form.kraPin || '',
        gender,
        poBoxNo: '',
        postalCode: '',
        addressLine1: '1245',
        coverFrom: this.changeFormat(this.form.startDate),
        documents: [],
        additionalBenefits,
        carDTOS: [{
          ...(this.quoteData?.covertypes?.[0]?.car || {}),
          registrationNumber: this.form.regNo,
          make: this.form.make,
          model: this.form.model,
          yearOfManufacture: this.form.yom,
          chassisNumber: this.form.chassis,
          engineNumber: this.form.engine,
          engineCapacity: this.form.engineSize,
          seatingCapacity: parseInt(this.form.seatingCapacity, 10) || 0,
          logBookNumber: this.form.logbookNo,
          logbookTransferred: this.form.logbookTransferred === 'yes' ? 'Y' : 'N',
          financierName: (!this.isTpo && this.form.financierType === 'financier') ? this.form.financierCode : '',
          financierType: (!this.isTpo && this.form.financierType === 'financier') ? this.form.financierSubType : '',
          interestType: (!this.isTpo && this.form.financierType === 'financier') ? this.form.interestType : '',
          financierRef: (!this.isTpo && this.form.financierType === 'financier') ? this.form.financierRef : '',
          valuationDate: this.form.valuer ? this.changeFormat(this.form.valuationDate) : null,
          valuer: this.form.valuer || '',
          valuationLocation: this.valuerLocationFilter || '',
          cc: this.form.engineSize || '',
          color: this.form.colour || '',
          bodyType: this.form.bodyType || this.quoteData?.covertypes?.[0]?.car?.bodyType || '',
        }],
      };

      const benefitsSnapshot = {};
      if (this.quoteData?.covertypes) {
        this.quoteData.covertypes.forEach((ct) => {
          if (ct.additionalBenefits && ct.additionalBenefits.length > 0) {
            benefitsSnapshot[ct.riskId] = ct.additionalBenefits;
          }
        });
      }

      const res = await axios.put('/customer/quotation/update/agent', payload, { _skipErrorRedirect: true });

      const updated = res.data?.data || res.data;
      if (updated && updated.covertypes) {
        updated.covertypes = updated.covertypes.map((ct) => {
          if ((!ct.additionalBenefits || ct.additionalBenefits.length === 0) && benefitsSnapshot[ct.riskId]) {
            return { ...ct, additionalBenefits: benefitsSnapshot[ct.riskId] };
          }
          return ct;
        });

        this.quoteData = { ...this.quoteData, ...updated };
      }
    },

    /* ── Tray 4 ── */
    async generateConsentLink() {
      if (!this.quoteId || !this.form.email) return;

      try {
        const policyUrl = 'https://www.pacisinsurance.com/privacy-policy-2/';

        await axios.post(EMAIL_API_URL, {
          to: this.form.email,
          subject: `Pacis Insurance — Motor Policy Details (Quote #${this.quoteId})`,
          body: `
        <p>Dear ${this.form.fullName || VALUED_CUSTOMER},</p>
        <p>Your motor insurance cover has been successfully received and is now being processed by our underwriting team.</p>
        <p>Please review your policy details below along with our privacy policy.</p>

        <table style="border-collapse:collapse;font-size:14px;margin:16px 0;width:100%;max-width:480px;">
          <tr style="background:#F0F9FF;">
            <td style="padding:8px 14px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Quote Reference</td>
            <td style="padding:8px 14px;font-weight:700;color:#1E4276;border-bottom:1px solid #E8EDF2;">#${this.quoteId}</td>
          </tr>
          <tr>
            <td style="padding:8px 14px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Cover Type</td>
            <td style="padding:8px 14px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${this.isTpo ? TPO_LABEL : COMPREHENSIVE_LABEL}</td>
          </tr>
          <tr style="background:#F0F9FF;">
            <td style="padding:8px 14px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Vehicle</td>
            <td style="padding:8px 14px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${this.form.make} ${this.form.model} (${this.form.yom})</td>
          </tr>
          <tr>
            <td style="padding:8px 14px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Registration</td>
            <td style="padding:8px 14px;font-weight:700;color:#1E4276;border-bottom:1px solid #E8EDF2;font-family:monospace;letter-spacing:0.05em;">${this.form.regNo}</td>
          </tr>
          <tr style="background:#F0F9FF;">
            <td style="padding:8px 14px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Cover Period</td>
            <td style="padding:8px 14px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${this.form.startDate} → ${this.nextYearDate}</td>
          </tr>
          <tr>
            <td style="padding:8px 14px;color:#64748b;font-weight:600;">Premium</td>
            <td style="padding:8px 14px;font-weight:800;color:#00AEEF;">${this.formattedPremium}</td>
          </tr>
        </table>

        <p style="margin:20px 0 8px;font-size:13px;color:#64748b;font-weight:600;">Data Protection & Privacy</p>
        <p style="margin:0 0 12px;font-size:13px;color:#64748b;">To understand how we handle and protect your personal information during the processing of your <strong>${this.isTpo ? TPO_LABEL : COMPREHENSIVE_LABEL}</strong> cover, please review our privacy policy:</p>
        <p><a href="${policyUrl}" style="color:#00AEEF;font-size:14px;font-weight:600;text-decoration:underline;">${policyUrl}</a></p>

        <p style="margin:24px 0 8px;font-size:13px;color:#64748b;">If you have any questions regarding your cover, please contact us at <a href="mailto:dataprotectionoffice@paciskenya.com" style="color:#00AEEF;">dataprotectionoffice@paciskenya.com</a>.</p>
        <br/>
        <p>Regards,<br/>Pacis Insurance Company Limited</p>
      `,
        });
        this.$notify.success(`Policy information sent to ${this.form.email}`, 'Sent');
      } catch (err) {
        console.error('Consent dispatch failed', err);
      }
    },

    async sendPaymentSuccessEmails() {
      const customerEmail = this.form.email;
      const agentUser = this.$store?.getters?.[AUTH_USER_GETTER];
      const agentEmail = agentUser?.email;
      const agentName = agentUser?.name || 'Agent';

      if (!this.quoteId || !customerEmail) return;

      const vehicleString = `${this.form.make} ${this.form.model} (${this.form.regNo})`;
      const coverLabel = this.isTpo ? TPO_LABEL : COMPREHENSIVE_LABEL;
      const subject = `Payment Confirmed — ${coverLabel} Policy Processing (Quote #${this.quoteId})`;

      const policySummaryTable = `
    <table style="border-collapse:collapse;font-size:13px;margin:14px 0;width:100%;max-width:520px;">
      <tr style="background:#F0F9FF;">
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Quote Reference</td>
        <td style="padding:7px 12px;font-weight:700;color:#1E4276;border-bottom:1px solid #E8EDF2;">#${this.quoteId}</td>
      </tr>
      <tr>
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Cover Type</td>
        <td style="padding:7px 12px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${coverLabel}</td>
      </tr>
      <tr style="background:#F0F9FF;">
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Vehicle</td>
        <td style="padding:7px 12px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${vehicleString}</td>
      </tr>
      <tr>
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Registration</td>
        <td style="padding:7px 12px;font-weight:700;color:#1E4276;border-bottom:1px solid #E8EDF2;font-family:monospace;">${this.form.regNo}</td>
      </tr>
      <tr style="background:#F0F9FF;">
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Cover Period</td>
        <td style="padding:7px 12px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${this.form.startDate} → ${this.nextYearDate}</td>
      </tr>
      <tr>
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Insured</td>
        <td style="padding:7px 12px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${this.form.fullName || '—'}</td>
      </tr>
      <tr style="background:#F0F9FF;">
        <td style="padding:7px 12px;color:#64748b;font-weight:600;">Premium Paid</td>
        <td style="padding:7px 12px;font-weight:800;color:#00AEEF;">${this.formattedAmountDueNow}</td>
      </tr>
    </table>
  `;

      /*
      const valuationSection = (!this.isTpo && this.form.valuer && this.form.valuationDate) ? `
    <p style="margin:16px 0 4px;font-weight:700;color:#1E4276;">Valuation Details</p>
    <table style="border-collapse:collapse;font-size:13px;margin:0 0 14px;width:100%;max-width:520px;">
      <tr style="background:#F0FDF4;">
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Assigned Valuer</td>
        <td style="padding:7px 12px;font-weight:700;color:#065F46;border-bottom:1px solid #E8EDF2;">${this.form.valuer}</td>
      </tr>
      <tr>
        <td style="padding:7px 12px;color:#64748b;font-weight:600;border-bottom:1px solid #E8EDF2;">Valuation Date</td>
        <td style="padding:7px 12px;color:#1E4276;border-bottom:1px solid #E8EDF2;">${this.form.valuationDate}</td>
      </tr>
      ${this.selectedValuerData?.phone ? `<tr style="background:#F0FDF4;"><td style="padding:7px 12px;color:#64748b;font-weight:600;">Valuer Contact</td><td style="padding:7px 12px;color:#1E4276;">${this.selectedValuerData.phone}</td></tr>` : ''}
    </table>
  ` : '';
      */
      const valuationSection = '';

      const promises = [];

      // 1. Customer email
      promises.push(
        axios.post(EMAIL_API_URL, {
          to: customerEmail,
          subject,
          body: `
        <p>Dear ${this.form.fullName || VALUED_CUSTOMER},</p>
        <p>Your payment for motor insurance has been successfully received. Your policy is now being processed by our underwriting team.</p>
        ${policySummaryTable}
        ${valuationSection}
        <p>Your Certificate of Insurance and Policy Document will be sent to this email once processing is complete.</p>
        <br/>
        <p>Thank you for choosing Pacis.</p>
        <p>Regards,<br/>Pacis Insurance Company Limited</p>
      `,
        }, { _skipErrorRedirect: true }),
      );

      // 2. Agent email
      if (agentEmail) {
        promises.push(
          axios.post(EMAIL_API_URL, {
            to: agentEmail,
            subject: `[Agent Confirmation] ${subject}`,
            body: `
          <p>Dear ${agentName},</p>
          <p>Payment has been successfully received for your client <strong>${this.form.fullName || '—'}</strong>.</p>
          ${policySummaryTable}
          ${valuationSection}
          <p>The policy is now queued for underwriting. Documents will be dispatched automatically upon completion.</p>
          <br/>
          <p>System Notification,<br/>Pacis Insurance Company Limited</p>
        `,
          }, { _skipErrorRedirect: true }),
        );
      }

      // 3. Underwriting team
      promises.push(
        axios.post(EMAIL_API_URL, {
          to: 'underwriting@paciskenya.com',
          subject: `[New Sale] ${coverLabel} — ${vehicleString} (Quote #${this.quoteId})`,
          body: `
        <p>A new motor insurance sale has been completed and payment received. </p>
        ${policySummaryTable}
        ${valuationSection}
        <p><strong>Agent:</strong> ${agentName} (${agentEmail || '—'})<br/>
        <strong>Client Phone:</strong> ${this.form.phone || '—'}<br/>
        <strong>Client Email:</strong> ${customerEmail}<br/>
        <strong>KRA PIN:</strong> ${this.form.kraPin || '—'}</p>
        <br/>
        <p>System Notification,<br/>Pacis Agency Portal</p>
      `,
        }, { _skipErrorRedirect: true }),
      );

      /*
      // 4. Valuer email (Comprehensive only, if selected)
      if (!this.isTpo && this.selectedValuerData && this.form.valuationDate) {
        const valuer = this.selectedValuerData;
        const stampUrl = `${window.location.origin}/images/stamp-pacis.jpeg`;
        promises.push(
          axios.post(EMAIL_API_URL, {
            to: valuer.email,
            subject: `Valuation Authority — ${vehicleString} (Quote #${this.quoteId})`,
            body: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto;">
            <img src="${window.location.origin}/pacis_logo.png" width="140" style="display:block;margin-bottom:20px;" />

            <p>Dear Sir/Madam,</p>

            <p><strong>RE: VALUATION</strong></p>

            <p><strong><u>POLICY NO. #${this.quoteId} REG NO. ${this.form.regNo}</u></strong></p>

            <p>We refer to the above vehicle(s) which is/are insured with us. To ensure that the sum insured of the motor vehicle reflects the current market value and that the compensation given in the event of a claim is adequate, we authorize the valuation of this vehicle by <strong>${valuer.name}</strong>.</p>

            <p>This valuation authority is valid for <strong>14 days</strong> as from <strong>${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.</p>

            <p>Upon valuation, Pacis will adjust the values as per the report and send an endorsement indicating the revision. You are required to pay additional premium where necessary.</p>

            <p>All claims will be settled based on this value less any deductions as provided for in the policy conditions.</p>

            <p>In the absence of a valuation report the pre-accident value (PAV) will apply. In such cases, there is a risk of under insurance where the principle of average is applicable.</p>

            <p>Yours Sincerely,</p>

            <p><strong>Pacis Digital</strong></p>

            <img src="${stampUrl}" width="180" style="display:block; margin-top: 10px; margin-bottom: 20px;" alt="Pacis Stamp" />

            <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;" />
            <p style="font-size: 12px; color: #666;">
              <strong>Client Details:</strong><br/>
              Name: ${this.form.fullName || '—'}<br/>
              Phone: ${this.form.phone || '—'}<br/>
              Proposed Valuation Date: ${this.form.valuationDate}
            </p>
            <p style="font-size: 12px; color: #666;">
              <strong>Intermediary:</strong> ${agentName} (${agentEmail || '—'})
            </p>
          </div>
        `,
          }, { _skipErrorRedirect: true }),
        );
      }
      */

      await Promise.allSettled(promises);
    },

    /* ── Payment ── */
    buildPolicyInitPayload() {
      return {
        branchCode: '10',
        carDTOS: [
          {
            riskId: this.selectedRiskId || this.quoteData?.covertypes?.[0]?.riskId,
            registrationNumber: this.form.regNo,
          },
        ],
        coverFrom: this.changeFormat(this.form.startDate),
        dateFormat: 'dd/MM/yyyy',
        premium: this.amountDueNow,
        mobileNumber: this.form.phone,
        email: this.form.email,
        fullName: this.form.fullName,
        pinNumber: this.form.kraPin,
        dlImage: {
          backImageUrl: '',
          frontImageUrl: '',
        },
        prodId: this.prodId,
        quoteId: this.quoteId,
      };
    },
    async initLocalPolicyForPayment() {
      const res = await axios.post('/customer/quotation/policy/agent', this.buildPolicyInitPayload(), {
        _skipErrorRedirect: true,
      });
      if (res.data?.success === false) {
        throw new Error(res.data.msg || res.data.message || 'Could not initialize policy for payment.');
      }
    },
    async processPayment() {
      trackAgentInteraction(this, 'agent_sale_process_payment', {
        amount: this.amountDueNow || 0,
      });
      if (!this.form.mpesaPhone || !this.quoteData) return;
      this.fetching.payment = true;

      try {
        await this.initLocalPolicyForPayment();
      } catch (err) {
        console.error('Policy creation fail', err);
        this.$notify.error(err.message || err.response?.data?.message || 'Could not initialize policy for payment.');
        this.fetching.payment = false;
        return;
      }

      const rawPhone = this.form.mpesaPhone.replace(/\\D/g, '');
      let normalizedPhone = rawPhone;
      if (rawPhone.startsWith('0') && rawPhone.length === 10) {
        normalizedPhone = `254${rawPhone.slice(1)}`;
      } else if (rawPhone.startsWith('254')) {
        normalizedPhone = rawPhone;
      } else if (rawPhone.startsWith('7') && rawPhone.length === 9) {
        normalizedPhone = `254${rawPhone}`;
      }

      let agentId;
      try {
        agentId = await this.resolveAgentIdOrFail();
      } catch (err) {
        this.fetching.payment = false;
        return; // blocked — error already shown
      }
      const transactionReference = this.paymentReference;

      axios.post('/customer/payment/client-api/v1/payments/mpesa/stk/request/agent', {
        serviceType: 'PORTAL',
        transactionType: 'PAYMENT',
        payableAmount: this.amountDueNow,
        agentId,
        kraPin: this.form.kraPin,
        transactionReference,
        transactionDescription: `rfq-${this.quoteData.quoteId}`,
        customerPhoneNumber: normalizedPhone,
      }, { _skipErrorRedirect: true })
        .then((res) => {
          if (res.data.success) {
            this.$notify.success('M-Pesa push sent. Check your phone.');
            this.pollingActive = true;
            this.pollTimer = setTimeout(() => this.checkPaymentStatus(res.data.data.paymentId), 10000);
          } else {
            this.$notify.error(res.data.message || 'Failed to trigger M-Pesa.');
            this.fetching.payment = false;
          }
        }).catch((err) => {
          this.$notify.fromError(err, 'M-Pesa Error');
          this.fetching.payment = false;
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
        });
    },

    async fetchCertificate() {
      if (!this.quoteId) return;
      this.certificateLoading = true;
      try {
        const res = await axios.get(`/customer/gis/policies/certificates/download?quoteId=${this.quoteId}`, {
          responseType: 'blob',
          _skipErrorRedirect: true,
        });
        if (res.status === 200) {
          const blob = new Blob([res.data], { type: 'application/pdf' });
          this.certificateUrl = window.URL.createObjectURL(blob);
          this.$notify.success('Certificate ready for download.');
        } else {
          this.$notify.info('Certificate is still being processed. Please wait a moment and try again.');
        }
      } catch (err) {
        console.error('Failed to fetch certificate', err);
        this.$notify.error('Certificate not yet available. It will be sent to the client email once issued.');
      } finally {
        this.certificateLoading = false;
      }
    },

    checkPaymentStatus(paymentId) {
      axios.get(`/customer/payment/client-api/v1/payments/mpesa/payment/query/agent?paymentId=${paymentId}`, { _skipErrorRedirect: true })
        .then((res) => {
          const status = res.data?.data?.paymentStatus;
          if (status === 'SUCCESSFUL') {
            this.fetching.payment = false; this.pollingActive = false;
            this.$notify.success('Payment received! Issuing cover.');
            invalidateAfterPayment(this.$store, 'agent');
            this.onSaleComplete();
            this.completeTray(5);
            // Trigger success emails asynchronously
            this.sendPaymentSuccessEmails().catch((e) => console.error('Silent failure dispatching payment success emails:', e));
            this.generateConsentLink().catch((e) => console.error('Consent dispatch failed:', e));
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            this.fetching.payment = false; this.pollingActive = false;
            this.$notify.error('Payment failed. Use Paybill.');
            this.paybillFallback = true;
            this.activePayTab = 'paybill';
          } else if (this.pollingActive) {
            this.pollTimer = setTimeout(() => this.checkPaymentStatus(paymentId), 10000);
          }
        }).catch((err) => {
          console.error('Payment polling failed', err);
          this.fetching.payment = false;
          this.pollingActive = false;
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
        });
    },

    /* ── Helpers ── */
    initGoogleMaps() {
      // Reset the flag so re-render doesn't block re-init
      const input = document.getElementById('inspAddress');
      if (!input) return;

      if (this.googleMapsLoaded) {
        // Already loaded — just re-attach the autocomplete to the (possibly new) input
        const { google } = window;
        if (!google) return;
        const ac = new google.maps.places.Autocomplete(input, {
          componentRestrictions: { country: 'ke' }, strictBounds: true,
        });
        google.maps.event.addListener(ac, 'place_changed', () => {
          const place = ac.getPlace();
          if (place.geometry) {
            this.inspectionCoordinates = {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            };
            this.inspection.location = place.formatted_address || this.inspection.location;
          }
        });
        return;
      }

      const loader = new Loader(process.env.VUE_APP_GOOGLE_MAPS_API_KEY, {
        version: '3.40', language: 'en', libraries: ['places'],
      });
      loader.load().then((google) => {
        this.googleMapsLoaded = true;
        const el = document.getElementById('inspAddress');
        if (!el) return;
        const ac = new google.maps.places.Autocomplete(el, {
          componentRestrictions: { country: 'ke' }, strictBounds: true,
        });
        google.maps.event.addListener(ac, 'place_changed', () => {
          const place = ac.getPlace();
          if (place.geometry) {
            this.inspectionCoordinates = {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            };
            this.inspection.location = place.formatted_address || this.inspection.location;
          }
        });
      });
    },

    allowedInspectionDates: (val) => ![0, 6].includes(new Date(val).getDay()),
    allowedInspectionHours: (val) => val >= 8 && val <= 17,

    getBenefitPremium(benefit) {
      const customLimit = this.form.benefit_custom_limits[benefit.sectCode];
      const freeLimit = benefit.freeLimit || 0;

      // ── Phase 10: If it's a free limit benefit and NO custom limit over the free limit is set -> 0 ──
      if (freeLimit > 0 && (!customLimit || customLimit <= freeLimit)) {
        return 0;
      }

      if (benefit.rateType === 'Amount' || !benefit.divFactor) {
        return benefit.rate || benefit.minimumLimit || 0;
      }
      const valToUse = customLimit || this.form.value || 0;

      const raw = (benefit.sectDesc || '').toUpperCase().trim();
      const isPVT = raw.includes('PVT') || raw.includes('POLITICAL VIOLENCE') || raw.includes('TERRORISM');
      const isExcess = raw.includes('EXCESS PROTECTOR') || raw.includes('EXCESS PROTECT') || raw.includes('E.P.P') || raw.includes('EPP');

      // For PVT/Excess Protector (percentage of vehicle value)
      if ((isPVT || isExcess) && valToUse) {
        const rate = benefit.rate || 0.25;
        const amount = Math.ceil((rate / 100) * valToUse);
        let min = 0;
        if (isPVT) min = 3000;
        else if (isExcess) min = 5000;
        return Math.max(amount, min);
      }

      if (!valToUse) {
        return benefit.minimumLimit || (benefit.sectCode === 1614 ? 5000 : 0);
      }
      const chargeableValue = Math.max(valToUse - freeLimit, 0);
      const amount = Math.ceil((benefit.rate / benefit.divFactor) * chargeableValue);
      let min = benefit.minimumLimit || 0;
      if (isExcess || benefit.sectCode === 1614) min = 5000;
      return Math.max(amount, min);
    },
    // Helper to get YYYY-MM-DD in local time (prevents UTC shifting bug)
    toLocalISO(d) {
      if (!d) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    fmtCurrency(val) {
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(val || 0);
    },
  },
  metaInfo: { title: 'Convert to Sale' },
};
</script>

<style scoped>
.certificate-download-box {
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  padding: 12px 14px;
  border-radius: 10px;
}
.valuer-auto-notify-note {
  display: flex;
  align-items: flex-start;
  background: #F0FDF4;
  border: 1px solid #A7F3D0;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: .72rem;
  font-weight: 600;
  color: #047857;
  line-height: 1.5;
}
/* ── IPRS Verified Card ── */
.iprs-verified-card {
  background: linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%);
  border: 1.5px solid #A7F3D0;
  border-radius: 12px;
  overflow: hidden;
}
.iprs-verified-header {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background: rgba(16,185,129,0.08);
  border-bottom: 1px solid #A7F3D0;
  font-size: 0.72rem;
  font-weight: 700;
  color: #059669;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.iprs-name-row {
  padding: 14px 16px 8px;
}
.iprs-name {
  font-size: 1.05rem;
  font-weight: 800;
  color: #1E4276;
  letter-spacing: 0.02em;
}
.iprs-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 14px;
  flex-wrap: wrap;
}
.iprs-meta-chip {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #D1FAE5;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #374151;
}

/* ── Layout ── */
.sale-body { padding: 20px 32px 60px; max-width: 860px; margin: 0 auto; }

/* ── Header ── */
.sale-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
.back-btn { width: 36px; height: 36px; border-radius: 10px; border: 1.5px solid #E8EDF2; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .15s; flex-shrink: 0; }
.back-btn:hover { border-color: #00AEEF; background: #EBF8FF; }
.sale-breadcrumb { font-size: .72rem; color: #94a3b8; font-weight: 500; margin: 0 0 2px; text-transform: uppercase; letter-spacing: .4px; }
.sale-title { font-size: 1.2rem; font-weight: 800; color: #1E4276; margin: 0; }
.sale-premium-chip { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; background: rgba(0,174,239,.08); border: 1px solid rgba(0,174,239,.2); border-radius: 20px; padding: 6px 14px; font-size: .82rem; font-weight: 700; color: #1E4276; }
.sale-rate-note { font-size: 0.68rem; font-weight: 600; color: #64748b; width: 100%; }
.lec-rate-note { font-size: 0.72rem; text-align: center; margin: 8px 0 0; color: rgba(255,255,255,0.8); font-weight: 600; }

/* ── Step Progress ── */
.step-progress { display: flex; align-items: center; margin-bottom: 24px; padding: 14px 20px; background: white; border-radius: 12px; border: 1px solid #E8EDF2; box-shadow: 0 1px 4px rgba(0,0,0,.04); overflow-x: auto; }
.step-item { display: flex; align-items: center; flex: 1; min-width: 0; }
.step-circle { width: 28px; height: 28px; border-radius: 50%; background: #E8EDF2; color: #94a3b8; font-size: .72rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .2s; }
.step-active .step-circle { background: #00AEEF; color: white; box-shadow: 0 2px 8px rgba(0,174,239,.3); }
.step-done .step-circle { background: #10b981; color: white; }
.step-label { font-size: .7rem; font-weight: 600; color: #94a3b8; margin-left: 6px; white-space: nowrap; transition: color .2s; }
.step-active .step-label { color: #00AEEF; }
.step-done .step-label { color: #10b981; }
.step-connector { flex: 1; height: 2px; background: #E8EDF2; margin: 0 8px; border-radius: 1px; transition: background .2s; }
.step-connector.done { background: #10b981; }

/* ── Trays ── */
.trays-wrap { display: flex; flex-direction: column; gap: 12px; }
.tray-card { background: white; border-radius: 12px; border: 1.5px solid #E8EDF2; box-shadow: 0 1px 4px rgba(0,0,0,.05); overflow: hidden; transition: border-color .2s, box-shadow .2s; }
.tray-card.tray-open { border-color: #00AEEF; box-shadow: 0 4px 16px rgba(0,174,239,.1); }
.tray-card.tray-done { border-color: #D1FAE5; }
.tray-card.tray-locked { opacity: .55; pointer-events: none; background: #F8FAFC; }
.tray-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; cursor: pointer; user-select: none; transition: background .15s; }
.tray-head:hover { background: #F8FBFF; }
.tray-head-left { display: flex; align-items: center; gap: 12px; }
.tray-num { width: 30px; height: 30px; border-radius: 50%; background: #00AEEF; color: white; font-size: .78rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background .2s; }
.tray-num.done { background: #10b981; }
.tray-num.locked { background: #CBD5E1; color: #94a3b8; }
.tray-title { font-size: .92rem; font-weight: 700; color: #1E4276; margin: 0 0 2px; }
.tray-sub { font-size: .72rem; color: #94a3b8; margin: 0; }
.tray-body { padding: 0 20px 20px; border-top: 1px solid #F1F5F9; }
.tray-desc { font-size: .82rem; color: #64748b; margin: 14px 0 16px; }

/* ── Section Dividers ── */
.section-divider { font-size: .72rem; font-weight: 700; color: #00AEEF; text-transform: uppercase; letter-spacing: .5px; margin: 20px 0 12px; padding-bottom: 6px; border-bottom: 1px solid #EBF8FF; }

/* ── Forms ── */
.form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.form-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
.input-wrap { display: flex; flex-direction: column; }
.input-lbl { font-size: .78rem; font-weight: 600; color: #1E4276; margin-bottom: 6px; }
.req { color: #EF4444; margin-left: 2px; }
.optional { font-size: .7rem; color: #94a3b8; font-weight: 400; margin-left: 4px; }
.input-with-btn { display: flex; align-items: flex-end; gap: 10px; }

/* Vuetify overrides */
::v-deep .sale-input.v-text-field--outlined > .v-input__control > .v-input__slot { background: #fff !important; border-radius: 8px !important; border: 1.5px solid #E2E8F0 !important; min-height: 42px !important; box-shadow: none !important; }
::v-deep .sale-input.v-text-field--outlined fieldset { border: none !important; }
::v-deep .sale-input.v-text-field--outlined.v-input--is-focused > .v-input__control > .v-input__slot { border-color: #00AEEF !important; box-shadow: 0 0 0 3px rgba(0,174,239,.12) !important; }
::v-deep .sale-input input { font-size: .88rem !important; font-weight: 500 !important; color: #1E4276 !important; }
::v-deep .sale-input input::placeholder { color: #94A3B8 !important; opacity: 1 !important; }
::v-deep .sale-input .v-input__prepend-inner { margin-top: 10px !important; }

/* ── Buttons ── */
.verify-btn { height: 42px; padding: 0 18px; background: #1E4276; color: white; border: none; border-radius: 8px; font-size: .82rem; font-weight: 700; display: flex; align-items: center; gap: 6px; cursor: pointer; white-space: nowrap; transition: all .2s; flex-shrink: 0; }
.verify-btn:hover:not(:disabled) { background: #2A5A9E; }
.verify-btn:disabled { opacity: .5; cursor: not-allowed; }
.btn-primary { height: 44px; background: #00AEEF; color: white; border: none; border-radius: 10px; font-size: .88rem; font-weight: 700; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .2s; box-shadow: 0 3px 10px rgba(0,174,239,.25); }
.btn-primary:hover:not(:disabled) { filter: brightness(.95); transform: translateY(-1px); }
.btn-primary:disabled { opacity: .45; cursor: not-allowed; box-shadow: none; }
.btn-danger { height: 44px; background: #EF4444; color: white; border: none; border-radius: 10px; font-size: .88rem; font-weight: 700; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.btn-danger:hover { opacity: .9; }
.btn-amber { height: 44px; background: #FFC107; color: #1E4276; border: none; border-radius: 10px; font-size: .88rem; font-weight: 700; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .2s; }
.btn-amber:hover:not(:disabled) { filter: brightness(.97); }
.btn-amber:disabled { opacity: .5; cursor: not-allowed; }
.btn-pay { height: 48px; background: linear-gradient(135deg,#1E4276,#2A5A9E); color: white; border: none; border-radius: 12px; font-size: .95rem; font-weight: 700; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 4px 14px rgba(30,66,118,.3); transition: all .2s; }
.btn-pay:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(30,66,118,.35); }
.btn-pay:disabled { opacity: .5; cursor: not-allowed; }
.btn-ghost { height: 44px; background: white; color: #1E4276; border: 1.5px solid #E8EDF2; border-radius: 10px; font-size: .88rem; font-weight: 700; padding: 0 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .2s; }
.btn-ghost:hover { border-color: #00AEEF; color: #00AEEF; }

/* ── Alerts ── */
.alert-box { display: flex; gap: 12px; padding: 14px; border-radius: 10px; }
.alert-info    { background: #EBF8FF; border: 1px solid #BAE6FD; }
.alert-warning { background: #FFFBEB; border: 1px solid #FDE68A; }
.alert-danger  { background: #FFF5F5; border: 1px solid #FECACA; }
.alert-icon-wrap { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.alert-icon-info    { background: #DBEAFE; }
.alert-icon-warning { background: #FEF3C7; }
.alert-icon-danger  { background: #FEE2E2; }
.alert-title { font-size: .82rem; font-weight: 700; color: #1E4276; margin: 0 0 3px; }
.alert-body  { font-size: .75rem; color: #64748b; margin: 0; line-height: 1.5; }

/* ── Verified Grid ── */
.verified-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; background: #F8FBFF; border: 1px solid #DEE9F5; border-radius: 10px; padding: 14px; }
.verified-field {
  display: flex; flex-direction: column; gap: 2px;
}

/* ── Direct Journey Fields ── */
.direct-fields-box {
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 14px;
  padding: 20px;
}

.benefits-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.benefit-pill {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748B;
  cursor: pointer;
  transition: all 0.2s;
}

.benefit-pill:hover {
  border-color: #00AEEF;
  color: #00AEEF;
}

.benefit-pill.active, .benefit-pill.is-selected {
  background: #00AEEF;
  color: white;
  border-color: #00AEEF;
}

.live-estimate-card {
  background: linear-gradient(135deg, #184679 0%, #112F56 100%);
  padding: 16px;
  border-radius: 8px;
  color: white;
  border: 1px solid rgba(255,255,255,0.1);
}

.estimate-label {
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.9;
}

.estimate-value {
  font-size: 1.15rem;
  font-weight: 700;
}
.ntsa-locked-notice { font-size: .65rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .4px; }
.vf-value { font-size: .82rem; font-weight: 600; color: #1E4276; }

/* ── NTSA Status Banners ── */
.ntsa-success-banner, .ntsa-manual-banner { display: flex; align-items: flex-start; gap: 11px; border-radius: 10px; padding: 12px 14px; }
.ntsa-success-banner { background: #F0FDF4; border: 1.5px solid #A7F3D0; }
.ntsa-manual-banner  { background: #FFFBEB; border: 1.5px solid #FDE68A; }
.nsb-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ntsa-success-banner .nsb-icon { background: #D1FAE5; }
.ntsa-manual-banner  .nsb-icon { background: #FEF3C7; }
.nsb-title { font-size: .8rem; font-weight: 700; margin: 0 0 2px; }
.ntsa-success-banner .nsb-title { color: #065F46; }
.ntsa-manual-banner  .nsb-title { color: #92400E; }
.nsb-sub { font-size: .73rem; margin: 0; line-height: 1.4; }
.ntsa-success-banner .nsb-sub { color: #047857; }
.ntsa-manual-banner  .nsb-sub { color: #B45309; }

/* ── Double Insurance Block ── */
.double-insure-block { border: 1.5px solid #FCA5A5; background: #FFF5F5; border-radius: 12px; padding: 16px 18px; }
.di-header { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
.di-icon-wrap { width: 40px; height: 40px; border-radius: 10px; background: #FEE2E2; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.di-title { font-size: .85rem; font-weight: 700; color: #991B1B; margin: 0 0 2px; }
.di-subtitle { font-size: .76rem; color: #B91C1C; margin: 0; line-height: 1.4; }
.di-details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; background: #fff; border-radius: 8px; padding: 12px 14px; border: 1px solid #FECACA; margin-bottom: 12px; }
.di-field { display: flex; flex-direction: column; gap: 2px; }
.di-label { font-size: .68rem; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: .04em; }
.di-value { font-size: .8rem; font-weight: 600; color: #1E293B; }
.di-value-date { color: #DC2626; }
.di-footer-note { font-size: .73rem; color: #B91C1C; display: flex; align-items: center; gap: 5px; margin: 0; line-height: 1.4; }

/* ── Two-step Verify Progress ── */
.verify-progress-strip { display: flex; align-items: center; background: #F0F9FF; border: 1px solid #BAE6FD; border-radius: 8px; padding: 10px 14px; gap: 0; }
.verify-step { display: flex; align-items: center; gap: 7px; font-size: .74rem; font-weight: 600; color: #94a3b8; flex-shrink: 0; }
.verify-step.vp-active { color: #00AEEF; }
.verify-step.vp-done   { color: #10b981; }
.verify-step.vp-pending { color: #cbd5e1; }
.vp-dot { width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.vp-connector { flex: 1; height: 2px; background: #CBD5E1; margin: 0 10px; border-radius: 1px; transition: background .3s; min-width: 20px; }
.vp-connector.done { background: #10b981; }

/* ── Inspection Box ── */
.inspection-box { background: #F0F9FF; border: 1px solid #BAE6FD; border-radius: 10px; padding: 14px; }

/* ── Toggle ── */
.toggle-group { display: flex; gap: 10px; }
.toggle-option { flex: 1; display: flex; align-items: center; padding: 10px 14px; border: 1.5px solid #E8EDF2; border-radius: 8px; cursor: pointer; font-size: .82rem; font-weight: 600; color: #64748b; transition: all .15s; background: white; }
.toggle-option.toggle-active { border-color: #00AEEF; background: rgba(0,174,239,.05); color: #1E4276; }

/* ── Gender Toggle ── */
.gender-toggle { display: flex; gap: 8px; padding-top: 4px; }
.gender-option { flex: 1; display: flex; align-items: center; justify-content: center; padding: 9px 10px; border: 1.5px solid #E8EDF2; border-radius: 8px; cursor: pointer; font-size: .82rem; font-weight: 600; color: #64748b; transition: all .15s; background: white; }
.gender-option.gender-active { border-color: #00AEEF; background: rgba(0,174,239,.05); color: #1E4276; }

/* ── Declarations ── */
.declaration-box { background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 10px; padding: 16px; }
.declaration-title { font-size: .82rem; font-weight: 700; color: #92400e; display: flex; align-items: center; margin-bottom: 12px; }
.declaration-checks { display: flex; flex-direction: column; gap: 8px; }
.dec-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 10px; border-radius: 7px; cursor: pointer; font-size: .8rem; color: #475569; transition: background .15s; }
.dec-row:hover { background: rgba(255,193,7,.08); }
.dec-row.dec-checked { color: #1E4276; }
.dec-checkbox { width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid #CBD5E1; background: white; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; transition: all .15s; }
.dec-row.dec-checked .dec-checkbox { background: #10b981; border-color: #10b981; }
.consent-sent-banner { display: flex; align-items: center; background: #F0FFF4; border: 1px solid #A7F3D0; border-radius: 8px; padding: 10px 14px; font-size: .82rem; font-weight: 600; color: #065F46; }

/* ── Pay Summary Card ── */
.pay-summary-card { background: white; border: 1.5px solid #E8EDF2; border-radius: 14px; overflow: hidden; box-shadow: 0 2px 10px rgba(30,66,118,.06); }
.pay-summary-head { display: flex; align-items: center; gap: 8px; padding: 13px 16px; background: linear-gradient(90deg, #1E4276 0%, #2A5A9E 100%); font-size: .82rem; font-weight: 700; color: white; }
.pay-summary-head-icon { width: 26px; height: 26px; border-radius: 6px; background: rgba(255,255,255,.15); display: flex; align-items: center; justify-content: center; }
.pay-summary-edit { margin-left: auto; display: flex; align-items: center; gap: 3px; font-size: .72rem; font-weight: 600; color: rgba(255,255,255,.75); text-decoration: none; background: rgba(255,255,255,.1); padding: 3px 8px; border-radius: 20px; transition: background .15s; }
.pay-summary-edit:hover { background: rgba(255,255,255,.2); color: white; }
.pay-summary-section { padding: 12px 16px; border-bottom: 1px solid #F1F5F9; }
.pay-summary-section-alt { background: #FAFCFF; }
.pay-sec-label { font-size: .68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; margin: 0 0 8px; display: flex; align-items: center; }
.pay-sum-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 5px 0; }
.pay-sum-icon-cell { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.pay-sum-icon-wrap { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pay-sum-icon-blue  { background: rgba(0,174,239,.1); }
.pay-sum-icon-navy  { background: rgba(30,66,118,.08); }
.pay-sum-icon-green { background: rgba(16,185,129,.1); }
.pay-sum-label { font-size: .74rem; font-weight: 600; color: #64748b; white-space: nowrap; }
.pay-sum-value { font-size: .78rem; color: #1E4276; font-weight: 500; text-align: right; }
.pay-benefits-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.pay-benefit-chip { display: flex; align-items: center; background: rgba(0,174,239,.06); border: 1px solid rgba(0,174,239,.2); border-radius: 20px; padding: 3px 10px; font-size: .72rem; font-weight: 600; color: #1E4276; }
.pay-premium-footer { padding: 12px 16px; background: #F8FBFF; }
.pay-prem-row { display: flex; justify-content: space-between; font-size: .78rem; color: #64748b; margin-bottom: 6px; }
.pay-prem-total { display: flex; justify-content: space-between; align-items: center; padding-top: 8px; border-top: 1px dashed #DEE9F5; font-size: .78rem; font-weight: 700; color: #1E4276; }
.pay-prem-total-val { font-size: 1.15rem; font-weight: 800; color: #00AEEF; }

/* ── Plan Grid v2 ── */
.section-sub-label { font-size: .78rem; font-weight: 700; color: #1E4276; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 10px; }
.plan-grid-v2 { display: flex; flex-direction: column; gap: 8px; }
.plan-card-v2 { display: flex; align-items: flex-start; gap: 12px; padding: 12px 14px; border: 1.5px solid #E8EDF2; border-radius: 10px; background: white; cursor: pointer; transition: all .15s; position: relative; }
.plan-card-v2:hover:not(.plan-v2-disabled) { border-color: #BAE6FD; background: #F8FDFF; }
.plan-v2-active { border-color: #00AEEF !important; background: rgba(0,174,239,.04) !important; box-shadow: 0 0 0 1px #00AEEF; }
.plan-v2-disabled { opacity: .45; cursor: not-allowed; }
.plan-v2-disabled .plan-v2-amount { visibility: hidden; }
.plan-v2-icon-wrap { width: 38px; height: 38px; border-radius: 10px; background: #F1F5F9; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; transition: background .15s; }
.plan-v2-icon-active { background: rgba(0,174,239,.12) !important; }
.plan-v2-body { flex: 1; }
.plan-v2-name { font-size: .84rem; font-weight: 700; color: #1E4276; margin: 0 0 2px; }
.plan-v2-amount { font-size: .72rem; color: #64748b; margin: 0; }
.plan-v2-check { width: 20px; height: 20px; border-radius: 50%; background: #00AEEF; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 8px; }

/* ── Installment Breakdown Chips ── */
.installment-breakdown { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.inst-chip { display: flex; flex-direction: column; align-items: center; padding: 5px 10px; border-radius: 8px; min-width: 68px; }
.inst-chip-now { background: rgba(0,174,239,.1); border: 1px solid rgba(0,174,239,.25); }
.inst-chip-later { background: #F8FBFF; border: 1px solid #DEE9F5; }
.inst-chip-pct { font-size: .65rem; font-weight: 800; color: #1E4276; letter-spacing: .02em; }
.inst-chip-label { font-size: .6rem; color: #94a3b8; font-weight: 600; margin: 1px 0; }
.inst-chip-amount { font-size: .72rem; font-weight: 700; color: #00AEEF; }
.inst-chip-now .inst-chip-amount { color: #00AEEF; }
.inst-chip-later .inst-chip-amount { color: #475569; }
.inst-chip-connector { font-size: .7rem; color: #cbd5e1; font-weight: 700; flex-shrink: 0; }

/* ── Payment Method Tabs ── */
.pay-method-tabs { display: flex; gap: 8px; }
.pay-method-tab { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 14px; border: 1.5px solid #E8EDF2; border-radius: 10px; background: white; cursor: pointer; font-size: .82rem; font-weight: 600; color: #64748b; transition: all .15s; }
.pay-method-tab:hover { border-color: #BAE6FD; color: #1E4276; }
.pay-tab-active { border-color: #1E4276 !important; background: #F0F4FA !important; color: #1E4276 !important; box-shadow: 0 0 0 1px #1E4276; }
.pay-tab-mpesa-logo { height: 22px; object-fit: contain; }
.pay-tab-icon-wrap { width: 28px; height: 28px; border-radius: 6px; background: #F1F5F9; display: flex; align-items: center; justify-content: center; }
.pay-tab-active .pay-tab-icon-wrap { background: rgba(30,66,118,.1); }
.pay-panel { animation: fadeSlideIn .2s ease-out; }
@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

/* ── M-Pesa Panel ── */
.mpesa-panel-card { background: #F8FBFF; border: 1.5px solid #DEE9F5; border-radius: 12px; padding: 16px; }
.mpesa-panel-header { display: flex; align-items: center; gap: 12px; }
.mpesa-panel-logo { height: 36px; object-fit: contain; }
.mpesa-panel-title { font-size: .88rem; font-weight: 700; color: #1E4276; margin: 0 0 2px; }
.mpesa-panel-sub { font-size: .72rem; color: #64748b; margin: 0; }
.pay-ref-chip { display: inline-flex; align-items: center; background: white; border: 1px solid #E8EDF2; border-radius: 6px; padding: 5px 10px; font-size: .72rem; }
.pay-ref-label { color: #94a3b8; margin-right: 5px; font-weight: 600; }
.pay-ref-code { font-family: 'JetBrains Mono', monospace; font-weight: 700; color: #1E4276; letter-spacing: .05em; }
.pay-stk-hint { margin-top: 10px; font-size: .72rem; color: #94a3b8; display: flex; align-items: center; line-height: 1.5; }

/* ── Paybill Panel ── */
.paybill-fallback-alert { display: flex; align-items: center; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 8px; padding: 10px 14px; font-size: .78rem; font-weight: 600; color: #92400e; }
.paybill-panel-card { background: white; border: 1.5px solid #E8EDF2; border-radius: 12px; overflow: hidden; }
.paybill-panel-header { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: linear-gradient(90deg, #1E4276, #2A5A9E); }
.paybill-icon-circle { width: 42px; height: 42px; border-radius: 50%; background: rgba(255,255,255,.15); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.paybill-panel-title { font-size: .88rem; font-weight: 700; color: white; margin: 0 0 2px; }
.paybill-panel-sub { font-size: .72rem; color: rgba(255,255,255,.7); margin: 0; }
.paybill-steps-grid { display: flex; flex-direction: column; gap: 0; padding: 0 16px; }
.paybill-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid #F1F5F9; }
.paybill-step-item:last-child { border-bottom: none; }
.paybill-step-highlight { background: rgba(255,193,7,.04); margin: 0 -16px; padding-left: 16px; padding-right: 16px; }
.pb-step-num { width: 26px; height: 26px; border-radius: 50%; background: #E8EDF2; color: #64748b; font-size: .72rem; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
.pb-step-num-amber { background: #FEF3C7; color: #92400E; }
.pb-step-num-green { background: #D1FAE5; color: #065F46; }
.pb-step-body { flex: 1; }
.pb-step-label { font-size: .72rem; font-weight: 600; color: #64748b; margin: 0 0 3px; display: flex; align-items: center; gap: 6px; }
.pb-unique-badge { font-size: .62rem; background: #FEF3C7; color: #92400E; border: 1px solid #FDE68A; border-radius: 10px; padding: 1px 6px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; }
.pb-step-value { font-size: .88rem; font-weight: 700; color: #1E4276; margin: 0; }
.pb-step-hint { font-size: .68rem; color: #94a3b8; margin: 3px 0 0; line-height: 1.4; }
.pb-copy-btn { padding: 5px; border: none; background: #F1F5F9; border-radius: 6px; cursor: pointer; flex-shrink: 0; margin-top: 2px; transition: background .15s; }
.pb-copy-btn:hover { background: #E2E8F0; }
.paybill-ussd-steps { background: #F8FBFF; border-top: 1px solid #F1F5F9; padding: 12px 16px; }
.paybill-ussd-title { font-size: .68rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .4px; margin: 0 0 8px; display: flex; align-items: center; }
.paybill-ussd-list { display: flex; flex-direction: column; gap: 5px; }
.paybill-ussd-item { display: flex; align-items: flex-start; gap: 8px; font-size: .74rem; color: #475569; line-height: 1.5; }
.paybill-ussd-num { width: 18px; height: 18px; border-radius: 50%; background: #E8EDF2; font-size: .62rem; font-weight: 800; color: #64748b; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
.pay-sec-count { font-size: .65rem; font-weight: 700; color: #94a3b8; background: #F1F5F9; border-radius: 10px; padding: 1px 6px; }
.pay-benefit-chip-paid { background: rgba(16,185,129,.06) !important; border-color: rgba(16,185,129,.25) !important; color: #065F46 !important; }
.pay-benefit-premium { margin-left: 5px; font-size: .65rem; font-weight: 700; color: #10b981; background: rgba(16,185,129,.1); border-radius: 10px; padding: 1px 5px; white-space: nowrap; }

/* ── Consent target row ── */
.consent-target-row { display: flex; align-items: center; gap: 12px; background: #F0F9FF; border: 1px solid #BAE6FD; border-radius: 10px; padding: 12px 14px; }
.ctr-icon-wrap { width: 36px; height: 36px; border-radius: 8px; background: rgba(0, 174, 239, .12); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ctr-body { flex: 1; min-width: 0; }
.ctr-label { font-size: .74rem; font-weight: 600; color: #64748b; margin: 0 0 2px; }
.ctr-email { font-size: .82rem; font-weight: 700; color: #1E4276; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Success ── */
.success-card { background: white; border-radius: 16px; border: 1px solid #D1FAE5; padding: 40px 32px; text-align: center; box-shadow: 0 8px 32px rgba(16,185,129,.12); }
.success-icon-wrap { width: 80px; height: 80px; background: linear-gradient(135deg,#10b981,#34d399); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 8px 20px rgba(16,185,129,.35); animation: popIn .5s cubic-bezier(.175,.885,.32,1.275); }
@keyframes popIn { 0% { transform: scale(0); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
.success-title { font-size: 1.6rem; font-weight: 800; color: #1E4276; margin-bottom: 6px; }
.success-subtitle { font-size: .9rem; color: #64748b; margin-bottom: 24px; }
.success-detail-box { background: #F8FBFF; border: 1px solid #E8EDF2; border-radius: 12px; padding: 20px; text-align: left; margin-bottom: 24px; }
.docs-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.doc-chip { display: flex; align-items: center; background: rgba(0,174,239,.08); border: 1px solid rgba(0,174,239,.2); border-radius: 20px; padding: 4px 10px; font-size: .72rem; font-weight: 600; color: #1E4276; }
.success-actions { display: flex; justify-content: center; gap: 12px; }

/* ── Transitions ── */
.fade-up-enter-active { transition: all .3s ease-out; }
.fade-up-enter { opacity: 0; transform: translateY(10px); }

/* ── Responsive ── */
@media (max-width: 600px) {
  .sale-body { padding: 12px 12px 40px; }
  .form-row-2 { grid-template-columns: 1fr; }
  .form-row-3 { grid-template-columns: 1fr 1fr; }
  .verified-grid { grid-template-columns: 1fr 1fr; }
  .step-label { display: none; }
  .toggle-group { flex-direction: column; }
  .success-card { padding: 24px 16px; }
  .installment-breakdown { gap: 3px; }
  .inst-chip { min-width: 56px; padding: 4px 6px; }
}
/* ── NTSA locked notice ── */
.ntsa-locked-notice {
  display: flex;
  align-items: center;
  font-size: .73rem;
  font-weight: 600;
  color: #047857;
  background: #F0FDF4;
  border: 1px solid #A7F3D0;
  border-radius: 7px;
  padding: 7px 12px;
}
/* ── TPO flat info strip ── */
.tpo-flat-info {
  display: flex;
  align-items: center;
  background: #EBF8FF;
  border: 1px solid #BAE6FD;
  border-radius: 7px;
  padding: 7px 12px;
  font-size: .73rem;
  font-weight: 600;
  color: #0369a1;
}
/* ── Verify info strip (direct journey) ── */
.verify-info-strip {
  display: flex;
  align-items: flex-start;
  background: #F0F9FF;
  border: 1px solid #E0F2FE;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.72rem;
  font-weight: 500;
  color: #0369a1;
  line-height: 1.5;
}
.verify-info-strip strong {
  color: #0284c7;
  padding: 0 2px;
}
/* ── Benefit summary pill (direct journey) ── */
.benefit-summary-pill {
  display: flex;
  align-items: center;
  background: #F0FDF4;
  border: 1px solid #A7F3D0;
  border-radius: 7px;
  padding: 7px 12px;
  font-size: .73rem;
  font-weight: 600;
  color: #047857;
}

/* ── TPO benefit mini list (direct journey) ── */
.tpo-benefit-mini-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tpo-benefit-mini-row {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1px solid #E8EDF2;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.73rem;
  font-weight: 600;
  color: #1E4276;
}

/* ── Live Estimate Card breakdown rows ── */
.lec-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.8);
  margin-bottom: 5px;
}
.lec-row-dim {
  opacity: 0.5;
  font-size: 0.72rem;
}
.lec-row-total {
  font-size: 1rem;
  font-weight: 800;
  color: #ffd700;
  margin-bottom: 0;
}
.lec-divider {
  height: 1px;
  background: rgba(255,255,255,0.12);
  margin: 8px 0;
}

/* ── Financier Type Toggle ── */
.fin-type-toggle { display: flex; gap: 8px; }
.fin-type-opt { flex: 1; display: flex; align-items: center; gap: 8px; padding: 10px 12px; border: 1.5px solid #E8EDF2; border-radius: 10px; cursor: pointer; background: white; transition: all .15s; }
.fin-type-opt p { font-size: .82rem; font-weight: 700; color: #1E4276; margin: 0 0 1px; }
.fin-type-opt span { font-size: .68rem; color: #94a3b8; }
.fin-type-active { border-color: #00AEEF; background: rgba(0,174,239,.05); }
.fin-type-icon { width: 30px; height: 30px; border-radius: 7px; background: #F1F5F9; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fin-type-active .fin-type-icon { background: rgba(0,174,239,.12); }

/* ── Institution selected chip ── */
.fin-selected-chip { display: flex; align-items: center; gap: 10px; background: #F8FBFF; border: 1px solid #DEE9F5; border-radius: 8px; padding: 9px 12px; }
.fin-chip-avatar { width: 32px; height: 32px; border-radius: 6px; background: rgba(0,174,239,.12); color: #00AEEF; font-size: .72rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fin-chip-body { flex: 1; min-width: 0; }
.fin-chip-body p { font-size: .82rem; font-weight: 700; color: #1E4276; margin: 0 0 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fin-chip-body span { font-size: .7rem; color: #94a3b8; }
.fin-chip-clear { font-size: .72rem; color: #94a3b8; cursor: pointer; padding: 3px 7px; border: 1px solid #E8EDF2; border-radius: 5px; background: white; display: flex; align-items: center; gap: 3px; }
.fin-chip-clear:hover { color: #EF4444; border-color: #FECACA; }

/* ── Institution dropdown item ── */
.fin-dd-item { display: flex; align-items: center; justify-content: space-between; width: 100%; font-size: .82rem; }
.fin-dd-badge { font-size: .68rem; background: #F1F5F9; color: #64748b; border-radius: 4px; padding: 1px 6px; margin-left: 8px; }

/* ── Interest type grid ── */
.interest-type-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.interest-type-opt { padding: 10px 12px; border: 1.5px solid #E8EDF2; border-radius: 9px; cursor: pointer; background: white; transition: all .15s; }
.interest-type-opt p { font-size: .82rem; font-weight: 700; color: #1E4276; margin: 0 0 2px; }
.interest-type-opt span { font-size: .68rem; color: #94a3b8; line-height: 1.3; display: block; }
.interest-type-active { border-color: #00AEEF; background: rgba(0,174,239,.05); }

/* ── Summary strip ── */
.fin-summary-strip { background: rgba(0,174,239,.04); border: 1px solid rgba(0,174,239,.18); border-radius: 9px; padding: 11px 14px; }
.fin-sum-row { display: flex; justify-content: space-between; align-items: center; font-size: .75rem; padding: 3px 0; }
.fin-sum-label { color: #94a3b8; font-weight: 500; }
.fin-sum-val { color: #1E4276; font-weight: 600; }

/* Logbook transfer wrap */
.logbook-transfer-wrap {
  background: #F8FBFF;
  border: 1px solid #DEE9F5;
  border-radius: 9px;
  padding: 12px 14px;
}
.logbook-transfer-label {
  font-size: .75rem;
  font-weight: 600;
  color: #1E4276;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
}
.logbook-transfer-toggle {
  display: flex;
  gap: 8px;
}
.lbt-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: 1.5px solid #E8EDF2;
  border-radius: 8px;
  cursor: pointer;
  font-size: .78rem;
  font-weight: 600;
  color: #64748b;
  background: white;
  transition: all .15s;
}
.lbt-option:hover { border-color: #BAE6FD; }
.lbt-yes { border-color: #A7F3D0 !important; background: #F0FDF4 !important; color: #065F46 !important; }
.lbt-no  { border-color: #FDE68A !important; background: #FFFBEB !important; color: #92400E !important; }
.lbt-warning {
  font-size: .72rem;
  color: #92400E;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 6px;
  padding: 6px 10px;
  display: flex;
  align-items: flex-start;
  line-height: 1.4;
}

/* Benefit surcharge list */
.benefit-surcharge-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.benefit-surcharge-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F0FDF4;
  border: 1px solid #A7F3D0;
  border-radius: 8px;
  padding: 8px 12px;
  flex-wrap: wrap;
  gap: 6px;
}
.bsr-left {
  display: flex;
  align-items: center;
  font-size: .75rem;
  font-weight: 600;
  color: #065F46;
}
.bsr-name { color: #1E4276; font-weight: 700; }
.bsr-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.bsr-free {
  font-size: .68rem;
  color: #94a3b8;
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 10px;
  padding: 2px 8px;
}
.bsr-charge {
  font-size: .68rem;
  font-weight: 600;
  color: #1E4276;
  background: #EBF8FF;
  border: 1px solid #BAE6FD;
  border-radius: 10px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
  gap: 2px;
}
.bsr-premium {
  font-size: .8rem;
  font-weight: 700;
  color: #10b981;
}
/* Comprehensive limit note */
.comprehensive-limit-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  background: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 7px;
  padding: 8px 12px;
  font-size: .72rem;
  color: #0369a1;
  line-height: 1.5;
}

/* Nairobi sub-area grid */
.nairobi-area-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.nairobi-area-chip {
  padding: 5px 10px;
  border: 1.5px solid #E8EDF2;
  border-radius: 20px;
  font-size: .72rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  background: white;
  transition: all .15s;
  white-space: nowrap;
}
.nairobi-area-chip:hover { border-color: #BAE6FD; color: #1E4276; }
.nairobi-area-chip.area-active { border-color: #00AEEF; background: rgba(0,174,239,.08); color: #1E4276; }

/* Valuer cards */
.valuer-cards-list { display: flex; flex-direction: column; gap: 8px; }
.valuer-card {
  background: white;
  border: 1.5px solid #E8EDF2;
  border-radius: 12px;
  padding: 13px 14px;
  cursor: pointer;
  transition: all .15s;
}
.valuer-card:hover { border-color: #BAE6FD; background: #F8FDFF; }
.valuer-card-selected { border-color: #00AEEF !important; background: rgba(0,174,239,.04) !important; box-shadow: 0 0 0 1px #00AEEF; }
.vc-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px; }
.vc-avatar {
  width: 36px; height: 36px; border-radius: 9px;
  background: rgba(0,174,239,.12); color: #1E4276;
  font-size: .72rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.vc-info { flex: 1; min-width: 0; }
.vc-name { font-size: .82rem; font-weight: 700; color: #1E4276; margin: 0 0 2px; }
.vc-info .vc-hq { font-size: .7rem; color: #64748b; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.vc-check {
  width: 22px; height: 22px; border-radius: 50%;
  background: #00AEEF;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.vc-meta { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.vc-meta-chip {
  display: flex; align-items: center;
  font-size: .68rem; font-weight: 500; color: #64748b;
  background: #F8FAFC; border: 1px solid #E8EDF2;
  border-radius: 10px; padding: 2px 8px;
}
.vc-mobile-badge { background: #F0FDF4 !important; border-color: #A7F3D0 !important; color: #047857 !important; }
.vc-branch {
  font-size: .7rem; color: #00AEEF;
  background: #EBF8FF; border: 1px solid #BAE6FD;
  border-radius: 7px; padding: 5px 9px;
  display: flex; align-items: flex-start;
  line-height: 1.4;
}
.valuer-empty-state {
  text-align: center; padding: 24px 16px;
  color: #94a3b8; font-size: .8rem;
  background: #F8FAFC; border-radius: 10px;
  border: 1px dashed #DEE9F5;
}
.valuer-email-sent {
  display: flex; align-items: center;
  background: #F0FDF4; border: 1.5px solid #A7F3D0;
  border-radius: 8px; padding: 10px 14px;
  font-size: .78rem; font-weight: 600; color: #065F46;
}

/* ── New Sale button ── */
.btn-new-sale {
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 12px;
  background: white;
  border: 1.5px solid #E8EDF2;
  border-radius: 8px;
  font-size: .75rem;
  font-weight: 700;
  color: #1E4276;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}
.btn-new-sale:hover { border-color: #EF4444; color: #EF4444; background: #FFF5F5; }

/* ── New Sale Dialog ── */
.new-sale-dialog {
  background: white;
  border-radius: 12px;
  overflow: hidden;
}
.nsd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 14px;
  background: linear-gradient(135deg, #1E4276, #2A5A9E);
}
.nsd-icon {
  width: 40px; height: 40px;
  background: rgba(239,68,68,.25);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.nsd-title { font-size: .92rem; font-weight: 800; color: white; margin: 0 0 2px; }
.nsd-sub { font-size: .72rem; color: rgba(255,255,255,.65); margin: 0; }
.nsd-body { padding: 16px 20px; }
.nsd-desc { font-size: .82rem; color: #64748b; margin: 0; line-height: 1.5; }
.nsd-footer {
  display: flex; gap: 10px;
  padding: 12px 20px 16px;
  background: #F8FAFC;
  border-top: 1px solid #F1F5F9;
}
.nsd-btn-cancel {
  flex: 1; padding: 10px; border: 1.5px solid #E8EDF2;
  border-radius: 8px; background: white; color: #64748b;
  font-weight: 600; font-size: .84rem; cursor: pointer;
}
.nsd-btn-confirm {
  flex: 1; padding: 10px; border: none;
  border-radius: 8px; background: #EF4444; color: white;
  font-weight: 700; font-size: .84rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.nsd-btn-confirm:hover { background: #DC2626; }

/* ── IPRS Unavailable Banner ── */
.iprs-unavailable-banner {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  background: #FFFBEB;
  border: 1.5px solid #FDE68A;
  border-radius: 10px;
  padding: 12px 14px;
}
.iub-icon {
  width: 32px; height: 32px; border-radius: 8px;
  background: #FEF3C7;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.iub-title { font-size: .8rem; font-weight: 700; color: #92400E; margin: 0 0 2px; }
.iub-sub { font-size: .73rem; color: #B45309; margin: 0; line-height: 1.4; }

/* ── Manual Customer Form ── */
.manual-customer-form {
  background: #F8FBFF;
  border: 1.5px solid #DEE9F5;
  border-radius: 12px;
  padding: 16px;
}
.mcf-header {
  display: flex;
  align-items: center;
  font-size: .8rem;
  font-weight: 700;
  color: #1E4276;
  padding-bottom: 10px;
  border-bottom: 1px solid #E8EDF2;
}

/* ── KYC Documents Reminder ── */
.kyc-reminder {
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px solid #FDE68A;
  box-shadow: 0 2px 10px rgba(234,179,8,.1);
}
.kyc-reminder-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
}
.kyc-reminder-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: rgba(255,255,255,.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kyc-reminder-title {
  font-size: .88rem;
  font-weight: 800;
  color: white;
  margin: 0 0 2px;
}
.kyc-reminder-sub {
  font-size: .7rem;
  color: rgba(255,255,255,.65);
  margin: 0;
}
.kyc-docs-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  background: #FFFBEB;
  border-bottom: 1.5px solid #FDE68A;
}
.kyc-doc-chip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 14px;
  border-right: 1px solid #FDE68A;
}
.kyc-doc-chip:last-child {
  border-right: none;
}
.kyc-doc-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: white;
  border: 1.5px solid #E8EDF2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kyc-doc-name {
  font-size: .78rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 2px;
}
.kyc-doc-hint {
  font-size: .68rem;
  color: #92400E;
  margin: 0;
  line-height: 1.3;
}
.kyc-reminder-footer {
  display: flex;
  align-items: flex-start;
  padding: 10px 14px;
  background: #FEF3C7;
  font-size: .72rem;
  font-weight: 600;
  color: #92400E;
  line-height: 1.5;
}

/* Responsive: stack docs vertically on mobile */
@media (max-width: 600px) {
  .kyc-docs-row {
    grid-template-columns: 1fr;
  }
  .kyc-doc-chip {
    border-right: none;
    border-bottom: 1px solid #FDE68A;
  }
  .kyc-doc-chip:last-child {
    border-bottom: none;
  }
}
.underwriting-disclaimer-mini {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #FFFBEB;
  border: 1px solid #FCD34D;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  color: #92400E;
  white-space: nowrap;
}
</style>
